import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArchiveBoxIcon,
  BriefcaseIcon,
  CameraIcon,
  ChevronDownIcon,
  EnvelopeIcon,
  DocumentArrowUpIcon,
  FlagIcon,
  FunnelIcon,
  InformationCircleIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  NoSymbolIcon,
  PaperClipIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import BaseInput from '../../components/ui/base-input';
import { CustomCheckbox } from '../../components/ui/CustomCheckbox';
import { ContentTabToggle } from '../../components/ui/content-tab-toggle';
import { TabToggle } from '../../components/ui/tab-toggle';
import { UniversalDropdown } from '../../components/ui/universal-dropdown';
import { useAuthStore } from '../../store/authStore';
import { apiService } from '../../services/apiService';
import { AtsParseTimeoutError, atsApi, sha256File } from '../services/atsApi';
import { useAtsCandidates } from '../hooks/useAtsCandidates';
import { useAtsNavigationStore } from '../store/atsNavigationStore';
import type { AtsCandidateCard, AtsCandidateListItem, AtsDocument, AtsDuplicateGroup, AtsImportJob, AtsSearchFilters, AtsTab } from '../types';
import { buildFolderTreeFromPaths, getCandidateFolderPaths, getCandidateDocumentFolderPaths, getFolderCreateParentPath, ALL_COUNTRIES_FOLDER, DEFAULT_COUNTRY_FOLDER, isAllCountriesFolderName } from '../utils/folders';
import { buildAtsCandidateLink, parseAtsCandidateDeepLink } from '../utils/candidateDeepLink';
import { formatCandidateLocation } from '../utils/candidateLocation';
import { candidateAge, formatBirthDate, todayDateInput } from '../utils/candidateAge';
import { formatProcessStageHistoryDetails, processVacancyTitleFromFolder, stageEventsForCurrentVacancy } from '../utils/processHistory';
import type { AtsFolderNode } from '../utils/folders';
import { getBulkImportSource, getImportFilesError } from '../utils/imports';
import {
  extractSearchHighlightTerms,
  HighlightedText,
  SearchHighlightProvider,
} from '../utils/searchHighlight';
import { FolderMoveModal } from './FolderMoveModal';
import { CandidatePhoto, candidateHasPhoto, invalidateCandidatePhoto } from './CandidatePhoto';
import { ContactValue } from './ContactValue';
import { AtsCardValidationError, formatAtsValidationErrors, type AtsFieldValidationError } from '../utils/validationErrors';

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'new_candidate', label: 'Новый кандидат' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'inactive', label: 'Неактивен' },
  { value: 'candidate_refused', label: 'Отказ кандидата' },
  { value: 'rejected', label: 'Отказ компании' },
  { value: 'security_rejected', label: 'Отказ СБ' },
  { value: 'offer', label: 'Оффер' },
  { value: 'hired', label: 'Выход' },
];

const CARD_STATUS_EDIT_OPTIONS = STATUS_OPTIONS.filter((option) => option.value);

const EDUCATION_LEVEL_OPTIONS: Array<{ value: 'higher' | 'secondary_special' | 'secondary'; label: string }> = [
  { value: 'higher', label: 'Высшее' },
  { value: 'secondary_special', label: 'Среднее специальное' },
  { value: 'secondary', label: 'Среднее' },
];

const REJECTION_REASONS = [
  { value: 'no_contact', label: 'Не вышел на связь' },
  { value: 'hard_skills', label: 'Недостаточно опыта / hard skills' },
  { value: 'soft_skills', label: 'Личные качества / soft skills' },
  { value: 'salary_expectations', label: 'Завышены зарплатные ожидания' },
  { value: 'location', label: 'Локация' },
  { value: 'other_candidate', label: 'Выбрали другого кандидата' },
  { value: 'overqualified', label: 'Overqualified' },
  { value: 'missing_documents', label: 'Нет необходимых документов' },
  { value: 'other', label: 'Другое' },
];

const CANDIDATE_REFUSAL_REASONS = [
  { value: 'not_looking_for_work', label: 'Не ищет работу' },
  { value: 'insufficient_experience', label: 'Недостаточно опыта' },
  { value: 'salary', label: 'Зарплата' },
  { value: 'location', label: 'Локация' },
  { value: 'work_format', label: 'Формат работы' },
  { value: 'responsibilities', label: 'Не устроили обязанности' },
  { value: 'company_or_domain', label: 'Не устроила компания / предметная область' },
  { value: 'benefits', label: 'Социальный пакет' },
  { value: 'counteroffer', label: 'Контроффер на текущем месте' },
  { value: 'accepted_another_offer', label: 'Принял другой оффер' },
  { value: 'other', label: 'Другое' },
];

const DEFAULT_PROCESS_STEPS = [
  'Новый кандидат',
  'Первый контакт',
  'HR-интервью',
  'Техническое интервью',
  'Финальное интервью',
  'Оффер',
  'Найм',
];

const DEFAULT_PROCESS_STAGE_OPTIONS = DEFAULT_PROCESS_STEPS.map((step) => ({
  value: step,
  label: step,
}));

const getStageStatusOptions = (stage: string) => {
  if (stage === 'Новый кандидат') return [{ value: 'created', label: 'Создан' }];
  if (stage === 'Оффер') return [
    { value: 'sent', label: 'Отправлен' },
    { value: 'accepted', label: 'Принят' },
    { value: 'declined', label: 'Не принят' },
    { value: 'security_rejected', label: 'Отказ СБ' },
  ];
  if (stage === 'Найм') return [{ value: 'hired', label: 'Оформлен' }];
  return [
    { value: 'approved', label: 'Одобрен' },
    { value: 'rejected', label: 'Отказ компании' },
    { value: 'candidate_refused', label: 'Отказ кандидата' },
    { value: 'security_rejected', label: 'Отказ СБ' },
    { value: 'thinking', label: 'Подумать' },
  ];
};

const TABS = [
  { id: 'profile' as AtsTab, label: 'Профиль' },
  { id: 'history' as AtsTab, label: 'История' },
  { id: 'notes' as AtsTab, label: 'Заметки' },
];

const LABEL_COLORS = [
  '#E5484D',
  '#F45D22',
  '#F5B700',
  '#7757FF',
  '#2F7BFF',
  '#1F9254',
  '#9A9CA3',
];

export const AtsSearchInput = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <label className="flex h-11 min-w-0 flex-1 basis-[240px] cursor-text items-center gap-2 rounded-[10px] border border-[#DDDCD6] bg-white px-3 focus-within:border-[#E16349]" data-testid="ats-search-input">
    <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-[#868C98]" />
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label="Поиск кандидатов"
      placeholder="Поиск по ФИО, навыкам, заметкам..."
      className="h-full min-w-0 w-full bg-transparent text-[14px] outline-none"
    />
  </label>
);

export const AtsWorkspaceHeading = ({
  title,
  candidatesCount,
  searchSummary,
}: {
  title: string;
  candidatesCount: number;
  searchSummary?: string;
}) => (
  <div data-testid="ats-workspace-heading-container" className="min-w-0 2xl:w-[520px] 2xl:shrink-0">
    <h1
      data-testid="ats-workspace-heading"
      className="max-w-[520px] break-words whitespace-normal text-[28px] font-semibold leading-tight text-[#23252D]"
    >
      {title}
    </h1>
    <p className="mt-1 text-[14px] text-[#868C98]">
      {searchSummary || `Резюме в папке: ${candidatesCount}`}
    </p>
  </div>
);

type RecruiterOption = {
  id: string;
  name: string;
  email: string;
  color: string;
};

const RESPONSIBLE_RECRUITER_KEYS = new Set([
  'responsible_recruiter',
  'responsible_recruiter_id',
  'responsible_recruiter_name',
  'responsible_recruiter_email',
]);

const SYSTEM_CUSTOM_FIELD_KEYS = new Set([
  'id',
  'candidate_id',
  'full_name',
  'email',
  'phone',
  'emails',
  'phones',
  'messengers',
  'links',
  'location',
  'location_city',
  'location_country',
  'citizenship',
  'gender',
  'age',
  'telegram',
  'source',
  'status',
  'is_blacklisted',
  'color_label',
  'desired_position',
  'salary_expectation',
  'skills',
  'education',
  'education_details',
  'experience',
  'work_history',
  'custom_fields',
  'tags',
  'vacancy_links',
  'labels',
  'red_flags',
  'comments',
  'attachments',
  'documents',
  'history',
  'created_at',
  'updated_at',
  'folder',
  'folder_path',
]);

const SYSTEM_CUSTOM_FIELD_LABELS = new Set([
  'фио',
  'имя',
  'полное имя',
  'email',
  'емейл',
  'почта',
  'телефон',
  'телефоны',
  'мессенджеры',
  'ссылки',
  'локация',
  'город',
  'страна',
  'гражданство',
  'пол',
  'возраст',
  'telegram',
  'телеграм',
  'источник',
  'статус',
  'чёрный список',
  'черный список',
  'желаемая должность',
  'должность',
  'зарплата',
  'зарплатные ожидания',
  'навыки',
  'образование',
  'опыт',
  'опыт работы',
  'метки',
  'редифлаг',
  'red flag',
  'комментарии',
  'вложения',
  'документы',
  'история',
  'папка',
  'дата создания',
  'дата обновления',
  'ответственный',
  'ответственный рекрутер',
  'рекрутер',
]);

const normalizeCustomFieldKey = (value: string): string => (
  value
    .trim()
    .toLocaleLowerCase('ru-RU')
    .replace(/ё/g, 'е')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
);

const isReservedCustomFieldKey = (key: string): boolean => {
  const normalized = normalizeCustomFieldKey(key);
  if (!normalized) return true;
  if (RESPONSIBLE_RECRUITER_KEYS.has(normalized)) return false;
  if (SYSTEM_CUSTOM_FIELD_KEYS.has(normalized)) return true;
  const label = normalized.replace(/_/g, ' ');
  if (SYSTEM_CUSTOM_FIELD_LABELS.has(label)) return true;
  return Array.from(SYSTEM_CUSTOM_FIELD_LABELS).some((item) => normalizeCustomFieldKey(item) === normalized);
};

const RECRUITER_COLORS = ['#D8E9FF', '#D6F3EC', '#EEE6FF', '#FFE2E2', '#FFF3D6', '#ECEBE6'];

type AtsFilterState = {
  source: string;
  recruiter: string;
  recruiterIds: string[];
  vacancy: string;
  vacancyPaths: string[];
  stopCompanies: string;
  donorCompanies: string;
  companyMatchScope: 'current' | 'all';
  positionMode: 'exact' | 'partial';
  position: string;
  ageFrom: string;
  ageTo: string;
  includeUnknownAge: boolean;
  experienceFrom: string;
  experienceTo: string;
  educationLevels: Array<'higher' | 'secondary_special' | 'secondary'>;
  includeUnknownEducation: boolean;
  skillGroups: Array<{ id: string; operator: 'and' | 'or'; skills: string[]; input: string }>;
  location: string;
  country: string;
  city: string;
  region: '' | 'APAC' | 'EMEA' | 'AMER' | 'LATAM' | 'NA' | 'MENA' | 'CEEMEA';
  salaryFrom: string;
  salaryTo: string;
  currency: 'RUB' | 'EUR' | 'USD';
  includeUnknownSalary: boolean;
  tags: string;
  blacklist: 'any' | 'only' | 'exclude';
  documents: 'any' | 'with' | 'without';
  anyWord: string;
};

type AtsCandidateSort = 'updated' | 'full_name';

const CANDIDATE_SORT_OPTIONS: Array<{ value: AtsCandidateSort; label: string }> = [
  { value: 'updated', label: 'по дате обновления' },
  { value: 'full_name', label: 'по ФИО' },
];

const DEFAULT_FILTERS: AtsFilterState = {
  source: '',
  recruiter: '',
  recruiterIds: [],
  vacancy: '',
  vacancyPaths: [],
  stopCompanies: '',
  donorCompanies: '',
  companyMatchScope: 'current',
  positionMode: 'exact',
  position: '',
  ageFrom: '',
  ageTo: '',
  includeUnknownAge: true,
  experienceFrom: '',
  experienceTo: '',
  educationLevels: [],
  includeUnknownEducation: true,
  skillGroups: [],
  location: '',
  country: '',
  city: '',
  region: '',
  salaryFrom: '',
  salaryTo: '',
  currency: 'RUB',
  includeUnknownSalary: true,
  tags: '',
  blacklist: 'any',
  documents: 'any',
  anyWord: '',
};

const numberFromUnknown = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const text = asText(value).replace(/\s/g, '');
  const match = text.match(/\d+(?:[.,]\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0].replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

type CurrencyRates = Record<string, number>;

const parseSalaryExpectation = (value: unknown): { amount: number; currency: string } | null => {
  const text = asText(value).trim();
  if (!text) return null;

  const amount = numberFromUnknown(text);
  if (amount === null) return null;

  const upperText = text.toUpperCase();
  let currency = '';

  if (upperText.includes('$')) {
    currency = 'USD';
  } else if (upperText.includes('€')) {
    currency = 'EUR';
  } else if (/[A-Z]{3}/.test(upperText)) {
    currency = upperText.match(/[A-Z]{3}/)?.[0] || '';
  } else if (/₽|РУБ|RUB|РОССИЙСК/.test(upperText)) {
    currency = 'RUB';
  }

  return currency ? { amount, currency } : null;
};

const formatRubles = (value: number): string => (
  new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 0,
  }).format(Math.round(value))
);

const convertSalaryToRub = (value: unknown, rates: CurrencyRates | null): string | null => {
  const parsed = parseSalaryExpectation(value);
  if (!parsed || parsed.currency === 'RUB' || !rates) return null;

  const rate = rates[parsed.currency];
  if (!rate) return null;

  return `≈ ${formatRubles(parsed.amount * rate)} ₽`;
};

const formatSalaryExpectation = (value: unknown): string => {
  const original = getStringValue(value);
  const parsed = parseSalaryExpectation(value);
  if (!original || !parsed) return original;
  return `${formatRubles(parsed.amount)} ${parsed.currency}`;
};

const SALARY_CURRENCY_OPTIONS = [
  { value: 'RUB', label: 'RUB' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
] as const;

type SalaryCurrency = (typeof SALARY_CURRENCY_OPTIONS)[number]['value'];

const splitSalaryExpectation = (value: unknown): { amount: string; currency: SalaryCurrency } => {
  const text = getStringValue(value).trim();
  if (!text) return { amount: '', currency: 'RUB' };

  const parsed = parseSalaryExpectation(text);
  if (parsed && (parsed.currency === 'RUB' || parsed.currency === 'USD' || parsed.currency === 'EUR')) {
    return {
      amount: String(parsed.amount).replace(/\.0$/, ''),
      currency: parsed.currency,
    };
  }

  const amount = text
    .replace(/(?:RUB|USD|EUR|РУБ(?:Л(?:ЕЙ|Я|Ь))?|₽|\$|€)/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  return { amount: amount || text, currency: 'RUB' };
};

const composeSalaryExpectation = (amount: string, currency: SalaryCurrency): string | null => {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  return `${trimmed} ${currency}`;
};

const getCandidateUpdatedTime = (item: AtsCandidateListItem): number => {
  const time = new Date(item.updated_at || item.created_at || '').getTime();
  return Number.isFinite(time) ? time : 0;
};

const sortCandidates = (items: AtsCandidateListItem[], sort: AtsCandidateSort): AtsCandidateListItem[] => (
  [...items].sort((left, right) => {
    if (sort === 'full_name') {
      return (left.full_name || '').localeCompare(right.full_name || '', 'ru');
    }

    return getCandidateUpdatedTime(right) - getCandidateUpdatedTime(left);
  })
);

const parseFilterNumber = (value: string): number | undefined => {
  const parsed = numberFromUnknown(value);
  return parsed === null ? undefined : parsed;
};

const parseCompanyList = (value: string): string[] => value
  .split(',')
  .map((company) => company.trim())
  .filter(Boolean);

const buildSearchFilters = (filters: AtsFilterState): AtsSearchFilters => {
  const ageFrom = parseFilterNumber(filters.ageFrom);
  const ageTo = parseFilterNumber(filters.ageTo);
  const experienceFrom = parseFilterNumber(filters.experienceFrom);
  const experienceTo = parseFilterNumber(filters.experienceTo);
  const salaryFrom = parseFilterNumber(filters.salaryFrom);
  const salaryTo = parseFilterNumber(filters.salaryTo);

  return {
    ...(filters.source.trim() ? { source: filters.source.trim() } : {}),
    ...(filters.recruiter.trim() ? { responsible_recruiter: filters.recruiter.trim() } : {}),
    ...(filters.recruiterIds.length ? { responsible_recruiter_ids: filters.recruiterIds } : {}),
    ...(filters.vacancy.trim() ? { vacancy: filters.vacancy.trim() } : {}),
    ...(filters.vacancyPaths.length ? { vacancy_paths: filters.vacancyPaths } : {}),
    ...(parseCompanyList(filters.stopCompanies).length ? { stop_companies: parseCompanyList(filters.stopCompanies) } : {}),
    ...(parseCompanyList(filters.donorCompanies).length ? { donor_companies: parseCompanyList(filters.donorCompanies) } : {}),
    ...(parseCompanyList(filters.stopCompanies).length || parseCompanyList(filters.donorCompanies).length
      ? { company_match_scope: filters.companyMatchScope }
      : {}),
    ...(filters.position.trim()
      ? {
          desired_position: filters.position.trim(),
          desired_position_mode: filters.positionMode,
        }
      : {}),
    ...(filters.skillGroups.some((group) => group.skills.length)
      ? {
          skill_groups: filters.skillGroups
            .filter((group) => group.skills.length)
            .map(({ operator, skills }) => ({ operator, skills })),
        }
      : {}),
    ...(parseCompanyList(filters.tags).length ? { tags: parseCompanyList(filters.tags) } : {}),
    ...(filters.location.trim() ? { location: filters.location.trim() } : {}),
    ...(filters.country.trim() ? { country: filters.country.trim() } : {}),
    ...(filters.city.trim() ? { city: filters.city.trim() } : {}),
    ...(filters.region ? { region: filters.region } : {}),
    ...(ageFrom !== undefined ? { age_from: ageFrom } : {}),
    ...(ageTo !== undefined ? { age_to: ageTo } : {}),
    ...(ageFrom !== undefined || ageTo !== undefined
      ? { include_unknown_age: filters.includeUnknownAge }
      : {}),
    ...(experienceFrom !== undefined ? { experience_years_from: experienceFrom } : {}),
    ...(experienceTo !== undefined ? { experience_years_to: experienceTo } : {}),
    ...(filters.educationLevels.length ? { education_levels: filters.educationLevels } : {}),
    ...(filters.educationLevels.length
      ? { include_unknown_education: filters.includeUnknownEducation }
      : {}),
    ...(salaryFrom !== undefined ? { salary_from: salaryFrom } : {}),
    ...(salaryTo !== undefined ? { salary_to: salaryTo } : {}),
    ...(salaryFrom !== undefined || salaryTo !== undefined
      ? { salary_currency: filters.currency, include_unknown_salary: filters.includeUnknownSalary }
      : {}),
    ...(filters.blacklist === 'only' ? { is_blacklisted: true } : {}),
    ...(filters.blacklist === 'exclude' ? { is_blacklisted: false } : {}),
    ...(filters.documents === 'with' ? { has_documents: true } : {}),
    ...(filters.documents === 'without' ? { has_documents: false } : {}),
    ...(filters.anyWord.trim() ? { any_word: filters.anyWord.trim() } : {}),
  };
};

const SEARCH_OPERATORS_HINT = 'Возможно использование операторов AND, OR, NOT, скобок и фраз в кавычках';

export const AtsSearchOperatorsHint = ({
  className = '',
}: {
  className?: string;
}) => (
  <p
    data-testid="ats-search-operators-hint"
    className={`max-w-full break-words text-[12px] leading-4 text-[#868C98] ${className}`.trim()}
  >
    {SEARCH_OPERATORS_HINT}
  </p>
);

type ActiveFilterChip = {
  id: string;
  label: string;
};

const getActiveFilterChips = (
  filters: AtsFilterState,
  status: string,
  recruiters: RecruiterOption[],
): ActiveFilterChip[] => {
  const chips: ActiveFilterChip[] = [];
  if (status) chips.push({ id: 'status', label: `Статус: ${getStatusLabel(status)}` });
  if (filters.source.trim()) chips.push({ id: 'source', label: `Источник: ${filters.source.trim()}` });
  filters.recruiterIds.forEach((id) => {
    const recruiter = recruiters.find((item) => item.id === id);
    chips.push({ id: `recruiter:${id}`, label: `Рекрутер: ${recruiter?.name || id}` });
  });
  if (filters.recruiter.trim() && !filters.recruiterIds.length) {
    chips.push({ id: 'recruiterText', label: `Рекрутер: ${filters.recruiter.trim()}` });
  }
  if (filters.vacancy.trim()) chips.push({ id: 'vacancy', label: `Вакансия: ${filters.vacancy.trim()}` });
  filters.vacancyPaths.forEach((path) => chips.push({ id: `vacancyPath:${path}`, label: `Папка: ${path}` }));
  if (filters.stopCompanies.trim()) chips.push({ id: 'stopCompanies', label: `Стоп-компании: ${filters.stopCompanies.trim()}` });
  if (filters.donorCompanies.trim()) chips.push({ id: 'donorCompanies', label: `Донор-компании: ${filters.donorCompanies.trim()}` });
  if (filters.position.trim()) {
    chips.push({
      id: 'position',
      label: `Должность (${filters.positionMode === 'exact' ? 'точно' : 'частично'}): ${filters.position.trim()}`,
    });
  }
  if (filters.ageFrom.trim() || filters.ageTo.trim() || !filters.includeUnknownAge) {
    const ageLabel = [filters.ageFrom.trim() && `от ${filters.ageFrom.trim()}`, filters.ageTo.trim() && `до ${filters.ageTo.trim()}`]
      .filter(Boolean)
      .join(' ');
    chips.push({ id: 'age', label: `Возраст: ${ageLabel || 'без неизвестного'}` });
  }
  if (filters.experienceFrom.trim() || filters.experienceTo.trim()) {
    chips.push({
      id: 'experience',
      label: `Опыт: ${[filters.experienceFrom.trim() && `от ${filters.experienceFrom}`, filters.experienceTo.trim() && `до ${filters.experienceTo}`].filter(Boolean).join(' ')}`,
    });
  }
  if (filters.educationLevels.length) {
    const labels = filters.educationLevels
      .map((level) => EDUCATION_LEVEL_OPTIONS.find((option) => option.value === level)?.label || level)
      .join(', ');
    chips.push({ id: 'education', label: `Образование: ${labels}` });
  }
  filters.skillGroups.forEach((group, index) => {
    if (!group.skills.length) return;
    chips.push({
      id: `skillGroup:${group.id}`,
      label: `Навыки ${index + 1} (${group.operator.toUpperCase()}): ${group.skills.join(', ')}`,
    });
  });
  if (filters.location.trim()) chips.push({ id: 'location', label: `Локация: ${filters.location.trim()}` });
  if (filters.country.trim()) chips.push({ id: 'country', label: `Страна: ${filters.country.trim()}` });
  if (filters.city.trim()) chips.push({ id: 'city', label: `Город: ${filters.city.trim()}` });
  if (filters.region) chips.push({ id: 'region', label: `Регион: ${filters.region}` });
  if (filters.salaryFrom.trim() || filters.salaryTo.trim() || !filters.includeUnknownSalary) {
    const salaryLabel = [filters.salaryFrom.trim() && `от ${filters.salaryFrom.trim()}`, filters.salaryTo.trim() && `до ${filters.salaryTo.trim()}`]
      .filter(Boolean)
      .join(' ');
    chips.push({ id: 'salary', label: `Зарплата (${filters.currency}): ${salaryLabel || 'без неизвестной'}` });
  }
  if (filters.tags.trim()) chips.push({ id: 'tags', label: `Метки: ${filters.tags.trim()}` });
  if (filters.blacklist !== 'any') {
    chips.push({ id: 'blacklist', label: filters.blacklist === 'only' ? 'Только ЧС' : 'Без ЧС' });
  }
  if (filters.documents !== 'any') {
    chips.push({ id: 'documents', label: filters.documents === 'with' ? 'С документами' : 'Без документов' });
  }
  if (filters.anyWord.trim()) chips.push({ id: 'anyWord', label: `Любое слово: ${filters.anyWord.trim()}` });
  if (filters.companyMatchScope === 'all' && (filters.stopCompanies.trim() || filters.donorCompanies.trim())) {
    chips.push({ id: 'companyMatchScope', label: 'Компании: весь опыт' });
  }
  return chips;
};

const clearFilterChip = (
  chipId: string,
  filters: AtsFilterState,
  status: string,
): { filters: AtsFilterState; status: string } => {
  if (chipId === 'status') return { filters, status: '' };
  if (chipId === 'source') return { filters: { ...filters, source: '' }, status };
  if (chipId === 'recruiterText') return { filters: { ...filters, recruiter: '' }, status };
  if (chipId.startsWith('recruiter:')) {
    const recruiterId = chipId.slice('recruiter:'.length);
    return {
      filters: { ...filters, recruiterIds: filters.recruiterIds.filter((id) => id !== recruiterId) },
      status,
    };
  }
  if (chipId === 'vacancy') return { filters: { ...filters, vacancy: '' }, status };
  if (chipId.startsWith('vacancyPath:')) {
    const path = chipId.slice('vacancyPath:'.length);
    return { filters: { ...filters, vacancyPaths: filters.vacancyPaths.filter((item) => item !== path) }, status };
  }
  if (chipId === 'stopCompanies') return { filters: { ...filters, stopCompanies: '' }, status };
  if (chipId === 'donorCompanies') return { filters: { ...filters, donorCompanies: '' }, status };
  if (chipId === 'position') return { filters: { ...filters, position: '' }, status };
  if (chipId === 'age') return { filters: { ...filters, ageFrom: '', ageTo: '', includeUnknownAge: true }, status };
  if (chipId === 'experience') return { filters: { ...filters, experienceFrom: '', experienceTo: '' }, status };
  if (chipId === 'education') return { filters: { ...filters, educationLevels: [], includeUnknownEducation: true }, status };
  if (chipId.startsWith('skillGroup:')) {
    const groupId = chipId.slice('skillGroup:'.length);
    return { filters: { ...filters, skillGroups: filters.skillGroups.filter((group) => group.id !== groupId) }, status };
  }
  if (chipId === 'location') return { filters: { ...filters, location: '' }, status };
  if (chipId === 'country') return { filters: { ...filters, country: '' }, status };
  if (chipId === 'city') return { filters: { ...filters, city: '' }, status };
  if (chipId === 'region') return { filters: { ...filters, region: '' }, status };
  if (chipId === 'salary') {
    return { filters: { ...filters, salaryFrom: '', salaryTo: '', includeUnknownSalary: true, currency: 'RUB' }, status };
  }
  if (chipId === 'tags') return { filters: { ...filters, tags: '' }, status };
  if (chipId === 'blacklist') return { filters: { ...filters, blacklist: 'any' }, status };
  if (chipId === 'documents') return { filters: { ...filters, documents: 'any' }, status };
  if (chipId === 'anyWord') return { filters: { ...filters, anyWord: '' }, status };
  if (chipId === 'companyMatchScope') return { filters: { ...filters, companyMatchScope: 'current' }, status };
  return { filters, status };
};

const flattenFolderOptions = (nodes: AtsFolderNode[]): Array<{ value: string; label: string; level: number; isLeaf: boolean }> => (
  nodes.flatMap((node) => [
    {
      value: node.path,
      label: node.name,
      level: node.path.split('/').filter(Boolean).length - 1,
      isLeaf: node.children.length === 0,
    },
    ...flattenFolderOptions(node.children),
  ])
);

const folderPathParts = (folderPath: string): [string, string, string] => {
  const parts = folderPath.split('/').filter(Boolean);
  const country = parts[0] && !isAllCountriesFolderName(parts[0]) ? parts[0] : DEFAULT_COUNTRY_FOLDER;
  return [country, parts[1] || 'Без проекта', parts[2] || 'Без вакансии'];
};

const AtsTextInput = ({
  value,
  onChange,
  placeholder,
  type = 'text',
  autoFocus,
  className = '',
  inputClassName = '',
  onKeyDown,
  ariaLabel,
  invalid,
  describedBy,
}: {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
  className?: string;
  inputClassName?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  ariaLabel?: string;
  invalid?: boolean;
  describedBy?: string;
}) => (
  <BaseInput className={`${className} ${invalid ? '[&_.base-input-container]:!border-[#B42318]' : ''}`} size="sm">
    <input
      type={type}
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      className={`w-full bg-transparent text-[14px] text-[#23252D] outline-none placeholder:text-[#A0A4AE] ${inputClassName}`}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
    />
  </BaseInput>
);

const AtsTextArea = ({
  value,
  onChange,
  placeholder,
  rows = 4,
  autoFocus,
  className = '',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  autoFocus?: boolean;
  className?: string;
}) => (
  <BaseInput className={className} dynamicHeight>
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={rows}
      placeholder={placeholder}
      autoFocus={autoFocus}
      className="w-full resize-none bg-transparent text-[14px] leading-6 text-[#23252D] outline-none placeholder:text-[#A0A4AE]"
    />
  </BaseInput>
);

const formatDate = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('ru-RU');
};

const getCandidateDisplayName = (name?: string | null): string => {
  const cleaned = (name || '').trim();
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cleaned);
  const isResumeHeading = /^(?:желаемая\s+должность(?:\s+и\s+зарплата)?|desired\s+position(?:\s+and\s+salary)?)$/i.test(cleaned);
  return cleaned && !isUuid && !isResumeHeading ? cleaned : 'Без имени';
};

const getInitials = (name?: string | null): string => {
  const displayName = getCandidateDisplayName(name);
  if (displayName === 'Без имени') return '?';
  return displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

const getStatusLabel = (status: string): string => {
  if (status === 'active_processing') return 'В работе';
  if (status === 'talent_pool') return 'Неактивен';
  return STATUS_OPTIONS.find((option) => option.value === status)?.label || status || 'Без статуса';
};

const asText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Не заполнено';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(asText).join(', ');
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return String(record.name || record.title || record.value || record.text || JSON.stringify(record));
  }
  return String(value);
};

const getItemId = (item: unknown): string => {
  if (typeof item === 'object' && item !== null && 'id' in item) {
    return String((item as Record<string, unknown>).id || '');
  }
  return String(item);
};

const getCommentText = (item: unknown): string => {
  if (typeof item === 'object' && item !== null) {
    return String((item as Record<string, unknown>).text || '');
  }
  return String(item || '');
};

const getStringValue = (value: unknown): string => (
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : ''
);

const getRequestStatus = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { status?: unknown } }).response;
    return typeof response?.status === 'number' ? response.status : undefined;
  }
  return undefined;
};

const recruiterFromUnknown = (value: unknown): RecruiterOption | null => {
  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>;
    const name = getStringValue(record.name) || getStringValue(record.full_name) || getStringValue(record.title);
    const email = getStringValue(record.email);
    const id = getStringValue(record.id) || email || name;
    if (!name && !email) return null;

    return {
      id: id || 'responsible-recruiter',
      name: name || email,
      email,
      color: getStringValue(record.color) || '#D8E9FF',
    };
  }

  const text = getStringValue(value);
  return text ? { id: text, name: text, email: '', color: '#D8E9FF' } : null;
};

const getResponsibleRecruiter = (card?: AtsCandidateCard | null): RecruiterOption | null => {
  const customFields = card?.custom_fields || {};
  return recruiterFromUnknown(customFields.responsible_recruiter)
    || recruiterFromUnknown({
      id: customFields.responsible_recruiter_id,
      name: customFields.responsible_recruiter_name,
      email: customFields.responsible_recruiter_email,
    });
};

const recruiterFromUserRecord = (value: unknown, index: number): RecruiterOption | null => {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const firstName = getStringValue(record.firstName) || getStringValue(record.first_name);
  const lastName = getStringValue(record.lastName) || getStringValue(record.last_name);
  const email = getStringValue(record.email);
  const fullName = getStringValue(record.fullName)
    || getStringValue(record.full_name)
    || getStringValue(record.name)
    || [firstName, lastName].filter(Boolean).join(' ')
    || email;
  if (!fullName) return null;

  return {
    id: getStringValue(record.id) || email || fullName,
    name: fullName,
    email,
    color: getStringValue(record.color) || RECRUITER_COLORS[index % RECRUITER_COLORS.length],
  };
};

const uniqueRecruiterOptions = (items: Array<RecruiterOption | null | undefined>): RecruiterOption[] => {
  const options: RecruiterOption[] = [];

  items.forEach((item) => {
    if (!item) return;
    const exists = options.some((option) => option.id === item.id || (!!item.email && option.email === item.email));
    if (!exists) options.push(item);
  });

  return options;
};

const buildRecruiterOptions = (companyUsers: unknown[]): RecruiterOption[] => (
  uniqueRecruiterOptions(companyUsers.map(recruiterFromUserRecord))
);

const getVisibleCustomFields = (customFields: Record<string, unknown> = {}): Array<[string, unknown]> => (
  Object.entries(customFields).filter(([key]) => !RESPONSIBLE_RECRUITER_KEYS.has(key))
);

type CustomFieldValueType = 'text' | 'number' | 'date' | 'list';

const customFieldInputValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (value === null || value === undefined) return '';
  return String(value);
};

const inferCustomFieldType = (value: unknown): CustomFieldValueType => {
  if (Array.isArray(value)) return 'list';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return 'date';
  return 'text';
};

const parseCustomFieldValue = (value: string, type: CustomFieldValueType): unknown => {
  if (type === 'number') {
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : value;
  }
  if (type === 'list') {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return value;
};

const getContactValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return getStringValue(record.value)
      || getStringValue(record.phone)
      || getStringValue(record.email)
      || getStringValue(record.telegram)
      || getStringValue(record.url)
      || getStringValue(record.link)
      || getStringValue(record.text)
      || getStringValue(record.name);
  }
  return '';
};

const normalizeContactList = (value: unknown, fallback?: unknown): string[] => {
  const parsed = parseJsonLike(value);
  const items = Array.isArray(parsed) ? parsed : (parsed ? [parsed] : []);
  const normalized = items.map(getContactValue).filter(Boolean);
  const fallbackValue = getContactValue(fallback);
  return normalized.length || !fallbackValue ? normalized : [fallbackValue];
};

const editableContactList = (value: unknown, fallback?: unknown): string[] => {
  const parsed = parseJsonLike(value);
  if (Array.isArray(parsed) && parsed.length) return parsed.map(getContactValue);
  const fallbackValue = getContactValue(fallback);
  return fallbackValue ? [fallbackValue] : [];
};

const cleanContactList = (value: unknown): string[] => (
  (Array.isArray(value) ? value : []).map(getContactValue).map((item) => item.trim()).filter(Boolean)
);

export const cleanCandidateContactDraft = (draft: Partial<AtsCandidateCard>): Partial<AtsCandidateCard> => {
  const payload = { ...draft };
  if (payload.phones !== undefined) payload.phones = cleanContactList(payload.phones);
  if (payload.emails !== undefined) payload.emails = cleanContactList(payload.emails);
  if (payload.links !== undefined) payload.links = cleanContactList(payload.links);
  return payload;
};

const TECHNICAL_LINK_HOSTS = [
  'schemas.microsoft.com',
  'schemas.openxmlformats.org',
  'www.w3.org',
];

const toExternalLink = (value: string): URL | null => {
  try {
    return new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
};

const getCandidateLinks = (value: unknown): Array<{ href: string; label: string }> => (
  normalizeContactList(value)
    .map((item) => toExternalLink(item.trim()))
    .filter((url): url is URL => Boolean(url))
    .filter((url) => !TECHNICAL_LINK_HOSTS.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`)))
    .map((url) => ({
      href: url.href,
      label: `${url.hostname.replace(/^www\./, '')}${url.pathname === '/' ? '' : url.pathname}`,
    }))
);

const normalizePhoneList = (value: unknown, fallback?: unknown): string[] => {
  const items = normalizeContactList(value, fallback);
  const unique = new Set<string>();

  return items.filter((item) => {
    const trimmed = item.trim();
    const digits = trimmed.replace(/\D/g, '');
    const isInternational = trimmed.startsWith('+') && digits.length >= 10 && digits.length <= 15;
    const isRussian = digits.length === 11 && /^(7|8)/.test(digits);
    if (!isInternational && !isRussian) return false;

    const normalized = isRussian && digits.startsWith('8') ? `+7${digits.slice(1)}` : `+${digits}`;
    if (unique.has(normalized)) return false;
    unique.add(normalized);
    return true;
  });
};

const getLatestBlacklistInfo = (history?: unknown[]) => {
  if (!Array.isArray(history)) return null;

  for (const event of [...history].reverse()) {
    if (typeof event !== 'object' || event === null) continue;
    const record = event as Record<string, unknown>;
    if (record.type !== 'blacklist_added') continue;

    const payload = typeof record.payload === 'object' && record.payload !== null
      ? record.payload as Record<string, unknown>
      : {};
    const date = getStringValue(payload.date) || getStringValue(record.at);

    return {
      reason: getStringValue(payload.reason),
      author: getStringValue(payload.author),
      date: date ? formatDate(date) : '',
    };
  }

  return null;
};

const getLabelText = (item: unknown): string => {
  if (typeof item === 'object' && item !== null) {
    const record = item as Record<string, unknown>;
    return String(record.title || record.name || record.text || record.value || '');
  }
  return String(item || '');
};

const getLabelColor = (item: unknown, fallback = '#7757FF'): string => {
  if (typeof item === 'object' && item !== null) {
    const color = (item as Record<string, unknown>).color;
    return typeof color === 'string' && color ? color : fallback;
  }
  return fallback;
};

const HISTORY_TYPE_LABELS: Record<string, string> = {
  resume_parsed: 'Резюме распознано',
  manual_update: 'Карточка изменена',
  comment_added: 'Комментарий добавлен',
  comment_updated: 'Комментарий изменён',
  comment_deleted: 'Комментарий удалён',
  label_added: 'Метка добавлена',
  label_updated: 'Метка изменена',
  label_deleted: 'Метка удалена',
  red_flag_added: 'Red Flag добавлен',
  red_flag_updated: 'Red Flag изменён',
  red_flag_deleted: 'Red Flag удалён',
  process_stage: 'Этап процессинга изменён',
  status_change: 'Статус кандидата изменён',
  candidate_refused: 'Самоотказ кандидата',
  attachment_added: 'Вложение добавлено',
  document_deleted: 'Документ удалён',
  folder_moved: 'Карточка перенесена в папку',
  blacklist_added: 'Кандидат добавлен в чёрный список',
  blacklist_removed: 'Кандидат исключён из чёрного списка',
  responsible_recruiter_changed: 'Изменён ответственный рекрутер',
};

const formatAuditValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Не заполнено';
  if (Array.isArray(value)) return value.map(formatAuditValue).join(', ') || 'Не заполнено';
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${key}: ${formatAuditValue(item)}`)
      .join('; ');
  }
  return String(value);
};

const generateLocalId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const parseJsonLike = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const normalizeExperienceItems = (value: unknown): unknown[] => {
  const parsed = parseJsonLike(value);
  if (Array.isArray(parsed)) {
    return parsed.flatMap(normalizeExperienceItems);
  }
  return parsed === null || parsed === undefined || parsed === '' ? [] : [parsed];
};

const ruPlural = (value: number, one: string, few: string, many: string): string => {
  const abs = Math.abs(value) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
};

const formatExperienceYearsLabel = (months: number): string => {
  const years = Math.floor(months / 12);
  const restMonths = months % 12;
  if (years <= 0) {
    return `${restMonths} ${ruPlural(restMonths, 'месяц', 'месяца', 'месяцев')}`;
  }
  const yearsLabel = `${years} ${ruPlural(years, 'год', 'года', 'лет')}`;
  if (!restMonths) return yearsLabel;
  return `${yearsLabel} ${restMonths} ${ruPlural(restMonths, 'месяц', 'месяца', 'месяцев')}`;
};

export const formatCandidateExperienceSummary = (card: Pick<AtsCandidateCard, 'experience' | 'work_history' | 'experience_months'>): string => {
  const items = normalizeExperienceItems(card.experience?.length ? card.experience : card.work_history);
  const places = items.length;
  const months = card.experience_months ?? null;
  const parts: string[] = [];
  if (months !== null) parts.push(formatExperienceYearsLabel(months));
  if (places > 0) parts.push(`${places} ${ruPlural(places, 'место', 'места', 'мест')}`);
  return parts.length ? `Опыт: ${parts.join(' · ')}` : 'Опыт не указан';
};

const experienceField = (item: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = item[key];
    if (value !== null && value !== undefined && value !== '') {
      return asText(value);
    }
  }
  return '';
};

const editorField = (item: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = item[key];
    if (value !== null && value !== undefined && value !== '') {
      return String(value);
    }
  }
  return '';
};

const toExperienceRecord = (item: unknown): Record<string, unknown> => {
  const parsed = parseJsonLike(item);
  if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
    return parsed as Record<string, unknown>;
  }
  const text = parsed === null || parsed === undefined || parsed === '' ? '' : String(parsed);
  return { description: text };
};

const toEducationRecord = (item: unknown): Record<string, unknown> => {
  const parsed = parseJsonLike(item);
  if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
    return parsed as Record<string, unknown>;
  }
  const text = parsed === null || parsed === undefined || parsed === '' ? '' : String(parsed);
  return { institution: text };
};

const emptyExperienceItem = (): Record<string, unknown> => ({
  position: '',
  company: '',
  period_text: '',
  description: '',
});

const emptyEducationItem = (): Record<string, unknown> => ({
  institution: '',
  level: '',
  speciality: '',
  start_year: '',
  end_year: '',
});

const educationSummary = (item: unknown): string => {
  const record = toEducationRecord(item);
  return editorField(record, ['institution', 'university', 'school', 'name']);
};

const normalizeStructuredItems = (value: unknown): unknown[] => {
  const parsed = parseJsonLike(value);
  if (Array.isArray(parsed)) {
    return parsed.flatMap(normalizeStructuredItems);
  }
  return parsed === null || parsed === undefined || parsed === '' ? [] : [parsed];
};

const normalizeEducationItems = (value: unknown): unknown[] => {
  const items = normalizeStructuredItems(value);
  const fragments = items.map((item) => {
    const parsed = parseJsonLike(item);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return asText(parsed);
    return experienceField(parsed as Record<string, unknown>, ['institution', 'university', 'school', 'name']);
  }).filter((item) => item && item !== 'Не заполнено');
  const hasSplitRecord = fragments.length > 1
    && fragments.some((item) => (item.match(/\(/g) || []).length !== (item.match(/\)/g) || []).length);

  if (!hasSplitRecord) return items;

  const mergedFragments = fragments.reduce<string[]>((result, fragment) => {
    if (!result.some((previous) => previous.includes(fragment))) result.push(fragment);
    return result;
  }, []);
  const lastItem = parseJsonLike(items[items.length - 1]);
  const lastRecord = typeof lastItem === 'object' && lastItem !== null && !Array.isArray(lastItem)
    ? lastItem as Record<string, unknown>
    : {};
  return [{ ...lastRecord, institution: mergedFragments.join(' ') }];
};

const ExperienceItem = ({ item }: { item: unknown }) => {
  const parsed = parseJsonLike(item);

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return (
      <div className="rounded-[8px] border border-[#EEF0F2] bg-[#F8F9FB] p-4 text-[14px] leading-6 text-[#1F2A44] whitespace-pre-line">
        <HighlightedText text={asText(parsed)} />
      </div>
    );
  }

  const record = parsed as Record<string, unknown>;
  const company = experienceField(record, ['company', 'company_name', 'employer', 'organization']);
  const position = experienceField(record, ['position', 'title', 'role']);
  const industry = experienceField(record, ['industry', 'company_industry']);
  const startDate = experienceField(record, ['start_date', 'started_at', 'date_from']);
  const endDate = experienceField(record, ['end_date', 'ended_at', 'date_to']);
  const period = experienceField(record, ['period_text', 'period', 'duration']);
  const description = experienceField(record, ['description', 'responsibilities', 'duties']);
  const skills = normalizeExperienceItems(record.skills);

  return (
    <article className="rounded-[8px] border border-[#EEF0F2] bg-[#F8F9FB] p-4">
      <div className="flex flex-col gap-1">
        <div className="text-[15px] font-semibold text-[#1F2A44]">
          <HighlightedText text={position || 'Должность не указана'} />
        </div>
        {company && (
          <div className="text-[14px] text-[#525866]">
            <HighlightedText text={company} />
          </div>
        )}
        {(startDate || endDate || period) && (
          <div className="text-[13px] text-[#868C98]">
            {[startDate, endDate].filter(Boolean).join(' — ')}
            {period ? `${startDate || endDate ? ' · ' : ''}${period}` : ''}
          </div>
        )}
        {industry && (
          <div className="text-[13px] text-[#868C98]">
            Отрасль: <HighlightedText text={industry} />
          </div>
        )}
      </div>

      {description && (
        <div className="mt-3 text-[14px] leading-6 text-[#1F2A44] whitespace-pre-line">
          <HighlightedText text={description} />
        </div>
      )}

      {skills.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {skills.map((skill, index) => (
            <span key={`${asText(skill)}-${index}`} className="px-2 py-1 rounded-[8px] bg-white text-[13px] text-[#1F2A44] border border-[#EEF0F2]">
              <HighlightedText text={asText(skill)} />
            </span>
          ))}
        </div>
      )}
    </article>
  );
};

const EducationItem = ({ item }: { item: unknown }) => {
  const parsed = parseJsonLike(item);

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return (
      <div className="rounded-[8px] border border-[#EEF0F2] bg-[#F8F9FB] p-4 text-[14px] leading-6 text-[#1F2A44] whitespace-pre-line">
        <HighlightedText text={asText(parsed)} />
      </div>
    );
  }

  const record = parsed as Record<string, unknown>;
  const level = experienceField(record, ['level', 'degree', 'education_level']);
  const institution = experienceField(record, ['institution', 'university', 'school', 'name']);
  const speciality = experienceField(record, ['speciality', 'specialty', 'faculty', 'field']);
  const startYear = experienceField(record, ['start_year', 'year_from']);
  const endYear = experienceField(record, ['end_year', 'graduation_year', 'year_to']);
  const years = [startYear, endYear].filter(Boolean).join(' — ');

  return (
    <article className="rounded-[8px] border border-[#EEF0F2] bg-[#F8F9FB] p-4">
      <div className="text-[15px] font-semibold text-[#1F2A44]">
        <HighlightedText text={institution || 'Учебное заведение не указано'} />
      </div>
      {level && (
        <div className="mt-1 text-[13px] text-[#868C98]">
          Уровень: <HighlightedText text={level} />
        </div>
      )}
      {speciality && (
        <div className="mt-2 text-[14px] leading-6 text-[#525866]">
          <HighlightedText text={speciality} />
        </div>
      )}
      {years && <div className="mt-2 text-[13px] text-[#868C98]">{years}</div>}
    </article>
  );
};

const ExperienceEditor = ({
  item,
  onChange,
  onRemove,
}: {
  item: unknown;
  onChange: (item: Record<string, unknown>) => void;
  onRemove: () => void;
}) => {
  const record = toExperienceRecord(item);
  const update = (patch: Record<string, unknown>) => onChange({ ...record, ...patch });

  return (
    <article className="rounded-[8px] border border-[#EEF0F2] bg-[#F8F9FB] p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <AtsTextInput
          value={editorField(record, ['position', 'title', 'role'])}
          onChange={(value) => update({ position: value })}
          placeholder="Должность"
        />
        <AtsTextInput
          value={editorField(record, ['company', 'company_name', 'employer', 'organization'])}
          onChange={(value) => update({ company: value })}
          placeholder="Компания"
        />
      </div>
      <AtsTextInput
        value={editorField(record, ['period_text', 'period', 'duration']) || [editorField(record, ['start_date', 'started_at', 'date_from']), editorField(record, ['end_date', 'ended_at', 'date_to'])].filter(Boolean).join(' — ')}
        onChange={(value) => update({ period_text: value, start_date: undefined, end_date: undefined })}
        placeholder="Период, например январь 2020 — настоящее время"
        className="mt-3"
      />
      <AtsTextArea
        value={editorField(record, ['description', 'responsibilities', 'duties'])}
        onChange={(value) => update({ description: value })}
        placeholder="Обязанности и достижения"
        rows={3}
        className="mt-3"
      />
      <button type="button" onClick={onRemove} className="mt-3 text-[13px] font-semibold text-[#E5484D]">
        Удалить место работы
      </button>
    </article>
  );
};

const EducationEditor = ({
  item,
  onChange,
  onRemove,
}: {
  item: unknown;
  onChange: (item: Record<string, unknown>) => void;
  onRemove: () => void;
}) => {
  const record = toEducationRecord(item);
  const update = (patch: Record<string, unknown>) => onChange({ ...record, ...patch });

  return (
    <article className="rounded-[8px] border border-[#EEF0F2] bg-[#F8F9FB] p-4">
      <AtsTextInput
        value={editorField(record, ['institution', 'university', 'school', 'name'])}
        onChange={(value) => update({ institution: value })}
        placeholder="Учебное заведение"
      />
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <AtsTextInput
          value={editorField(record, ['level', 'degree', 'education_level'])}
          onChange={(value) => update({ level: value })}
          placeholder="Уровень"
        />
        <AtsTextInput
          value={editorField(record, ['speciality', 'specialty', 'faculty', 'field'])}
          onChange={(value) => update({ speciality: value })}
          placeholder="Специальность"
        />
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <AtsTextInput
          value={editorField(record, ['start_year', 'year_from'])}
          onChange={(value) => update({ start_year: value })}
          placeholder="Год начала"
        />
        <AtsTextInput
          value={editorField(record, ['end_year', 'graduation_year', 'year_to'])}
          onChange={(value) => update({ end_year: value })}
          placeholder="Год окончания"
        />
      </div>
      <button type="button" onClick={onRemove} className="mt-3 text-[13px] font-semibold text-[#E5484D]">
        Удалить образование
      </button>
    </article>
  );
};

const documentField = (doc: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = doc[key];
    if (value !== null && value !== undefined && value !== '') {
      return asText(value);
    }
  }
  return '';
};

const formatDocumentFileSize = (value: unknown): string => {
  const size = Number(value);
  if (!Number.isFinite(size) || size < 0) return '';
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} ГБ`;
};

const getDocumentType = (doc: unknown): string => {
  const parsed = parseJsonLike(doc);
  return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
    ? documentField(parsed as Record<string, unknown>, ['document_type', 'type'])
    : '';
};

const DocumentItem = ({
  doc,
  onDelete,
}: {
  doc: unknown;
  onDelete: (id: string) => void;
}) => {
  const parsed = parseJsonLike(doc);
  const id = getItemId(parsed);

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-[8px] border border-[#EEF0F2] bg-[#F8F9FB] p-3 text-[14px]">
        <span className="flex min-w-0 items-center gap-2 text-[#1F2A44]">
          <PaperClipIcon className="h-4 w-4 shrink-0 text-[#868C98]" />
          <span className="truncate">{asText(parsed)}</span>
        </span>
        {id && (
          <button type="button" onClick={() => onDelete(id)} className="text-[#D92D20]">
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  const record = parsed as Record<string, unknown>;
  const fileName = documentField(record, ['file_name', 'original_file_name', 'name', 'title']) || 'Документ';
  const format = documentField(record, ['format', 'content_type']);
  const documentType = documentField(record, ['document_type', 'type']);
  const status = documentField(record, ['status']);
  const sourcePath = documentField(record, ['folder_path', 'source_path']);
  const uploadedAt = documentField(record, ['uploaded_at', 'created_at']);
  const uploadedBy = documentField(record, ['uploaded_by', 'author']);
  const fileSize = formatDocumentFileSize(record.file_size);

  return (
    <div className="rounded-[8px] border border-[#EEF0F2] bg-[#F8F9FB] p-3">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          disabled={!id}
          onClick={() => {
            if (id) {
              void atsApi.downloadDocument(id, fileName).catch((error) => {
                console.error('Failed to download ATS document', error);
              });
            }
          }}
          className="flex min-w-0 items-center gap-2 text-[14px] font-medium text-[#1F2A44] hover:underline"
        >
          <PaperClipIcon className="h-4 w-4 shrink-0 text-[#868C98]" />
          <span className="truncate">{fileName}</span>
        </button>
        {id && (
          <button type="button" onClick={() => onDelete(id)} className="shrink-0 text-[#D92D20]">
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {[format, documentType, status].filter(Boolean).map((value) => (
          <span key={value} className="rounded-[6px] bg-white px-2 py-1 text-[12px] text-[#525866] border border-[#EEF0F2]">
            {value}
          </span>
        ))}
      </div>

      {(sourcePath || uploadedAt || uploadedBy || fileSize) && (
        <div className="mt-2 text-[12px] leading-5 text-[#868C98]">
          {sourcePath && <div className="break-words">{sourcePath}</div>}
          {(uploadedAt || uploadedBy || fileSize) && (
            <div>{[uploadedAt && formatDate(uploadedAt), uploadedBy, fileSize].filter(Boolean).join(' · ')}</div>
          )}
        </div>
      )}
    </div>
  );
};

const FieldRow = ({ label, value, multiple = false }: { label: string; value: unknown; multiple?: boolean }) => {
  const values = multiple
    ? normalizeContactList(value).filter(Boolean)
    : [asText(value)];

  return (
    <div className="flex flex-col gap-1 py-2 border-b border-[#EEF0F2] last:border-b-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <span className="text-[13px] text-[#868C98]">{label}</span>
      <div className="min-w-0 max-w-full break-words text-left text-[14px] text-[#1F2A44] sm:max-w-[260px] sm:text-right">
        {values.length
          ? values.map((item, index) => (
            <div key={`${item}-${index}`}>
              <HighlightedText text={item} />
            </div>
          ))
          : 'Не заполнено'}
      </div>
    </div>
  );
};

export const ContactFieldRow = ({ label, value, multiple = false }: { label: string; value: unknown; multiple?: boolean }) => {
  const values = multiple ? normalizeContactList(value).filter(Boolean)
    : (value === null || value === undefined || value === '' ? [] : [asText(value)]);
  return (
    <div className="grid min-w-0 grid-cols-[64px_minmax(0,1fr)] items-start gap-3 border-b border-[#EEF0F2] py-2 last:border-b-0">
      <span className="text-[13px] text-[#868C98]">{label}</span>
      <div className="min-w-0 text-[14px] text-[#1F2A44]">
        {values.length ? values.map((item, index) => (
          <ContactValue key={`${item}-${index}`} value={item} label={label} />
        )) : 'Не заполнено'}
      </div>
    </div>
  );
};

export const LinkFieldRow = ({ value }: { value: unknown }) => {
  const links = getCandidateLinks(value);

  return (
    <div className="grid min-w-0 grid-cols-[64px_minmax(0,1fr)] items-start gap-3 py-2 border-b border-[#EEF0F2] last:border-b-0">
      <span className="text-[13px] text-[#868C98]">Ссылки</span>
      <div className="min-w-0 text-left text-[14px]">
        {links.length ? links.map((link) => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            title={link.href}
            className="block truncate text-[#2F7BFF] hover:underline"
          >
            {link.label}
          </a>
        )) : 'Не заполнено'}
      </div>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="rounded-[14px] bg-white p-5 shadow-[0_8px_24px_rgba(21,22,24,0.08)]">
    <h3 className="mb-4 text-[13px] font-bold uppercase tracking-[0.05em] text-[#23252D]">{title}</h3>
    {children}
  </section>
);

const Pill = ({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'green' | 'red' | 'yellow' | 'violet' | 'blue' }) => {
  const classes = {
    neutral: 'bg-[#ECEBE6] text-[#525866]',
    green: 'bg-[#DFF4E8] text-[#178C4F]',
    red: 'bg-[#FFE2E2] text-[#E5484D]',
    yellow: 'bg-[#FFF3CD] text-[#8A5A00]',
    violet: 'bg-[#EEE6FF] text-[#7757FF]',
    blue: 'bg-[#E6F0FF] text-[#2F7BFF]',
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded-[7px] px-2 py-1 text-[12px] leading-none ${classes[tone]}`}>
      {children}
    </span>
  );
};

const getStatusTone = (status: string): 'neutral' | 'green' | 'yellow' => {
  if (['rejected', 'candidate_refused', 'security_rejected', 'inactive', 'withdrawn'].includes(status)) return 'yellow';
  if (status === 'in_progress' || status === 'active_processing' || status === 'offer') return 'green';
  if (status === 'hired') return 'green';
  return 'neutral';
};

const isRejectionStatus = (status: string): boolean => (
  ['rejected', 'candidate_refused', 'security_rejected', 'withdrawn', 'declined'].includes(status)
);

const historyStatusClass = (status: string): string => (
  isRejectionStatus(status)
    ? 'bg-[#FFF3CD] text-[#8A5A00]'
    : 'bg-[#DFF4E8] text-[#178C4F]'
);

const SalaryExpectation = ({
  value,
  rates,
  compact = false,
}: {
  value?: unknown;
  rates: CurrencyRates | null;
  compact?: boolean;
}) => {
  const original = formatSalaryExpectation(value);
  const converted = convertSalaryToRub(value, rates);

  if (!original) return null;

  if (compact) {
    return (
      <div className="text-right">
        <div>{original}</div>
        {converted && <div className="mt-0.5 text-[11px] font-medium text-[#868C98]">{converted}</div>}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Pill>Ожидания: {original}</Pill>
      {converted && <Pill>{converted}</Pill>}
    </div>
  );
};

const CandidateLabelPill = ({
  item,
  fallbackColor,
  onEdit,
  onDelete,
}: {
  item: unknown;
  fallbackColor: string;
  onEdit?: () => void;
  onDelete?: () => void;
}) => {
  const text = getLabelText(item);
  const color = getLabelColor(item, fallbackColor);

  if (!text) return null;

  return (
    <span
      className="inline-flex items-center gap-1 rounded-[999px] px-3 py-1 text-[13px] leading-none"
      style={{
        color,
        backgroundColor: `${color}1A`,
      }}
    >
      {onEdit ? (
        <button type="button" onClick={onEdit} className="text-current" aria-label={`Редактировать метку ${text}`}>{text}</button>
      ) : text}
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="ml-1 text-current opacity-70 hover:opacity-100"
          aria-label={`Удалить метку ${text}`}
        >
          ×
        </button>
      )}
    </span>
  );
};

const LabelPopover = ({
  type,
  setType,
  color,
  setColor,
  name,
  setName,
  onCancel,
  onSubmit,
  disabled,
  editing,
}: {
  type: 'label' | 'red_flag';
  setType: (type: 'label' | 'red_flag') => void;
  color: string;
  setColor: (color: string) => void;
  name: string;
  setName: (name: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  disabled: boolean;
  editing: boolean;
}) => (
  <div className="absolute left-0 top-full z-20 mt-3 w-[360px] rounded-[14px] bg-white p-4 shadow-[0_18px_48px_rgba(21,22,24,0.18)] ring-1 ring-[#E1DFD8]">
    <h3 className="text-[16px] font-bold text-[#23252D]">{editing ? 'Редактировать метку' : 'Новая метка'}</h3>

    {!editing && <div className="mt-4">
      <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.05em] text-[#868C98]">Тип</div>
      <TabToggle
        options={[
          { id: 'red_flag', label: 'Red Flag' },
          { id: 'label', label: 'Обычная метка' },
        ]}
        activeTab={type}
        onTabChange={(tab) => setType(tab as 'label' | 'red_flag')}
        radius="rounded-[8px]"
      />
    </div>}

    <div className="mt-4">
      <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.05em] text-[#868C98]">Цвет</div>
      <div className="flex gap-3">
        {LABEL_COLORS.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setColor(item)}
            className="flex h-8 w-8 items-center justify-center rounded-full"
            style={{ backgroundColor: item }}
            aria-label={`Выбрать цвет ${item}`}
          >
            {color === item && <span className="h-4 w-4 rounded-full border-2 border-white" />}
          </button>
        ))}
      </div>
    </div>

    <label className="mt-4 block">
      <span className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.05em] text-[#868C98]">Название</span>
      <AtsTextInput
        value={name}
        onChange={setName}
        placeholder={type === 'red_flag' ? 'Причина Red Flag' : 'Название метки'}
        inputClassName="font-semibold"
        autoFocus
      />
    </label>

    <div className="mt-4">
      <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.05em] text-[#C0BEB7]">Превью</div>
      <div className="rounded-[10px] bg-[#F8F7F3] p-3">
        <span className="inline-flex rounded-[7px] px-3 py-1 text-[13px]" style={{ color, backgroundColor: `${color}1A` }}>
          {name.trim() || (type === 'red_flag' ? 'Red Flag' : 'Новая метка')}
        </span>
      </div>
    </div>

    <div className="mt-4 grid grid-cols-[96px_minmax(0,1fr)] gap-3">
      <button type="button" onClick={onCancel} className="h-11 rounded-[9px] border border-[#DDDCD6] text-[14px] font-semibold text-[#77736B]">
        Отмена
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled || !name.trim()}
        className="h-11 rounded-[9px] bg-[#E16349] text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
      >
        {editing ? 'Сохранить' : 'Добавить'}
      </button>
    </div>
  </div>
);

type ContactType = 'phone' | 'email' | 'telegram' | 'link';

const CONTACT_OPTIONS: Array<{ type: ContactType; label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }> = [
  { type: 'phone', label: 'Телефон', icon: PhoneIcon },
  { type: 'email', label: 'E-mail', icon: EnvelopeIcon },
  { type: 'telegram', label: 'Telegram', icon: PaperAirplaneIcon },
  { type: 'link', label: 'Ссылка', icon: LinkIcon },
];

const ContactInputRow = ({
  icon: Icon,
  value,
  placeholder,
  onChange,
  onRemove,
  label,
  errorId,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onRemove: () => void;
  label: string;
  errorId?: string;
}) => (
  <div className="flex items-center gap-3">
    <Icon className="h-4 w-4 shrink-0 text-[#6B7280]" />
    <AtsTextInput
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="min-w-0 flex-1"
      ariaLabel={label}
      invalid={Boolean(errorId)}
      describedBy={errorId}
    />
    <button
      type="button"
      onClick={onRemove}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#9A9CA3] hover:bg-[#F5F4EF] hover:text-[#23252D]"
      aria-label="Удалить контакт"
    >
      <XMarkIcon className="h-4 w-4" />
    </button>
  </div>
);

export const EditableContacts = ({
  card,
  setDraft,
  fieldErrors = [],
  onFieldChange,
}: {
  card: AtsCandidateCard;
  setDraft: React.Dispatch<React.SetStateAction<Partial<AtsCandidateCard>>>;
  fieldErrors?: AtsFieldValidationError[];
  onFieldChange?: (fields: string[]) => void;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const phones = editableContactList(card.phones, card.phone);
  const emails = editableContactList(card.emails, card.email);
  const links = editableContactList(card.links);
  const telegram = getContactValue(card.telegram);
  const errorPrefix = React.useId();
  const contactError = (fields: string[]) => fieldErrors.filter(error => fields.includes(error.field))
    .map(error => error.message).filter((message, index, messages) => messages.indexOf(message) === index).join(' ');
  const phoneError = contactError(['phone', 'phones']);
  const emailError = contactError(['email', 'emails']);
  const telegramError = contactError(['telegram']);
  const linkError = contactError(['links']);

  const updateList = (field: 'phones' | 'emails' | 'links', values: string[]) => {
    onFieldChange?.(field === 'phones' ? ['phone', 'phones'] : field === 'emails' ? ['email', 'emails'] : [field]);
    const cleaned = values.map((value) => value.trim()).filter(Boolean);
    setDraft((current) => ({
      ...current,
      [field]: values,
      ...(field === 'phones' ? { phone: cleaned[0] || null } : {}),
      ...(field === 'emails' ? { email: cleaned[0] || null } : {}),
    }));
  };

  const addContact = (type: ContactType) => {
    if (type === 'phone') updateList('phones', [...phones, '']);
    if (type === 'email') updateList('emails', [...emails, '']);
    if (type === 'link') updateList('links', [...links, '']);
    if (type === 'telegram') setDraft((current) => ({ ...current, telegram: telegram || '' }));
    setMenuOpen(false);
  };

  return (
    <div className="space-y-3">
      {phones.map((phone, index) => (
        <ContactInputRow
          key={`phone-${index}`}
          icon={PhoneIcon}
          value={phone}
          placeholder="+7 999 999 99 99"
          label={`Телефон ${index + 1}`}
          errorId={phoneError ? `${errorPrefix}-phone` : undefined}
          onChange={(value) => updateList('phones', phones.map((item, itemIndex) => (itemIndex === index ? value : item)))}
          onRemove={() => updateList('phones', phones.filter((_, itemIndex) => itemIndex !== index))}
        />
      ))}
      {phoneError && <p id={`${errorPrefix}-phone`} className="text-[13px] text-[#B42318]">Телефон: {phoneError}</p>}
      {emails.map((email, index) => (
        <ContactInputRow
          key={`email-${index}`}
          icon={EnvelopeIcon}
          value={email}
          placeholder="name@example.com"
          label={`Email ${index + 1}`}
          errorId={emailError ? `${errorPrefix}-email` : undefined}
          onChange={(value) => updateList('emails', emails.map((item, itemIndex) => (itemIndex === index ? value : item)))}
          onRemove={() => updateList('emails', emails.filter((_, itemIndex) => itemIndex !== index))}
        />
      ))}
      {emailError && <p id={`${errorPrefix}-email`} className="text-[13px] text-[#B42318]">Email: {emailError}</p>}
      {(telegram || card.telegram === '') && (
        <ContactInputRow
          icon={PaperAirplaneIcon}
          value={telegram}
          placeholder="@username"
          label="Telegram"
          errorId={telegramError ? `${errorPrefix}-telegram` : undefined}
          onChange={(value) => { onFieldChange?.(['telegram']); setDraft((current) => ({ ...current, telegram: value })); }}
          onRemove={() => { onFieldChange?.(['telegram']); setDraft((current) => ({ ...current, telegram: '' })); }}
        />
      )}
      {telegramError && <p id={`${errorPrefix}-telegram`} className="text-[13px] text-[#B42318]">Telegram: {telegramError}</p>}
      {links.map((link, index) => (
        <ContactInputRow
          key={`link-${index}`}
          icon={LinkIcon}
          value={link}
          placeholder="https://..."
          label={`Ссылка ${index + 1}`}
          errorId={linkError ? `${errorPrefix}-links` : undefined}
          onChange={(value) => updateList('links', links.map((item, itemIndex) => (itemIndex === index ? value : item)))}
          onRemove={() => updateList('links', links.filter((_, itemIndex) => itemIndex !== index))}
        />
      ))}
      {linkError && <p id={`${errorPrefix}-links`} className="text-[13px] text-[#B42318]">Ссылки: {linkError}</p>}

      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((current) => !current)}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-[9px] border border-dashed border-[#D8D6CF] text-[13px] font-semibold text-[#9A9CA3] hover:border-[#C9C6BE] hover:text-[#6B7280]"
        >
          <PlusIcon className="h-4 w-4" />
          Добавить контакт
          <ChevronDownIcon className="h-4 w-4" />
        </button>
        {menuOpen && (
          <div className="absolute left-0 top-[calc(100%+6px)] z-20 w-full overflow-hidden rounded-[10px] border border-[#EEF0F2] bg-white p-1 shadow-[0_12px_32px_rgba(21,22,24,0.16)]">
            {CONTACT_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.type}
                  type="button"
                  onClick={() => addContact(option.type)}
                  className="flex w-full items-center gap-3 rounded-[8px] px-3 py-2 text-left text-[14px] text-[#23252D] hover:bg-[#F5F4EF]"
                >
                  <Icon className="h-4 w-4 text-[#6B7280]" />
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const CandidateResultCard = ({
  item,
  onOpen,
  onChangeStatus,
  compact = false,
  rates,
}: {
  item: AtsCandidateListItem;
  onOpen: () => void;
  onChangeStatus?: () => void;
  compact?: boolean;
  rates: CurrencyRates | null;
}) => {
  const skills = (item.skills || []).slice(0, compact ? 0 : 4);
  const extraSkills = Math.max(0, (item.skills || []).length - skills.length);
  const statusControl = onChangeStatus ? (
    <button
      type="button"
      title="Нажмите, чтобы изменить"
      aria-label={`Изменить статус: ${getStatusLabel(item.status)}`}
      onClick={(event) => {
        event.stopPropagation();
        onChangeStatus();
      }}
      className={`group relative ${compact ? '' : ''}`}
    >
      {compact ? (
        <span className="rounded-[6px] px-1.5 py-0.5 text-[#525866] underline decoration-dotted underline-offset-2 hover:bg-[#F5F4EF] hover:text-[#23252D]">
          {getStatusLabel(item.status)}
        </span>
      ) : (
        <Pill tone={getStatusTone(item.status)}>{getStatusLabel(item.status)}</Pill>
      )}
      <span className="pointer-events-none absolute left-1/2 top-full z-10 mt-1 hidden -translate-x-1/2 whitespace-nowrap rounded-[6px] bg-[#23252D] px-2 py-1 text-[11px] text-white group-hover:block">
        Нажмите, чтобы изменить
      </span>
    </button>
  ) : compact ? (
    <span className="text-[#868C98]">{getStatusLabel(item.status)}</span>
  ) : (
    <Pill tone={getStatusTone(item.status)}>{getStatusLabel(item.status)}</Pill>
  );

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`w-full rounded-[14px] bg-white text-left shadow-[0_8px_24px_rgba(21,22,24,0.08)] transition hover:shadow-[0_10px_28px_rgba(21,22,24,0.12)] ${
        compact ? 'p-3' : 'p-6'
      }`}
    >
      <div className={`flex gap-4 ${compact ? 'items-start' : 'flex-col items-start lg:flex-row lg:items-center'}`}>
        <CandidatePhoto
          candidateId={item.candidate_id}
          hasPhoto={candidateHasPhoto(item)}
          initials={getInitials(item.full_name)}
          className={`${compact ? 'h-9 w-9 text-[14px]' : 'h-14 w-14 text-[18px]'} flex shrink-0 items-center justify-center rounded-full bg-[#ECEBE6] font-medium text-[#868C98]`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className={`${compact ? 'text-[13px]' : 'text-[16px]'} truncate font-semibold text-[#23252D]`}>
              <HighlightedText text={getCandidateDisplayName(item.full_name)} />
            </div>
            {!compact && item.red_flags?.length > 0 && <Pill tone="red">Red Flag</Pill>}
            {!compact && item.is_blacklisted && <Pill tone="red">В ЧС</Pill>}
            {!compact && statusControl}
          </div>

          {compact ? (
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px]">
              {statusControl}
              {item.is_blacklisted && (
                <span className="inline-flex items-center gap-1 font-semibold text-[#E5484D]">
                  <NoSymbolIcon className="h-3 w-3" />
                  В ЧС
                </span>
              )}
            </div>
          ) : (
            <>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-[13px] text-[#868C98]">
                <span className="inline-flex items-center gap-1"><BriefcaseIcon className="h-4 w-4" /><HighlightedText text={item.desired_position || 'Должность не указана'} /></span>
                <span className="inline-flex items-center gap-1"><MapPinIcon className="h-4 w-4" /><HighlightedText text={formatCandidateLocation(item) || 'Локация не указана'} /></span>
                <span className="inline-flex items-center gap-1"><UserIcon className="h-4 w-4" />{item.updated_at ? `Обновлено ${formatDate(item.updated_at)}` : 'Нет даты'}</span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {skills.map((skill, index) => <Pill key={`${asText(skill)}-${index}`}><HighlightedText text={asText(skill)} /></Pill>)}
                {extraSkills > 0 && <span className="text-[13px] text-[#868C98]">+{extraSkills}</span>}
              </div>

              <div className="mt-3 text-[13px] text-[#868C98]">
                Источник: <HighlightedText text={item.source || 'Не указан'} /> · <HighlightedText text={item.desired_position || 'Без специализации'} />
              </div>
            </>
          )}
        </div>
        <div className={`${compact ? 'text-[12px]' : 'self-start text-[18px] lg:self-auto'} shrink-0 font-semibold text-[#23252D]`}>
          <SalaryExpectation value={(item as unknown as Record<string, unknown>).salary_expectation} rates={rates} compact={compact} />
        </div>
      </div>
    </button>
  );
};

const EmptyState = ({ text }: { text: string }) => (
  <div className="rounded-[14px] border border-dashed border-[#D8D6CF] bg-white/60 p-8 text-center text-[14px] text-[#868C98]">
    {text}
  </div>
);

const AtsPage: React.FC = () => {
  const {
    items,
    selectedId,
    setSelectedId,
    selectedCard,
    setSelectedCard,
    query,
    setQuery,
    status,
    setStatus,
    setSearchFilters,
    loading,
    loadingMore,
    hasMore,
    total,
    error,
    reload,
    loadMore,
    refreshSelectedCard,
    searchAllFolders,
    setSearchAllFolders,
  } = useAtsCandidates();
  const { user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedFolder = useAtsNavigationStore((state) => state.selectedFolder);
  const setSelectedFolder = useAtsNavigationStore((state) => state.setSelectedFolder);
  const notifyCandidatesUpdated = useAtsNavigationStore((state) => state.notifyCandidatesUpdated);
  const refreshVersion = useAtsNavigationStore((state) => state.refreshVersion);
  const folderSearchResetVersion = useAtsNavigationStore((state) => state.folderSearchResetVersion);
  const [activeTab, setActiveTab] = useState<AtsTab>('profile');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<AtsCandidateCard>>({});
  const [comment, setComment] = useState('');
  const [labelPopoverOpen, setLabelPopoverOpen] = useState(false);
  const [labelType, setLabelType] = useState<'label' | 'red_flag'>('label');
  const [labelColor, setLabelColor] = useState('#7757FF');
  const [labelName, setLabelName] = useState('');
  const [editingLabel, setEditingLabel] = useState<{ id: string; type: 'label' | 'red_flag'; item: Record<string, unknown> } | null>(null);
  const [customKey, setCustomKey] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [customValueType, setCustomValueType] = useState<CustomFieldValueType>('text');
  const [editingCustomKey, setEditingCustomKey] = useState<string | null>(null);
  const [customFieldError, setCustomFieldError] = useState<string | null>(null);
  const [stage, setStage] = useState('Техническое интервью');
  const [processStatus, setProcessStatus] = useState('approved');
  const [rejectionReason, setRejectionReason] = useState('no_contact');
  const [processComment, setProcessComment] = useState('');
  const [rejectionModalMode, setRejectionModalMode] = useState<'rejected' | 'candidate_refused' | null>(null);
  const [candidateRefusalStandalone, setCandidateRefusalStandalone] = useState(false);
  const [offerDeclineAsStage, setOfferDeclineAsStage] = useState(false);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [offerModalMode, setOfferModalMode] = useState<'sent' | 'accepted'>('sent');
  const [hireModalOpen, setHireModalOpen] = useState(false);
  const [statusPickerOpen, setStatusPickerOpen] = useState(false);
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [statusChangeSource, setStatusChangeSource] = useState<'badge' | 'history'>('history');
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importJob, setImportJob] = useState<AtsImportJob | null>(null);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [deleteCandidateOpen, setDeleteCandidateOpen] = useState(false);
  const [blacklistReason, setBlacklistReason] = useState('');
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [folderMoveOpen, setFolderMoveOpen] = useState(false);
  const [recruiterOpen, setRecruiterOpen] = useState(false);
  const [selectedRecruiterId, setSelectedRecruiterId] = useState('');
  const [filters, setFilters] = useState<AtsFilterState>(DEFAULT_FILTERS);
  const [candidateSort, setCandidateSort] = useState<AtsCandidateSort>('updated');
  const [candidateSortOpen, setCandidateSortOpen] = useState(false);
  const [savedFolderPaths, setSavedFolderPaths] = useState<string[]>([]);
  const [folderCounts, setFolderCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (folderSearchResetVersion === 0) return;
    setQuery('');
    setStatus('');
    setSearchFilters({});
    setSearchAllFolders(false);
    setFilters(DEFAULT_FILTERS);
  }, [folderSearchResetVersion, setQuery, setSearchAllFolders, setSearchFilters, setStatus]);

  useEffect(() => {
    const openFolderImport = (event: Event) => {
      const folderPath = (event as CustomEvent<{ folderPath?: string }>).detail?.folderPath;
      if (!folderPath) return;
      setSelectedFolder(folderPath);
      setImportOpen(true);
    };
    window.addEventListener('ats:import-into-folder', openFolderImport);
    return () => window.removeEventListener('ats:import-into-folder', openFolderImport);
  }, [setSelectedFolder]);

  useEffect(() => {
    let active = true;

    void atsApi.listFolders()
      .then((catalog) => {
        if (active) {
          setSavedFolderPaths(catalog.paths);
          setFolderCounts(catalog.counts);
        }
      })
      .catch(() => {
        if (active) {
          setSavedFolderPaths([]);
          setFolderCounts({});
        }
      });

    return () => {
      active = false;
    };
  }, [refreshVersion]);
  const [currencyRates, setCurrencyRates] = useState<CurrencyRates | null>(null);
  const [companyUsers, setCompanyUsers] = useState<unknown[]>([]);
  const [recruitersLoading, setRecruitersLoading] = useState(false);
  const [recruitersError, setRecruitersError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cardFieldErrors, setCardFieldErrors] = useState<AtsFieldValidationError[]>([]);

  useEffect(() => { setCardFieldErrors([]); }, [editing, selectedId]);
  const candidateSortRef = useRef<HTMLDivElement | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const pendingEditRef = useRef(false);
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const listScrollTopRef = useRef(0);
  const detailScrollRef = useRef<HTMLElement | null>(null);

  const card = selectedCard;
  useLayoutEffect(() => {
    if (detailOpen) {
      if (card?.candidate_id === selectedId && detailScrollRef.current) {
        detailScrollRef.current.scrollTop = 0;
      }
    } else if (listScrollRef.current) {
      listScrollRef.current.scrollTop = listScrollTopRef.current;
    }
  }, [detailOpen, card?.candidate_id, selectedId]);

  const editable = card ? ({ ...card, ...draft } as AtsCandidateCard) : null;
  const author = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || undefined;
  const folders = useMemo(
    () => buildFolderTreeFromPaths(savedFolderPaths, folderCounts),
    [folderCounts, savedFolderPaths],
  );
  const effectiveFolder = selectedFolder || ALL_COUNTRIES_FOLDER;
  const cardFolder = useMemo(() => {
    if (!card) return effectiveFolder;
    return getCandidateFolderPaths(card, items)[0] || effectiveFolder;
  }, [card, effectiveFolder, items]);
  const processVacancy = processVacancyTitleFromFolder(cardFolder);
  const visibleItems = useMemo(
    () => sortCandidates(items, candidateSort),
    [candidateSort, items],
  );
  const candidateSortLabel = CANDIDATE_SORT_OPTIONS.find((option) => option.value === candidateSort)?.label || 'по дате обновления';

  const selectedCandidateTitle = useMemo(
    () => getCandidateDisplayName(card?.full_name),
    [card?.full_name],
  );
  const blacklistInfo = useMemo(() => getLatestBlacklistInfo(card?.history), [card?.history]);
  const responsibleRecruiter = useMemo(() => getResponsibleRecruiter(card), [card]);
  const recruiterOptions = useMemo(
    () => buildRecruiterOptions(companyUsers),
    [companyUsers],
  );
  const activeFilterChips = useMemo(
    () => getActiveFilterChips(filters, status, recruiterOptions),
    [filters, status, recruiterOptions],
  );
  const highlightTerms = useMemo(
    () => extractSearchHighlightTerms(
      query,
      filters.anyWord,
      filters.position,
      filters.donorCompanies,
      filters.source,
      filters.location,
      filters.country,
      filters.city,
      ...filters.skillGroups.flatMap((group) => group.skills),
    ),
    [
      filters.anyWord,
      filters.city,
      filters.country,
      filters.donorCompanies,
      filters.location,
      filters.position,
      filters.skillGroups,
      filters.source,
      query,
    ],
  );

  const handleClearFilterChip = (chipId: string) => {
    const { filters: nextFilters, status: nextStatus } = clearFilterChip(chipId, filters, status);
    setFilters(nextFilters);
    setStatus(nextStatus);
    setSearchFilters(buildSearchFilters(nextFilters));
  };

  useEffect(() => {
    if (location.pathname !== '/ats') return;
    const { candidateId, folderPath, edit } = parseAtsCandidateDeepLink(location.search);
    if (folderPath && !isAllCountriesFolderName(folderPath) && folderPath !== 'Все резюме') {
      setSelectedFolder(folderPath);
    }
    if (candidateId) {
      pendingEditRef.current = edit;
      setSelectedId(candidateId);
      setDetailOpen(true);
    }
    if (location.search) {
      navigate('/ats', { replace: true });
    }
  }, [location.pathname, location.search, navigate, setSelectedFolder, setSelectedId]);

  useEffect(() => {
    if (!pendingEditRef.current || !card) return;
    if (selectedId && card.candidate_id !== selectedId) return;
    pendingEditRef.current = false;
    const experienceItems = normalizeExperienceItems(card.experience?.length ? card.experience : card.work_history);
    const educationItems = normalizeEducationItems(
      card.education_details?.length ? card.education_details : card.education,
    );
    setDraft({
      experience: experienceItems,
      work_history: experienceItems,
      education_details: educationItems,
      education: educationItems.map(educationSummary),
    });
    setEditing(true);
  }, [card, selectedId]);

  useEffect(() => {
    let cancelled = false;
    const companyId = typeof user?.companyId === 'number' ? user.companyId : null;

    const loadRecruiters = async () => {
      setRecruitersLoading(true);
      setRecruitersError(null);

      if (!companyId) {
        setCompanyUsers([]);
        setRecruitersError('Не удалось определить компанию текущего пользователя');
        setRecruitersLoading(false);
        return;
      }

      try {
        const users = await apiService.getAssignableCompanyUsers(companyId);

        if (!cancelled) {
          setCompanyUsers(users);
        }
      } catch (error) {
        if (!cancelled) {
          setCompanyUsers([]);
          setRecruitersError(error instanceof Error ? error.message : 'Не удалось загрузить сотрудников компании');
        }
      } finally {
        if (!cancelled) {
          setRecruitersLoading(false);
        }
      }
    };

    void loadRecruiters();

    return () => {
      cancelled = true;
    };
  }, [user?.companyId]);

  useEffect(() => {
    let cancelled = false;

    const loadRates = async () => {
      try {
        const data = await atsApi.getCurrencyRates();
        const nextRates: CurrencyRates = { RUB: 1 };

        Object.entries(data.rates).forEach(([code, rate]) => {
          if (Number.isFinite(rate) && rate > 0) {
            nextRates[code.toUpperCase()] = rate;
          }
        });

        if (!cancelled) {
          setCurrencyRates(nextRates);
        }
      } catch {
        if (!cancelled) {
          setCurrencyRates(null);
        }
      }
    };

    void loadRates();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!candidateSortOpen) return undefined;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!candidateSortRef.current?.contains(event.target as Node)) {
        setCandidateSortOpen(false);
      }
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCandidateSortOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [candidateSortOpen]);

  const saveCard = async () => {
    if (!card) return;
    if (draft.full_name !== undefined && !String(draft.full_name).trim()) {
      setActionError('Укажите ФИО кандидата.');
      return;
    }
    if (draft.date_of_birth && (candidateAge(draft.date_of_birth) ?? 0) < 0) {
      setActionError('Дата рождения не может быть в будущем.');
      return;
    }
    setBusy(true);
    setActionError(null);
    setCardFieldErrors([]);
    try {
      const payload = cleanCandidateContactDraft(draft);
      const updated = await atsApi.updateCandidateCard(card.candidate_id, payload);
      setSelectedCard(updated);
      notifyCandidatesUpdated();
      setDraft({});
      setEditing(false);
    } catch (err) {
      if (err instanceof AtsCardValidationError) setCardFieldErrors(err.fieldErrors);
      await handleCardMutationError(err);
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file: File) => {
    if (!card) return;
    if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setActionError('Выберите фото PNG или JPEG размером до 5 МБ.');
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const updated = await atsApi.replaceCandidatePhoto(card.candidate_id, file);
      setSelectedCard(updated);
      invalidateCandidatePhoto(card.candidate_id);
      notifyCandidatesUpdated();
    } catch (err) {
      await handleCardMutationError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleCardMutationError = async (err: unknown) => {
    if (getRequestStatus(err) === 404) {
      setActionError('Карточка была удалена или переимпортирована. Список кандидатов обновлен.');
      setRecruiterOpen(false);
      setBlacklistOpen(false);
      setDeleteCandidateOpen(false);
      setLabelPopoverOpen(false);
      setAttachmentOpen(false);
      setEditing(false);
      setSelectedId(null);
      await reload();
      notifyCandidatesUpdated();
      return;
    }
    setActionError(err instanceof Error ? err.message : 'Не удалось сохранить изменения карточки');
  };

  const mutateCard = async (operation: () => Promise<AtsCandidateCard>) => {
    setBusy(true);
    setActionError(null);
    try {
      setSelectedCard(await operation());
      notifyCandidatesUpdated();
    } catch (err) {
      await handleCardMutationError(err);
    } finally {
      setBusy(false);
    }
  };

  const openOfferFlow = (mode: 'sent' | 'accepted' = 'sent') => {
    setOfferModalMode(mode);
    setOfferModalOpen(true);
  };

  const applyCardStatus = (nextStatus: string) => {
    setStatusPickerOpen(false);
    if (!card || nextStatus === card.status) return;
    if (nextStatus === 'inactive') {
      setInactiveModalOpen(true);
      return;
    }
    if (nextStatus === 'rejected' || nextStatus === 'candidate_refused') {
      setStatusChangeSource('badge');
      setCandidateRefusalStandalone(nextStatus === 'candidate_refused');
      setOfferDeclineAsStage(false);
      setRejectionModalMode(nextStatus);
      return;
    }
    if (nextStatus === 'offer') {
      setStatusChangeSource('badge');
      openOfferFlow('sent');
      return;
    }
    if (nextStatus === 'hired') {
      setStatusChangeSource('badge');
      setHireModalOpen(true);
      return;
    }
    void mutateCard(() => atsApi.changeCandidateStatus(card.candidate_id, { status: nextStatus }));
  };

  const addCustomField = async () => {
    if (!card || !customKey.trim()) return;
    const nextKey = customKey.trim();
    if (isReservedCustomFieldKey(nextKey) && nextKey !== editingCustomKey) {
      setCustomFieldError('Нельзя создать кастомное поле, дублирующее системное');
      return;
    }
    setCustomFieldError(null);
    const nextFields = {
      ...(card.custom_fields || {}),
      [nextKey]: parseCustomFieldValue(customValue, customValueType),
    };
    if (editingCustomKey && editingCustomKey !== nextKey) {
      delete nextFields[editingCustomKey];
    }
    await mutateCard(() => atsApi.updateCandidateCard(card.candidate_id, {
      custom_fields: nextFields,
    }));
    setCustomKey('');
    setCustomValue('');
    setCustomValueType('text');
    setEditingCustomKey(null);
    setCustomFieldError(null);
  };

  const editCustomField = (key: string, value: unknown) => {
    setEditingCustomKey(key);
    setCustomKey(key);
    setCustomValue(customFieldInputValue(value));
    setCustomValueType(inferCustomFieldType(value));
    setCustomFieldError(null);
  };

  const resetCustomFieldForm = () => {
    setEditingCustomKey(null);
    setCustomKey('');
    setCustomValue('');
    setCustomValueType('text');
    setCustomFieldError(null);
  };

  const removeCustomField = async (key: string) => {
    if (!card) return;
    const next = { ...(card.custom_fields || {}) };
    delete next[key];
    await mutateCard(() => atsApi.updateCandidateCard(card.candidate_id, { custom_fields: next }));
  };

  const moveCardToFolder = async (folderPath: string) => {
    if (!card || !folderPath.trim()) return;
    await mutateCard(() => atsApi.moveCandidateFolder(card.candidate_id, folderPath));
    setSelectedFolder(folderPath);
    setFolderMoveOpen(false);
  };

  const resetLabelForm = () => {
    setLabelName('');
    setLabelColor('#7757FF');
    setLabelType('label');
    setEditingLabel(null);
    setLabelPopoverOpen(false);
  };

  const editCandidateLabel = (item: unknown, type: 'label' | 'red_flag') => {
    const id = getItemId(item);
    if (!id) return;
    const record = typeof item === 'object' && item !== null ? item as Record<string, unknown> : { id, title: getLabelText(item) };
    setEditingLabel({ id, type, item: record });
    setLabelType(type);
    setLabelName(getLabelText(item));
    setLabelColor(getLabelColor(item, type === 'red_flag' ? '#E5484D' : '#7757FF'));
    setLabelPopoverOpen(true);
  };

  const addCandidateLabel = async () => {
    if (!card || !labelName.trim()) return;
    const item = {
      ...(editingLabel?.item || {}),
      id: editingLabel?.id || generateLocalId(),
      title: labelName.trim(),
      color: labelColor,
      type: labelType,
      ...(editingLabel ? {} : { author, created_at: new Date().toISOString() }),
    };

    if (editingLabel?.type === 'red_flag') {
      await mutateCard(() => atsApi.updateRedFlag(card.candidate_id, editingLabel.id, item));
    } else if (editingLabel?.type === 'label') {
      await mutateCard(() => atsApi.updateLabel(card.candidate_id, editingLabel.id, item));
    } else if (labelType === 'red_flag') {
      await mutateCard(() => atsApi.addRedFlag(card.candidate_id, item));
    } else {
      await mutateCard(() => atsApi.addLabel(card.candidate_id, item));
    }
    resetLabelForm();
  };

  const removeCandidateLabel = async (item: unknown, type: 'label' | 'red_flag') => {
    if (!card) return;
    const itemId = getItemId(item);
    if (!itemId) return;

    await mutateCard(() => (
      type === 'red_flag'
        ? atsApi.deleteRedFlag(card.candidate_id, itemId)
        : atsApi.deleteLabel(card.candidate_id, itemId)
    ));
  };

  const addToBlacklist = async () => {
    if (!card || !blacklistReason.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      await atsApi.updateCandidateCard(card.candidate_id, { is_blacklisted: true });
      const updated = await atsApi.addHistory(card.candidate_id, 'blacklist_added', {
        reason: blacklistReason.trim(),
        author,
        date: new Date().toISOString(),
      });
      setSelectedCard(updated);
      notifyCandidatesUpdated();
      setBlacklistReason('');
      setBlacklistOpen(false);
    } catch (err) {
      await handleCardMutationError(err);
    } finally {
      setBusy(false);
    }
  };

  const removeFromBlacklist = async () => {
    if (!card) return;
    setBusy(true);
    setActionError(null);
    try {
      await atsApi.updateCandidateCard(card.candidate_id, { is_blacklisted: false });
      setSelectedCard(await atsApi.addHistory(card.candidate_id, 'blacklist_removed', {
        author,
        date: new Date().toISOString(),
      }));
      notifyCandidatesUpdated();
    } catch (err) {
      await handleCardMutationError(err);
    } finally {
      setBusy(false);
    }
  };

  const deleteCandidate = async () => {
    if (!card) return;
    setBusy(true);
    setActionError(null);
    try {
      await atsApi.deleteCandidateCard(card.candidate_id);
      setDeleteCandidateOpen(false);
      setSelectedId(null);
      setDetailOpen(false);
      await reload();
      notifyCandidatesUpdated();
    } catch (err) {
      await handleCardMutationError(err);
    } finally {
      setBusy(false);
    }
  };

  const openRecruiterModal = () => {
    const currentRecruiterIsInCompany = recruiterOptions.some((option) => option.id === responsibleRecruiter?.id);
    setSelectedRecruiterId(
      currentRecruiterIsInCompany ? responsibleRecruiter?.id || '' : recruiterOptions[0]?.id || '',
    );
    setRecruiterOpen(true);
  };

  const changeResponsibleRecruiter = async () => {
    if (!card || !selectedRecruiterId) return;
    const selectedRecruiter = recruiterOptions.find((item) => item.id === selectedRecruiterId);
    if (!selectedRecruiter) return;

    setBusy(true);
    setActionError(null);
    try {
      await atsApi.updateCandidateCard(card.candidate_id, {
        custom_fields: {
          ...(card.custom_fields || {}),
          responsible_recruiter: selectedRecruiter,
          responsible_recruiter_id: selectedRecruiter.id,
          responsible_recruiter_name: selectedRecruiter.name,
          responsible_recruiter_email: selectedRecruiter.email,
        },
      });
      setSelectedCard(await atsApi.addHistory(card.candidate_id, 'responsible_recruiter_changed', {
        from: responsibleRecruiter ? {
          id: responsibleRecruiter.id,
          name: responsibleRecruiter.name,
          email: responsibleRecruiter.email,
        } : null,
        to: {
          id: selectedRecruiter.id,
          name: selectedRecruiter.name,
          email: selectedRecruiter.email,
        },
        author,
        date: new Date().toISOString(),
      }));
      notifyCandidatesUpdated();
      setRecruiterOpen(false);
    } catch (err) {
      await handleCardMutationError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleAttachmentUpload = async (file: File, type: 'resume' | 'attachment') => {
    if (!card) return;
    setBusy(true);
    setActionError(null);
    try {
      const metadata = {
        source: 'ats_card',
        tags: type,
        folder_path: cardFolder || undefined,
      };
      if (type === 'resume') {
        await atsApi.parseResume(file, metadata, card.candidate_id);
      } else {
        await atsApi.uploadAttachment(card.candidate_id, file, metadata);
      }
      await refreshSelectedCard();
      notifyCandidatesUpdated();
      setAttachmentOpen(false);
    } catch (err) {
      await handleCardMutationError(err);
    } finally {
      setBusy(false);
    }
  };

  const openCandidate = (candidateId: string, options?: { edit?: boolean }) => {
    if (!detailOpen) listScrollTopRef.current = listScrollRef.current?.scrollTop || 0;
    pendingEditRef.current = Boolean(options?.edit);
    setSelectedId(candidateId);
    setDetailOpen(true);
  };

  const openStatusFromList = async (candidateId: string) => {
    setBusy(true);
    setActionError(null);
    try {
      setSelectedId(candidateId);
      const loaded = await atsApi.getCandidateCard(candidateId);
      setSelectedCard(loaded);
      setStatusChangeSource('badge');
      setStatusPickerOpen(true);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Не удалось открыть смену статуса');
    } finally {
      setBusy(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setEditing(false);
    setDraft({});
  };

  const renderImportModal = () => (
    importOpen && (
      <ImportModal
        onClose={() => setImportOpen(false)}
        importJob={importJob}
        onImportJob={setImportJob}
        onImported={(folderPath) => {
          if (folderPath && folderPath !== 'Все резюме') setSelectedFolder(folderPath);
          void reload();
          notifyCandidatesUpdated();
        }}
        onOpenCandidate={(candidateId, options) => {
          openCandidate(candidateId, options);
          setImportOpen(false);
        }}
        onOpenDuplicates={() => {
          setImportOpen(false);
          setDuplicatesOpen(true);
        }}
        onFoldersChanged={async () => {
          const catalog = await atsApi.listFolders();
          setSavedFolderPaths(catalog.paths);
          setFolderCounts(catalog.counts);
          notifyCandidatesUpdated();
        }}
        folder={effectiveFolder}
        folders={folders}
      />
    )
  );

  const renderDuplicatesModal = () => (
    duplicatesOpen && (
      <DuplicatesModal
        onClose={() => setDuplicatesOpen(false)}
        onMerged={() => {
          void reload();
          notifyCandidatesUpdated();
        }}
      />
    )
  );

  const renderAttachmentModal = () => (
    attachmentOpen && card && (
      <AttachmentModal
        disabled={busy}
        onClose={() => setAttachmentOpen(false)}
        onSubmit={(file, type) => void handleAttachmentUpload(file, type)}
      />
    )
  );

  const renderFolderMoveModal = () => (
    folderMoveOpen && card && (
      <FolderMoveModal
        folders={folders}
        currentFolder={cardFolder}
        disabled={busy}
        onClose={() => setFolderMoveOpen(false)}
        onSubmit={(folderPath) => void moveCardToFolder(folderPath)}
      />
    )
  );

  const renderStatusFlowModals = () => (
    <>
      {rejectionModalMode && card && (
        <RejectionReasonModal
          mode={rejectionModalMode}
          initialReason={rejectionReason}
          disabled={busy}
          onClose={() => {
            setCandidateRefusalStandalone(false);
            setOfferDeclineAsStage(false);
            setRejectionModalMode(null);
          }}
          onSubmit={(reason, reasonComment) => {
            if (!card) return;
            setRejectionReason(reason);
            const payload = {
              vacancy: processVacancy,
              ...(rejectionModalMode === 'rejected'
                ? { rejection_reason: reason }
                : { candidate_refusal_reason: reason }),
              comment: reasonComment.trim(),
            };
            const request = (() => {
              if (offerDeclineAsStage) {
                return () => atsApi.addStage(card.candidate_id, {
                  ...payload,
                  stage: 'Оффер',
                  status: 'declined',
                  author,
                });
              }
              if (statusChangeSource === 'badge') {
                return () => atsApi.changeCandidateStatus(card.candidate_id, {
                  ...payload,
                  status: rejectionModalMode,
                });
              }
              if (candidateRefusalStandalone) {
                return () => atsApi.addCandidateRefusal(card.candidate_id, payload);
              }
              return () => atsApi.addStage(card.candidate_id, {
                ...payload,
                stage,
                status: rejectionModalMode,
                author,
              });
            })();
            void mutateCard(request).then(() => {
              setProcessComment('');
              setCandidateRefusalStandalone(false);
              setOfferDeclineAsStage(false);
              setRejectionModalMode(null);
            });
          }}
        />
      )}
      {offerModalOpen && card && (
        <OfferModal
          mode={offerModalMode}
          candidateName={selectedCandidateTitle || 'Кандидат'}
          vacancy={processVacancy}
          disabled={busy}
          onClose={() => setOfferModalOpen(false)}
          onSubmit={(payload) => {
            if (!card) return;
            void mutateCard(() => atsApi.addStage(card.candidate_id, {
              ...payload,
              vacancy: processVacancy,
              stage: 'Оффер',
              status: payload.outcome,
              author,
            })).then(() => setOfferModalOpen(false));
          }}
        />
      )}
      {hireModalOpen && card && (
        <HireModal
          candidateName={selectedCandidateTitle || 'Кандидат'}
          vacancy={processVacancy}
          disabled={busy}
          onClose={() => setHireModalOpen(false)}
          onSubmit={(payload) => {
            if (!card) return;
            void mutateCard(() => atsApi.addStage(card.candidate_id, {
              ...payload,
              vacancy: processVacancy,
              stage: 'Найм',
              status: 'hired',
              author,
            })).then(() => setHireModalOpen(false));
          }}
        />
      )}
      {statusPickerOpen && card && (
        <StatusPickerModal
          currentStatus={card.status}
          disabled={busy}
          onClose={() => setStatusPickerOpen(false)}
          onSelect={applyCardStatus}
        />
      )}
      {inactiveModalOpen && card && (
        <InactiveReasonModal
          disabled={busy}
          onClose={() => setInactiveModalOpen(false)}
          onSubmit={(reasonComment) => {
            void mutateCard(() => atsApi.changeCandidateStatus(card.candidate_id, {
              status: 'inactive',
              comment: reasonComment.trim(),
            })).then(() => setInactiveModalOpen(false));
          }}
        />
      )}
    </>
  );

  if (detailOpen && card && editable) {
    return (
      <SearchHighlightProvider terms={highlightTerms}>
      <div className="h-full overflow-hidden bg-[#F5F4EF]" data-testid="ats-detail-workspace">
        <div className="flex h-full min-h-0 flex-col xl:flex-row">
          <aside data-testid="ats-candidate-pane" className="max-h-[40vh] w-full shrink-0 overflow-y-auto overscroll-contain bg-[#F5F4EF] px-4 py-4 sm:px-6 xl:h-full xl:max-h-none xl:w-[280px] xl:py-5">
            <button
              type="button"
              onClick={closeDetail}
              className="mb-6 inline-flex items-center gap-2 rounded-[8px] bg-[#E9E8E2] px-3 py-2 text-[13px] text-[#23252D]"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Назад к списку кандидатов
            </button>

            <h2 className="text-[20px] font-semibold text-[#23252D]">{effectiveFolder || 'Кандидаты'}</h2>
            <div className="mt-1 text-[13px] text-[#868C98]">Кандидатов: {total}</div>

            <div className="mt-4 flex gap-2">
              <AtsTextInput
                value={query}
                onChange={setQuery}
                placeholder="Поиск по ФИО..."
                className="flex-1"
                inputClassName="text-[13px]"
              />
              <button
                type="button"
                title="Фильтры поиска"
                aria-label="Фильтры поиска"
                onClick={() => setFiltersOpen(true)}
                className="flex h-10 items-center justify-center gap-2 rounded-[8px] border border-[#DDDCD6] bg-white px-3"
              >
                <FunnelIcon className="h-4 w-4" />
                <span className="text-[13px] font-semibold text-[#23252D]">Фильтры</span>
              </button>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
              {(visibleItems.length ? visibleItems : items).map((item) => (
                <CandidateResultCard
                  key={item.candidate_id}
                  item={item}
                  compact
                  rates={currencyRates}
                  onOpen={() => openCandidate(item.candidate_id)}
                  onChangeStatus={() => void openStatusFromList(item.candidate_id)}
                />
              ))}
            </div>
          </aside>

          <main ref={detailScrollRef} data-testid="ats-resume-pane" className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6 xl:px-8">
            <section className="rounded-[14px] bg-white p-5 shadow-[0_8px_24px_rgba(21,22,24,0.08)] sm:p-6">
              <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[112px_minmax(0,1fr)] lg:items-start">
                <div className="flex flex-col items-center gap-2">
                  <CandidatePhoto
                    candidateId={card.candidate_id}
                    hasPhoto={candidateHasPhoto(card)}
                    initials={getInitials(card.full_name)}
                    className="flex h-[92px] w-[92px] shrink-0 items-center justify-center rounded-full bg-[#ECEBE6] text-[38px] font-semibold text-[#9CA3AF] sm:h-[112px] sm:w-[112px] sm:text-[48px]"
                  />
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    aria-label="Файл фото кандидата"
                    className="sr-only"
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0];
                      event.currentTarget.value = '';
                      if (file) void uploadPhoto(file);
                    }}
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => photoInputRef.current?.click()}
                    className="inline-flex items-center gap-1 text-center text-[12px] font-semibold text-[#2F7BFF] disabled:opacity-50"
                  >
                    <CameraIcon className="h-4 w-4" />
                    {candidateHasPhoto(card) ? 'Заменить фото' : 'Добавить фото'}
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <SalaryExpectation value={card.salary_expectation} rates={currencyRates} />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => (card.is_blacklisted ? void removeFromBlacklist() : setBlacklistOpen(true))}
                        className={`h-9 rounded-[8px] border px-3 text-[12px] font-semibold ${
                          card.is_blacklisted
                            ? 'border-[#F3A4A6] bg-[#FFE2E2] text-[#E5484D]'
                            : 'border-[#DDDCD6] text-[#23252D]'
                        }`}
                      >
                        {card.is_blacklisted ? 'Убрать из ЧС' : 'Внести в ЧС'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (card?.salary_expectation) {
                            const parts = splitSalaryExpectation(card.salary_expectation);
                            if (parts.amount && !parseSalaryExpectation(card.salary_expectation)) {
                              setDraft((current) => ({
                                ...current,
                                salary_expectation: composeSalaryExpectation(parts.amount, 'RUB'),
                              }));
                            }
                          }
                          setDraft((current) => ({
                            ...current,
                            experience: normalizeExperienceItems(card.experience?.length ? card.experience : card.work_history),
                            work_history: normalizeExperienceItems(card.experience?.length ? card.experience : card.work_history),
                            education_details: normalizeEducationItems(
                              card.education_details?.length ? card.education_details : card.education,
                            ),
                            education: normalizeEducationItems(
                              card.education_details?.length ? card.education_details : card.education,
                            ).map(educationSummary),
                          }));
                          setEditing(true);
                        }}
                        className="h-9 rounded-[8px] border border-[#DDDCD6] px-3 text-[12px] font-semibold text-[#23252D]"
                      >
                        Редактировать
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteCandidateOpen(true)}
                        className="h-9 rounded-[8px] border border-[#F3A4A6] px-3 text-[12px] font-semibold text-[#E5484D] hover:bg-[#FFF1F1]"
                      >
                        Удалить
                      </button>
                    </div>
                  </div>

                  <div className="rounded-[10px] bg-[#F5F4EF] px-4 py-3">
                    {editing ? (
                      <AtsTextInput
                        value={String(editable.full_name || '')}
                        ariaLabel="ФИО кандидата"
                        onChange={(value) => setDraft((current) => ({ ...current, full_name: value }))}
                        className="mb-3 w-full"
                        inputClassName="text-[20px] font-semibold"
                      />
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-[20px] font-bold text-[#23252D]">
                          <HighlightedText text={selectedCandidateTitle || 'Без имени'} />
                        </h1>
                        {card.is_blacklisted && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#FFE2E2] px-2.5 py-1 text-[12px] font-semibold text-[#E5484D]">
                            <NoSymbolIcon className="h-3.5 w-3.5" />
                            В ЧС
                          </span>
                        )}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-3 text-[13px] text-[#525866]">
                      {editing ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{asText(editable?.gender || card.gender)},</span>
                          <label className="flex items-center gap-1">
                            <span>Дата рождения</span>
                            <input
                              type="date"
                              aria-label="Дата рождения"
                              max={todayDateInput()}
                              value={editable.date_of_birth || ''}
                              onChange={(event) => setDraft((current) => ({
                                ...current,
                                date_of_birth: event.target.value || null,
                                age: null,
                              }))}
                              className="h-9 rounded-[8px] border border-[#CDD0D5] bg-white px-2 text-[13px] text-[#23252D]"
                            />
                          </label>
                          {editable.date_of_birth ? (
                            <span>{candidateAge(editable.date_of_birth)} лет</span>
                          ) : (
                            <label className="flex items-center gap-1">
                              <AtsTextInput
                                type="number"
                                value={String(editable.age ?? '')}
                                onChange={(value) => setDraft((current) => ({
                                  ...current,
                                  age: value.trim() ? Number(value) : null,
                                }))}
                                className="w-[88px]"
                                inputClassName="text-[13px]"
                                placeholder="возраст"
                              />
                              <span>лет</span>
                            </label>
                          )}
                        </div>
                      ) : (
                        <span>
                          {asText(card.gender)}, {candidateAge(card.date_of_birth, card.age) !== null
                            ? `${candidateAge(card.date_of_birth, card.age)} лет`
                            : 'возраст не указан'}, {formatBirthDate(card.date_of_birth)}
                        </span>
                      )}
                      <span className="hidden sm:inline">·</span>
                      <span><HighlightedText text={formatCandidateLocation(card) || 'Локация не указана'} /></span>
                      <span className="hidden sm:inline">·</span>
                      <span title="Каждый календарный месяц работы учитывается один раз. Периоды без точных дат не включены в стаж.">{formatCandidateExperienceSummary(card)}</span>
                    </div>
                    {editing ? (
                      <label className="mt-2 flex flex-col gap-1 text-[13px] text-[#525866]">
                        Специальность / желаемая должность
                        <AtsTextInput
                          value={editable.desired_position || ''}
                          onChange={(value) => setDraft((current) => ({ ...current, desired_position: value }))}
                          placeholder="Укажите специальность"
                          className="w-full"
                        />
                      </label>
                    ) : (card.desired_position || '').trim() ? (
                      <div className="mt-2 text-[15px] font-semibold text-[#23252D]">
                        <HighlightedText text={card.desired_position || ''} />
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      title="Нажмите, чтобы изменить"
                      onClick={() => setStatusPickerOpen(true)}
                      className="group relative"
                    >
                      <Pill tone={getStatusTone(card.status)}>{getStatusLabel(card.status)}</Pill>
                      <span className="pointer-events-none absolute left-1/2 top-full z-10 mt-1 hidden -translate-x-1/2 whitespace-nowrap rounded-[6px] bg-[#23252D] px-2 py-1 text-[11px] text-white group-hover:block">
                        Нажмите, чтобы изменить
                      </span>
                    </button>
                    {card.red_flags.map((flag, index) => (
                      <CandidateLabelPill
                        key={`${getLabelText(flag)}-${index}`}
                        item={flag}
                        fallbackColor="#E5484D"
                        onEdit={() => editCandidateLabel(flag, 'red_flag')}
                        onDelete={() => void removeCandidateLabel(flag, 'red_flag')}
                      />
                    ))}
                    {card.labels.map((item, index) => (
                      <CandidateLabelPill
                        key={`${getLabelText(item)}-${index}`}
                        item={item}
                        fallbackColor="#7757FF"
                        onEdit={() => editCandidateLabel(item, 'label')}
                        onDelete={() => void removeCandidateLabel(item, 'label')}
                      />
                    ))}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setLabelPopoverOpen((current) => !current)}
                        className="rounded-[999px] border border-dashed border-[#7757FF] px-3 py-1 text-[13px] leading-none text-[#7757FF]"
                      >
                        + Метка
                      </button>
                      {labelPopoverOpen && (
                        <LabelPopover
                          type={labelType}
                          setType={setLabelType}
                          color={labelColor}
                          setColor={setLabelColor}
                          name={labelName}
                          setName={setLabelName}
                          onCancel={resetLabelForm}
                          onSubmit={() => void addCandidateLabel()}
                          disabled={busy}
                          editing={!!editingLabel}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {card.red_flags.length > 0 && (
                <div className="mt-5 border-t border-[#EEF0F2] pt-4">
                  <div className="flex items-start justify-between gap-4 rounded-[10px] bg-[#FFE6E6] px-4 py-3 text-[13px] text-[#E5484D]">
                    <div>
                      <FlagIcon className="mr-2 inline h-4 w-4" />
                      <span className="font-semibold">Red Flag:</span> {card.red_flags.map(getLabelText).filter(Boolean).join(', ')}
                    </div>
                    <button
                      type="button"
                      onClick={() => void removeCandidateLabel(card.red_flags[0], 'red_flag')}
                      className="shrink-0 rounded-[8px] border border-[#FFB3B3] bg-white/60 px-3 py-1 text-[12px] text-[#E5484D]"
                    >
                      Снять метку
                    </button>
                  </div>
                </div>
              )}

              {card.is_blacklisted && (
                <div className="mt-4 rounded-[10px] bg-[#FFE2E2] px-4 py-3 text-[13px] text-[#E5484D]">
                  <div className="flex items-start gap-3">
                    <NoSymbolIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-semibold">В чёрном списке</div>
                      <div className="mt-1 leading-5">
                        {blacklistInfo?.reason || 'Причина не указана'}
                      </div>
                      {(blacklistInfo?.author || blacklistInfo?.date) && (
                        <div className="mt-2 text-[12px] text-[#C7373B]">
                          {[blacklistInfo?.author, blacklistInfo?.date].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {editing && (
                <div className="mt-4 flex flex-col items-end gap-2">
                  {actionError && (
                    <div role="alert" className="w-full whitespace-pre-line rounded-[8px] bg-[#FFF2F0] px-3 py-2 text-[13px] text-[#B42318]">
                      {actionError}
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setDraft({}); setEditing(false); setActionError(null); }} className="h-[38px] rounded-[8px] border border-[#CDD0D5] px-3 text-[14px]">Отмена</button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void saveCard()}
                      className="h-[38px] rounded-[8px] bg-[#E16349] px-3 text-[14px] text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
                    >
                      Сохранить
                    </button>
                  </div>
                </div>
              )}
            </section>

            <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[280px_minmax(0,1fr)]">
              <div className="space-y-4">
                <Section title="Контакты">
                  {editing && editable ? (
                    <EditableContacts card={editable} setDraft={setDraft} fieldErrors={cardFieldErrors}
                      onFieldChange={(fields) => {
                        const remaining = cardFieldErrors.filter(error => !fields.includes(error.field));
                        setCardFieldErrors(remaining);
                        setActionError(remaining.length ? formatAtsValidationErrors(remaining) : null);
                      }} />
                  ) : (
                    <>
                      <ContactFieldRow label="Телефон" value={normalizePhoneList(card.phones, card.phone)} multiple />
                      <ContactFieldRow label="Telegram" value={card.telegram} />
                      <ContactFieldRow label="Email" value={normalizeContactList(card.emails, card.email)} multiple />
                      <LinkFieldRow value={card.links} />
                    </>
                  )}
                </Section>

                <Section title="Информация">
                  <div className="mb-3 rounded-[8px] bg-[#F8F7F3] px-3 py-3">
                    <div className="text-[11px] uppercase tracking-[0.05em] text-[#868C98]">Ответственный рекрутер</div>
                    <div className="mt-2 flex items-center gap-2">
                      {responsibleRecruiter ? (
                        <>
                          <span
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-[#525866]"
                            style={{ backgroundColor: responsibleRecruiter.color }}
                          >
                            {getInitials(responsibleRecruiter.name)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] font-semibold text-[#23252D]">{responsibleRecruiter.name}</span>
                            {responsibleRecruiter.email && (
                              <span className="block truncate text-[12px] text-[#868C98]">{responsibleRecruiter.email}</span>
                            )}
                          </span>
                        </>
                      ) : (
                        <span className="flex-1 text-[13px] text-[#868C98]">Не назначен</span>
                      )}
                      <button
                        type="button"
                        onClick={openRecruiterModal}
                        className="shrink-0 text-[12px] font-semibold text-[#2F7BFF] hover:underline"
                      >
                        Изменить
                      </button>
                    </div>
                  </div>
                  {editing && editable ? (
                    <div className="grid gap-2 border-b border-[#EEF0F2] py-2 sm:grid-cols-[110px_minmax(0,1fr)] sm:items-center">
                      <span className="text-[13px] text-[#868C98]">Источник</span>
                      <AtsTextInput value={editable.source || ''} onChange={(value) => setDraft((current) => ({ ...current, source: value }))} placeholder="Например, HH" />
                    </div>
                  ) : <FieldRow label="Источник" value={card.source} />}
                  {editing && editable ? (
                    (() => {
                      const salaryParts = splitSalaryExpectation(editable.salary_expectation);
                      return (
                        <>
                          <div className="grid gap-2 border-b border-[#EEF0F2] py-2 sm:grid-cols-[110px_minmax(0,1fr)] sm:items-center">
                            <span className="text-[13px] text-[#868C98]">Зарплатные ожидания</span>
                            <AtsTextInput
                              value={salaryParts.amount}
                              onChange={(value) => setDraft((current) => ({
                                ...current,
                                salary_expectation: composeSalaryExpectation(value, salaryParts.currency),
                              }))}
                              placeholder="Например, 250 000"
                            />
                          </div>
                          <div className="grid gap-2 border-b border-[#EEF0F2] py-2 sm:grid-cols-[110px_minmax(0,1fr)] sm:items-center">
                            <span className="text-[13px] text-[#868C98]">Валюта</span>
                            <select
                              value={salaryParts.currency}
                              onChange={(event) => setDraft((current) => ({
                                ...current,
                                salary_expectation: composeSalaryExpectation(
                                  salaryParts.amount,
                                  (event.target.value as SalaryCurrency) || 'RUB',
                                ),
                              }))}
                              className="h-[36px] w-full max-w-[140px] rounded-[10px] border border-[#CDD0D5] bg-white px-3 text-[14px] text-[#23252D] outline-none focus:border-[#2F7BFF]"
                            >
                              {SALARY_CURRENCY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      );
                    })()
                  ) : <FieldRow label="Зарплатные ожидания" value={card.salary_expectation} />}
                  <div className="border-b border-[#EEF0F2] py-2 last:border-b-0">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-[13px] text-[#868C98]">Папка</span>
                      <button
                        type="button"
                        onClick={() => setFolderMoveOpen(true)}
                        className="text-[12px] font-semibold text-[#2F7BFF] hover:underline"
                      >
                        Переместить
                      </button>
                    </div>
                    <div className="mt-1 break-words text-[14px] text-[#1F2A44]">
                      <HighlightedText text={cardFolder} />
                    </div>
                  </div>
                  {editing && editable ? (
                    <div className="space-y-2 border-b border-[#EEF0F2] py-2">
                      <span className="text-[13px] text-[#868C98]">Локация</span>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <AtsTextInput value={editable.location_country || ''} onChange={(value) => setDraft((current) => ({ ...current, location_country: value }))} placeholder="Страна" />
                        <AtsTextInput value={editable.location_city || ''} onChange={(value) => setDraft((current) => ({ ...current, location_city: value }))} placeholder="Город" />
                      </div>
                    </div>
                  ) : <FieldRow label="Локация" value={formatCandidateLocation(card)} />}
                  <FieldRow label="Дата создания" value={formatDate(card.created_at)} />
                  <FieldRow label="Дата обновления" value={formatDate(card.updated_at)} />
                </Section>

                <Section title="Кастомные поля">
                  <div className="space-y-2 mb-3">
                    {getVisibleCustomFields(card.custom_fields || {}).map(([key, value]) => (
                      <div key={key} className="rounded-[8px] bg-[#F5F4EF] px-3 py-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-[11px] uppercase tracking-[0.05em] text-[#868C98]">{key}</div>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => editCustomField(key, value)}
                            className="mr-1 text-[12px] font-semibold text-[#525866] hover:text-[#23252D] disabled:opacity-50"
                          >
                            Изменить
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void removeCustomField(key)}
                            className="text-[#D92D20] disabled:opacity-50"
                            aria-label={`Удалить поле ${key}`}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-1 text-[13px] font-semibold text-[#23252D]">
                          <HighlightedText text={asText(value)} />
                        </div>
                      </div>
                    ))}
                    {getVisibleCustomFields(card.custom_fields || {}).length === 0 && <div className="text-[13px] text-[#868C98]">Нет кастомных полей</div>}
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <AtsTextInput
                      value={customKey}
                      onChange={(value) => {
                        setCustomKey(value);
                        setCustomFieldError(null);
                      }}
                      placeholder="Поле"
                      inputClassName="text-[13px]"
                    />
                    <UniversalDropdown
                      value={customValueType}
                      onChange={(value) => setCustomValueType(value as CustomFieldValueType)}
                      options={[
                        { value: 'text', label: 'Текст' },
                        { value: 'number', label: 'Число' },
                        { value: 'date', label: 'Дата' },
                        { value: 'list', label: 'Список через запятую' },
                      ]}
                    />
                    <AtsTextInput value={customValue} onChange={setCustomValue} placeholder="Значение" inputClassName="text-[13px]" />
                    {customFieldError && (
                      <div className="text-[12px] text-[#D92D20]">{customFieldError}</div>
                    )}
                    <div className="flex gap-2">
                      <button type="button" disabled={busy || !customKey.trim()} onClick={() => void addCustomField()} className="h-[36px] flex-1 rounded-[8px] border border-dashed border-[#CDD0D5] text-[13px] text-[#868C98] disabled:opacity-50">
                        {editingCustomKey ? 'Сохранить поле' : '+ Добавить поле'}
                      </button>
                      {editingCustomKey && (
                        <button type="button" onClick={resetCustomFieldForm} className="h-[36px] rounded-[8px] border border-[#DDDCD6] px-3 text-[13px] text-[#77736B]">
                          Отмена
                        </button>
                      )}
                    </div>
                  </div>
                </Section>

                <Section title="Вложения">
                  {(() => {
                    const allDocuments = [...(card.documents || []), ...(card.attachments || [])];
                    const resumes = allDocuments.filter((doc) => getDocumentType(doc) === 'resume');
                    const attachments = allDocuments.filter((doc) => {
                      const type = getDocumentType(doc);
                      return type !== 'resume' && type !== 'photo';
                    });
                    const renderDocuments = (itemsToRender: unknown[]) => itemsToRender.map((doc, index) => (
                      <DocumentItem
                        key={`${getItemId(doc)}-${index}`}
                        doc={doc}
                        onDelete={(id) => void mutateCard(() => atsApi.deleteAttachment(card.candidate_id, id))}
                      />
                    ));

                    return (
                      <div className="mb-3 space-y-4">
                        {resumes.length > 0 && (
                          <div>
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-[#868C98]">Резюме</div>
                            <div className="space-y-2">{renderDocuments(resumes)}</div>
                          </div>
                        )}
                        {attachments.length > 0 && (
                          <div>
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-[#868C98]">Обычные вложения</div>
                            <div className="space-y-2">{renderDocuments(attachments)}</div>
                          </div>
                        )}
                        {allDocuments.length === 0 && <div className="text-[13px] text-[#868C98]">Нет вложений</div>}
                      </div>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={() => setAttachmentOpen(true)}
                    className="inline-flex h-[36px] items-center gap-2 rounded-[8px] border border-dashed border-[#CDD0D5] px-3 text-[13px] text-[#868C98] hover:border-[#C9C6BE] hover:text-[#525866]"
                  >
                    <PaperClipIcon className="h-4 w-4" />
                    Загрузить файл
                  </button>
                </Section>
              </div>

              <section className="min-w-0">
                <ContentTabToggle options={TABS} activeTab={activeTab} onTabChange={setActiveTab} className="mb-4" />
                {activeTab === 'profile' && (
                  <ProfileTab
                    card={editing && editable ? editable : card}
                    editing={editing}
                    onChangeExperience={(items) => setDraft((current) => ({
                      ...current,
                      experience: items,
                      work_history: items,
                    }))}
                    onChangeEducation={(items) => setDraft((current) => ({
                      ...current,
                      education_details: items,
                      education: items.map(educationSummary),
                    }))}
                  />
                )}
                {activeTab === 'history' && (
                  <HistoryTab
                    card={card}
                    vacancyTitle={processVacancy}
                    stage={stage}
                    setStage={setStage}
                    processStatus={processStatus}
                    setProcessStatus={setProcessStatus}
                    processComment={processComment}
                    setProcessComment={setProcessComment}
                    onOpenRejection={() => {
                      setStatusChangeSource('history');
                      setOfferDeclineAsStage(false);
                      setCandidateRefusalStandalone(true);
                      setRejectionModalMode('candidate_refused');
                    }}
                    onOpenOffer={() => {
                      setStatusChangeSource('history');
                      openOfferFlow('sent');
                    }}
                    onOpenHire={() => {
                      setStatusChangeSource('history');
                      setHireModalOpen(true);
                    }}
                    onAdd={() => {
                      const normalizedStage = stage.trim();
                      if (!normalizedStage) return;
                      if (normalizedStage === 'Оффер') {
                        setStatusChangeSource('history');
                        if (processStatus === 'security_rejected') {
                          void mutateCard(() => atsApi.addStage(card.candidate_id, {
                            stage: normalizedStage,
                            status: processStatus,
                            comment: processComment.trim(),
                            vacancy: processVacancy,
                            author,
                          })).then(() => setProcessComment(''));
                          return;
                        }
                        if (processStatus === 'declined') {
                          setOfferDeclineAsStage(true);
                          setCandidateRefusalStandalone(false);
                          setRejectionModalMode('candidate_refused');
                          return;
                        }
                        openOfferFlow(processStatus === 'accepted' ? 'accepted' : 'sent');
                        return;
                      }
                      if (normalizedStage === 'Найм') {
                        setStatusChangeSource('history');
                        setHireModalOpen(true);
                        return;
                      }
                      if (processStatus === 'rejected' || processStatus === 'candidate_refused') {
                        setStatusChangeSource('history');
                        setOfferDeclineAsStage(false);
                        setCandidateRefusalStandalone(false);
                        setRejectionModalMode(processStatus);
                        return;
                      }
                      void mutateCard(() => atsApi.addStage(card.candidate_id, {
                        stage: normalizedStage,
                        status: processStatus,
                        comment: processComment.trim(),
                        vacancy: processVacancy,
                        author,
                      })).then(() => setProcessComment(''));
                    }}
                  />
                )}
                {activeTab === 'notes' && (
                  <NotesTab
                    card={card}
                    comment={comment}
                    setComment={setComment}
                    onAdd={() => {
                      if (!comment.trim()) return;
                      void mutateCard(() => atsApi.addComment(card.candidate_id, comment.trim(), author)).then(() => setComment(''));
                    }}
                    onEdit={(id, text) => void mutateCard(() => atsApi.updateComment(card.candidate_id, id, text))}
                    onDelete={(id) => void mutateCard(() => atsApi.deleteComment(card.candidate_id, id))}
                  />
                )}
              </section>
            </div>
          </main>
        </div>

        {filtersOpen && (
          <FiltersDrawer
            status={status}
            setStatus={setStatus}
            filters={filters}
            setFilters={setFilters}
            folders={folders}
            recruiters={recruiterOptions}
            recruitersLoading={recruitersLoading}
            recruitersError={recruitersError}
            onApply={(nextFilters) => {
              setSearchFilters(buildSearchFilters(nextFilters));
              setFiltersOpen(false);
            }}
            onReset={() => {
              setStatus('');
              setQuery('');
              setFilters(DEFAULT_FILTERS);
              setSearchFilters({});
            }}
            onClose={() => setFiltersOpen(false)}
          />
        )}
        {renderImportModal()}
        {renderDuplicatesModal()}
        {renderAttachmentModal()}
        {renderStatusFlowModals()}
        {blacklistOpen && (
          <BlacklistModal
            reason={blacklistReason}
            setReason={setBlacklistReason}
            disabled={busy}
            onClose={() => {
              setBlacklistOpen(false);
              setBlacklistReason('');
            }}
            onSubmit={() => void addToBlacklist()}
          />
        )}
        {deleteCandidateOpen && (
          <DeleteCandidateModal
            candidateName={selectedCandidateTitle || 'этого кандидата'}
            disabled={busy}
            onClose={() => setDeleteCandidateOpen(false)}
            onSubmit={() => void deleteCandidate()}
          />
        )}
        {recruiterOpen && (
          <RecruiterModal
            recruiters={recruiterOptions}
            selectedId={selectedRecruiterId}
            currentId={responsibleRecruiter?.id}
            setSelectedId={setSelectedRecruiterId}
            disabled={busy}
            loading={recruitersLoading}
            error={recruitersError}
            onClose={() => setRecruiterOpen(false)}
            onSubmit={() => void changeResponsibleRecruiter()}
          />
        )}
        {renderFolderMoveModal()}
      </div>
      </SearchHighlightProvider>
    );
  }

  return (
    <SearchHighlightProvider terms={highlightTerms}>
    <div ref={listScrollRef} data-testid="ats-results-workspace" className="h-full overflow-y-auto bg-[#F5F4EF]">
      <div className="min-h-full">
        <main className="min-w-0 flex-1 px-6 py-7 xl:px-10">
          <div className="mb-6 flex min-w-0 flex-col gap-4 2xl:flex-row 2xl:items-start 2xl:justify-between 2xl:gap-6">
            <AtsWorkspaceHeading
              title={query ? 'Результаты поиска' : effectiveFolder || 'Кандидаты'}
              candidatesCount={total}
              searchSummary={query
                ? `Найдено: ${total}${searchAllFolders ? ` в ${folders.length} папках` : ''} по запросу «${query}»`
                : undefined}
            />

            <div
              data-testid="ats-search-toolbar"
              className="flex min-w-0 w-full flex-1 flex-col gap-3 2xl:items-end"
            >
              <div
                data-testid="ats-search-toolbar-controls"
                className="flex w-full min-w-0 flex-wrap items-start gap-3"
              >
                <AtsSearchInput value={query} onChange={setQuery} />
              <div ref={candidateSortRef} className="relative min-w-[210px] flex-1 basis-[210px] sm:max-w-[260px]">
                <button
                  type="button"
                  onClick={() => setCandidateSortOpen((current) => !current)}
                  className="flex h-11 w-full items-center justify-between gap-3 rounded-[10px] border border-[#DDDCD6] bg-white px-3 text-left text-[14px] font-medium text-[#23252D]"
                >
                  <span className="whitespace-nowrap">{candidateSortLabel}</span>
                  <ChevronDownIcon className={`h-4 w-4 shrink-0 text-[#868C98] transition-transform ${candidateSortOpen ? 'rotate-180' : ''}`} />
                </button>
                {candidateSortOpen && (
                  <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-20 overflow-hidden rounded-[10px] border border-[#EEF0F2] bg-white p-1 shadow-[0_12px_32px_rgba(21,22,24,0.16)]">
                    {CANDIDATE_SORT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setCandidateSort(option.value);
                          setCandidateSortOpen(false);
                        }}
                        className={`flex h-9 w-full items-center rounded-[8px] px-3 text-left text-[13px] ${
                          candidateSort === option.value
                            ? 'bg-[#F5F4EF] font-semibold text-[#23252D]'
                            : 'text-[#525866] hover:bg-[#F8F7F3]'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                title="Фильтры поиска"
                aria-label="Фильтры поиска"
                onClick={() => setFiltersOpen(true)}
                className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-[10px] border border-[#DDDCD6] bg-white px-3"
              >
                <FunnelIcon className="h-5 w-5 text-[#23252D]" />
                <span className="text-[14px] font-semibold text-[#23252D]">Фильтры</span>
              </button>
              <button
                type="button"
                onClick={() => setDuplicatesOpen(true)}
                className="flex h-11 shrink-0 items-center justify-center rounded-[10px] border border-[#DDDCD6] bg-white px-4 text-[14px] font-semibold text-[#23252D] hover:bg-[#F5F4EF]"
              >
                Дубли
              </button>
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-[10px] bg-[#E16349] px-4 text-[14px] font-semibold text-white"
              >
                <DocumentArrowUpIcon className="h-5 w-5" />
                Импорт
              </button>
              </div>
              <AtsSearchOperatorsHint className="self-start" />
              <CustomCheckbox
                checked={searchAllFolders}
                onChange={setSearchAllFolders}
                label="По всем папкам"
                className="self-start text-[13px] xl:self-start"
              />
              {activeFilterChips.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {activeFilterChips.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      onClick={() => handleClearFilterChip(chip.id)}
                      className="inline-flex items-center gap-1 rounded-[8px] border border-[#DDDCD6] bg-white px-2.5 py-1 text-[12px] text-[#525866] hover:bg-[#F5F4EF]"
                      title="Снять фильтр"
                    >
                      {chip.label}
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {(error || actionError) && (
            <div className="mb-4 rounded-[10px] bg-[#FFF2F0] p-3 text-[14px] text-[#D92D20]">
              {actionError || error}
            </div>
          )}
          {loading && visibleItems.length === 0 && <EmptyState text="Загрузка кандидатов..." />}
          {!loading && visibleItems.length === 0 && (
            <EmptyState text={query.trim() || activeFilterChips.length > 0
              ? 'Кандидаты по запросу не найдены'
              : 'В этой папке пока нет кандидатов'} />
          )}

          <div className="w-full space-y-5">
            {visibleItems.map((item) => (
              <CandidateResultCard
                key={item.candidate_id}
                item={item}
                rates={currencyRates}
                onOpen={() => openCandidate(item.candidate_id)}
                onChangeStatus={() => void openStatusFromList(item.candidate_id)}
              />
            ))}
          </div>
          {hasMore && !loading && (
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="mt-5 rounded-[10px] border border-[#D8D6CF] bg-white px-5 py-3 text-[14px] font-semibold text-[#525866] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loadingMore ? 'Загрузка...' : 'Показать еще'}
            </button>
          )}
        </main>
      </div>

      {filtersOpen && (
        <FiltersDrawer
          status={status}
          setStatus={setStatus}
          filters={filters}
          setFilters={setFilters}
          folders={folders}
          recruiters={recruiterOptions}
          recruitersLoading={recruitersLoading}
          recruitersError={recruitersError}
          onApply={(nextFilters) => {
            setSearchFilters(buildSearchFilters(nextFilters));
            setFiltersOpen(false);
          }}
          onReset={() => {
            setStatus('');
            setQuery('');
            setFilters(DEFAULT_FILTERS);
            setSearchFilters({});
          }}
          onClose={() => setFiltersOpen(false)}
        />
      )}
      {renderImportModal()}
      {renderDuplicatesModal()}
      {renderAttachmentModal()}
      {renderStatusFlowModals()}
    </div>
    </SearchHighlightProvider>
  );
};

const FiltersDrawer = ({
  status,
  setStatus,
  filters,
  setFilters,
  folders,
  recruiters,
  recruitersLoading,
  recruitersError,
  onApply,
  onReset,
  onClose,
}: {
  status: string;
  setStatus: (value: string) => void;
  filters: AtsFilterState;
  setFilters: React.Dispatch<React.SetStateAction<AtsFilterState>>;
  folders: AtsFolderNode[];
  recruiters: RecruiterOption[];
  recruitersLoading: boolean;
  recruitersError: string | null;
  onApply: (filters: AtsFilterState) => void;
  onReset: () => void;
  onClose: () => void;
}) => {
  const updateFilter = <Key extends keyof AtsFilterState>(key: Key, value: AtsFilterState[Key]) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const addSkillGroup = () => {
    setFilters((current) => ({
      ...current,
      skillGroups: [...current.skillGroups, { id: generateLocalId(), operator: 'and', skills: [], input: '' }],
    }));
  };

  const updateSkillGroup = (
    id: string,
    update: (group: AtsFilterState['skillGroups'][number]) => AtsFilterState['skillGroups'][number],
  ) => {
    setFilters((current) => ({
      ...current,
      skillGroups: current.skillGroups.map((group) => (group.id === id ? update(group) : group)),
    }));
  };

  const addSkill = (id: string) => {
    const group = filters.skillGroups.find((item) => item.id === id);
    const skill = group?.input.trim();
    if (!group || !skill || group.skills.some((item) => item.toLowerCase() === skill.toLowerCase())) return;
    updateSkillGroup(id, (current) => ({ ...current, input: '', skills: [...current.skills, skill] }));
  };

  const removeSkill = (id: string, skill: string) => {
    updateSkillGroup(id, (current) => ({
      ...current,
      skills: current.skills.filter((item) => item !== skill),
    }));
  };

  const removeSkillGroup = (id: string) => {
    setFilters((current) => ({
      ...current,
      skillGroups: current.skillGroups.filter((group) => group.id !== id),
    }));
  };

  const vacancyOptions = useMemo(
    () => flattenFolderOptions(folders).filter((folder) => folder.isLeaf),
    [folders],
  );
  const recruiterOptions = useMemo(() => {
    const query = filters.recruiter.trim().toLocaleLowerCase('ru');
    if (!query) return recruiters;
    return recruiters.filter((recruiter) => (
      [recruiter.name, recruiter.email].some((value) => value.toLocaleLowerCase('ru').includes(query))
    ));
  }, [filters.recruiter, recruiters]);

  const selectedRecruiters = useMemo(
    () => recruiters.filter((recruiter) => filters.recruiterIds.includes(recruiter.id)),
    [filters.recruiterIds, recruiters],
  );

  const toggleRecruiter = (recruiterId: string) => {
    setFilters((current) => ({
      ...current,
      recruiter: '',
      recruiterIds: current.recruiterIds.includes(recruiterId)
        ? current.recruiterIds.filter((id) => id !== recruiterId)
        : [...current.recruiterIds, recruiterId],
    }));
  };

  const toggleVacancyPath = (path: string, checked: boolean) => {
    setFilters((current) => ({
      ...current,
      vacancyPaths: checked
        ? [...current.vacancyPaths, path]
        : current.vacancyPaths.filter((item) => item !== path),
    }));
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/30">
      <aside className="ml-auto flex h-full w-full max-w-[380px] flex-col bg-white shadow-[-8px_0_24px_rgba(21,22,24,0.12)]">
        <div className="flex items-center justify-between border-b border-[#EEF0F2] px-5 py-5">
          <h2 className="text-[18px] font-semibold text-[#23252D]">Фильтры</h2>
          <button type="button" onClick={onReset} className="text-[13px] text-[#2F7BFF]">
            Сбросить
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Источник кандидата</label>
            <AtsTextInput
              value={filters.source}
              onChange={(value) => updateFilter('source', value)}
              placeholder="Например, hh, referral, manual"
            />
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Ответственный рекрутер</label>
            <AtsTextInput
              value={filters.recruiter}
              onChange={(value) => setFilters((current) => ({ ...current, recruiter: value, recruiterIds: [] }))}
              placeholder="Выберите рекрутеров или введите ФИО"
            />
            {selectedRecruiters.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selectedRecruiters.map((recruiter) => (
                  <button
                    key={recruiter.id}
                    type="button"
                    onClick={() => toggleRecruiter(recruiter.id)}
                    className="inline-flex items-center gap-1 rounded-full bg-[#F5F4EF] px-2 py-1 text-[12px] font-medium text-[#525866] hover:bg-[#ECEBE6]"
                    aria-label={`Убрать рекрутера ${recruiter.name}`}
                  >
                    {recruiter.name}
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2 max-h-40 overflow-y-auto rounded-[9px] border border-[#DDDCD6] p-1">
              {recruitersLoading && <div className="px-2 py-1 text-[13px] text-[#868C98]">Загрузка сотрудников…</div>}
              {!recruitersLoading && recruitersError && <div className="px-2 py-1 text-[13px] text-[#B05A00]">{recruitersError}</div>}
              {!recruitersLoading && !recruitersError && recruiterOptions.length === 0 && (
                <div className="px-2 py-1 text-[13px] text-[#868C98]">Сотрудники не найдены</div>
              )}
              {!recruitersLoading && recruiterOptions.map((recruiter) => (
                <button
                  key={recruiter.id}
                  type="button"
                  onClick={() => toggleRecruiter(recruiter.id)}
                  aria-pressed={filters.recruiterIds.includes(recruiter.id)}
                  className={`flex w-full items-center gap-2 rounded-[7px] px-2 py-2 text-left ${
                    filters.recruiterIds.includes(recruiter.id)
                      ? 'bg-[#F5F4EF]'
                      : 'hover:bg-[#F8F7F3]'
                  }`}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-[#525866]" style={{ backgroundColor: recruiter.color }}>
                    {getInitials(recruiter.name)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-medium text-[#23252D]">{recruiter.name}</span>
                    {recruiter.email && <span className="block truncate text-[11px] text-[#868C98]">{recruiter.email}</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Название вакансии</label>
            <AtsTextInput
              value={filters.vacancy}
              onChange={(value) => updateFilter('vacancy', value)}
              placeholder="Введите название вакансии"
            />
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Вакансии из папок</label>
            {filters.vacancyPaths.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {filters.vacancyPaths.map((path) => (
                  <button
                    key={path}
                    type="button"
                    onClick={() => toggleVacancyPath(path, false)}
                    className="inline-flex max-w-full items-center gap-1 rounded-[8px] bg-[#F1F0EC] px-2 py-1 text-left text-[12px] text-[#525866]"
                    title="Снять выбор вакансии"
                  >
                    <span className="truncate">{path}</span>
                    <XMarkIcon className="h-3.5 w-3.5 shrink-0" />
                  </button>
                ))}
              </div>
            )}
            <div className="max-h-48 space-y-1 overflow-y-auto rounded-[9px] border border-[#DDDCD6] p-2">
              {vacancyOptions.length === 0 && (
                <div className="px-2 py-1 text-[13px] text-[#A0A4AE]">Папки с вакансиями ещё не созданы</div>
              )}
              {vacancyOptions.map((folder) => (
                <CustomCheckbox
                  key={folder.value}
                  checked={filters.vacancyPaths.includes(folder.value)}
                  onChange={(checked) => toggleVacancyPath(folder.value, checked)}
                  label={folder.value}
                  className="rounded-[6px] px-2 py-1 hover:bg-[#F8F9FB]"
                />
              ))}
            </div>
            {filters.vacancyPaths.length > 0 && <div className="mt-2 text-[12px] text-[#868C98]">Выбрано вакансий: {filters.vacancyPaths.length}</div>}
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Должность</label>
            <TabToggle
              options={[
                { id: 'exact', label: 'Точная фраза' },
                { id: 'partial', label: 'Частичное' },
              ]}
              activeTab={filters.positionMode}
              onTabChange={(value) => updateFilter('positionMode', value as AtsFilterState['positionMode'])}
              radius="rounded-[8px]"
            />
            <AtsTextInput
              className="mt-3"
              value={filters.position}
              placeholder="Введите должность"
              onChange={(value) => updateFilter('position', value)}
            />
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Опыт / стаж (лет)</label>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <AtsTextInput
                value={filters.experienceFrom}
                placeholder="от"
                type="number"
                onChange={(value) => updateFilter('experienceFrom', value)}
              />
              <span className="text-[#C0BEB7]">—</span>
              <AtsTextInput
                value={filters.experienceTo}
                placeholder="до"
                type="number"
                onChange={(value) => updateFilter('experienceTo', value)}
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Образование</label>
            <div className="space-y-2">
              {EDUCATION_LEVEL_OPTIONS.map((option) => (
                <CustomCheckbox
                  key={option.value}
                  checked={filters.educationLevels.includes(option.value)}
                  onChange={(checked) => {
                    setFilters((current) => ({
                      ...current,
                      educationLevels: checked
                        ? [...current.educationLevels, option.value]
                        : current.educationLevels.filter((level) => level !== option.value),
                    }));
                  }}
                  label={option.label}
                />
              ))}
            </div>
            <CustomCheckbox
              checked={filters.includeUnknownEducation}
              onChange={(checked) => updateFilter('includeUnknownEducation', checked)}
              label="Не указано образование"
              className="mt-3"
            />
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Возраст</label>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <AtsTextInput
                value={filters.ageFrom}
                placeholder="от"
                type="number"
                onChange={(value) => updateFilter('ageFrom', value)}
              />
              <span className="text-[#C0BEB7]">—</span>
              <AtsTextInput
                value={filters.ageTo}
                placeholder="до"
                type="number"
                onChange={(value) => updateFilter('ageTo', value)}
              />
            </div>
            <CustomCheckbox
              checked={filters.includeUnknownAge}
              onChange={(checked) => updateFilter('includeUnknownAge', checked)}
              label="Возраст не указан"
              className="mt-3"
            />
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Навыки</label>
            <div className="mb-3 rounded-[8px] bg-[#F8F7F3] px-3 py-2 text-[12px] text-[#6B7280]">
              Все группы применяются одновременно: между группами — <span className="font-semibold text-[#23252D]">И</span>.
              Внутри группы можно выбрать И или ИЛИ.
            </div>
            <AtsSearchOperatorsHint className="mb-3" />
            <div className="mt-3 space-y-3">
              {filters.skillGroups.map((group, index) => (
                <div key={group.id} className="rounded-[9px] border border-[#DDDCD6] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-[13px] font-semibold text-[#525866]">Группа {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeSkillGroup(group.id)}
                      className="text-[#D92D20]"
                      aria-label={`Удалить группу навыков ${index + 1}`}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <TabToggle
                    options={[{ id: 'and', label: 'И' }, { id: 'or', label: 'ИЛИ' }]}
                    activeTab={group.operator}
                    onTabChange={(value) => updateSkillGroup(group.id, (current) => ({ ...current, operator: value as 'and' | 'or' }))}
                    radius="rounded-[8px]"
                  />
                  <div className="mt-2 flex min-h-10 flex-wrap gap-2 rounded-[8px] bg-[#F8F9FB] px-2 py-2">
                    {group.skills.length === 0 && <span className="text-[13px] text-[#A0A4AE]">Навыки не выбраны</span>}
                    {group.skills.map((skill) => (
                      <button
                        key={skill}
                        type="button"
                        onClick={() => removeSkill(group.id, skill)}
                        className="inline-flex items-center gap-1 rounded-[8px] bg-[#F1F0EC] px-2.5 py-1 text-[13px] text-[#525866]"
                        title="Удалить навык"
                      >
                        {skill}
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                    <AtsTextInput
                      value={group.input}
                      placeholder="Добавить навык"
                      onChange={(value) => updateSkillGroup(group.id, (current) => ({ ...current, input: value }))}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addSkill(group.id);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => addSkill(group.id)}
                      disabled={!group.input.trim()}
                      className="h-10 rounded-[9px] border border-dashed border-[#DDDCD6] px-3 text-[13px] text-[#868C98] disabled:opacity-50"
                    >
                      Добавить
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addSkillGroup}
              className="mt-3 h-10 w-full rounded-[9px] border border-dashed border-[#CDD0D5] text-[13px] text-[#868C98]"
            >
              + Добавить группу навыков
            </button>
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Локация (страна / город / регион)</label>
            <AtsTextInput
              value={filters.location}
              onChange={(value) => updateFilter('location', value)}
              placeholder="Любая локация"
            />
            <div className="mt-3 grid grid-cols-2 gap-2">
              <AtsTextInput
                value={filters.country}
                onChange={(value) => updateFilter('country', value)}
                placeholder="Страна"
              />
              <AtsTextInput
                value={filters.city}
                onChange={(value) => updateFilter('city', value)}
                placeholder="Город"
              />
            </div>
            <UniversalDropdown
              className="mt-3"
              value={filters.region}
              onChange={(value) => updateFilter('region', String(value) as AtsFilterState['region'])}
              options={[
                { value: '', label: 'Любой регион' },
                { value: 'APAC', label: 'APAC' },
                { value: 'EMEA', label: 'EMEA' },
                { value: 'AMER', label: 'AMER / Americas' },
                { value: 'LATAM', label: 'LATAM' },
                { value: 'NA', label: 'NA' },
                { value: 'MENA', label: 'MENA' },
                { value: 'CEEMEA', label: 'CEEMEA' },
              ]}
            />
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Стоп-лист компаний</label>
            <AtsTextInput
              value={filters.stopCompanies}
              onChange={(value) => updateFilter('stopCompanies', value)}
              placeholder="Например, Wildberries, ООО Ромашка"
            />
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Компании-доноры</label>
            <AtsTextInput
              value={filters.donorCompanies}
              onChange={(value) => updateFilter('donorCompanies', value)}
              placeholder="Например, Ozon, Яндекс"
            />
            <TabToggle
              className="mt-3"
              options={[
                { id: 'current', label: 'Текущая компания' },
                { id: 'all', label: 'За всё время' },
              ]}
              activeTab={filters.companyMatchScope}
              onTabChange={(value) => updateFilter('companyMatchScope', value as AtsFilterState['companyMatchScope'])}
              radius="rounded-[8px]"
            />
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Статус</label>
            <UniversalDropdown value={status} onChange={(value) => setStatus(String(value))} options={STATUS_OPTIONS} />
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Метки</label>
            <AtsTextInput
              value={filters.tags}
              onChange={(value) => updateFilter('tags', value)}
              placeholder="Через запятую"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-2 block text-[13px] text-[#6B7280]">Чёрный список</label>
              <UniversalDropdown
                value={filters.blacklist}
                onChange={(value) => updateFilter('blacklist', String(value) as AtsFilterState['blacklist'])}
                options={[
                  { value: 'any', label: 'Все кандидаты' },
                  { value: 'only', label: 'Только ЧС' },
                  { value: 'exclude', label: 'Исключить ЧС' },
                ]}
              />
            </div>
            <div>
              <label className="mb-2 block text-[13px] text-[#6B7280]">Резюме</label>
              <UniversalDropdown
                value={filters.documents}
                onChange={(value) => updateFilter('documents', String(value) as AtsFilterState['documents'])}
                options={[
                  { value: 'any', label: 'Все карточки' },
                  { value: 'with', label: 'Есть резюме' },
                  { value: 'without', label: 'Нет резюме' },
                ]}
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Зарплатные ожидания</label>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <AtsTextInput
                value={filters.salaryFrom}
                placeholder="от"
                type="number"
                onChange={(value) => updateFilter('salaryFrom', value)}
              />
              <span className="text-[#C0BEB7]">—</span>
              <AtsTextInput
                value={filters.salaryTo}
                placeholder="до"
                type="number"
                onChange={(value) => updateFilter('salaryTo', value)}
              />
            </div>
            <TabToggle
              className="mt-3"
              options={[
                { id: 'RUB', label: 'RUB' },
                { id: 'EUR', label: 'EUR' },
                { id: 'USD', label: 'USD' },
              ]}
              activeTab={filters.currency}
              onTabChange={(value) => updateFilter('currency', value as AtsFilterState['currency'])}
              radius="rounded-[8px]"
            />
            <CustomCheckbox
              checked={filters.includeUnknownSalary}
              onChange={(checked) => updateFilter('includeUnknownSalary', checked)}
              label="Не указана ЗП"
              className="mt-2 rounded-[6px] px-1 py-1"
            />
          </div>

          <div>
            <label className="mb-2 block text-[13px] text-[#6B7280]">Поиск по любому слову</label>
            <AtsTextInput
              value={filters.anyWord}
              placeholder="Введите значение"
              onChange={(value) => updateFilter('anyWord', value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-[1fr_1.4fr] gap-3 border-t border-[#EEF0F2] p-5">
          <button type="button" onClick={onClose} className="h-11 rounded-[9px] bg-[#F1F0EC] text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]">
            Отмена
          </button>
          <button type="button" onClick={() => onApply(filters)} className="h-11 rounded-[9px] bg-[#E16349] text-[14px] font-semibold text-white">
            Применить
          </button>
        </div>
      </aside>
    </div>
  );
};

const ProfileTab = ({
  card,
  editing,
  onChangeExperience,
  onChangeEducation,
}: {
  card: AtsCandidateCard;
  editing: boolean;
  onChangeExperience: (items: unknown[]) => void;
  onChangeEducation: (items: unknown[]) => void;
}) => {
  const educationItems = normalizeEducationItems(
    card.education_details?.length ? card.education_details : card.education,
  );
  const experienceItems = normalizeExperienceItems(card.experience?.length ? card.experience : card.work_history);

  return (
    <section className="rounded-[14px] bg-white p-5 shadow-[0_8px_24px_rgba(21,22,24,0.08)]">
      <div>
        <h3 className="mb-3 flex items-center gap-2 text-[17px] font-bold text-[#23252D]">
          <span className="text-[14px]">⊕</span>
          Ключевые навыки
        </h3>
        <div className="flex flex-wrap gap-2">
          {(card.skills || []).map((skill) => (
            <span key={asText(skill)} className="rounded-[8px] bg-[#F1F0EC] px-3 py-1.5 text-[13px] text-[#525866]">
              <HighlightedText text={asText(skill)} />
            </span>
          ))}
          {(card.skills || []).length === 0 && <div className="text-[13px] text-[#868C98]">Не заполнено</div>}
        </div>
      </div>

      <div className="mt-7">
        <h3 className="mb-4 flex items-center gap-2 text-[17px] font-bold text-[#23252D]">
          <BriefcaseIcon className="h-5 w-5" />
          Места работы
        </h3>
        <div className="space-y-4">
          {editing
            ? experienceItems.map((item, index) => (
              <ExperienceEditor
                key={index}
                item={item}
                onChange={(next) => onChangeExperience(experienceItems.map((current, itemIndex) => (itemIndex === index ? next : current)))}
                onRemove={() => onChangeExperience(experienceItems.filter((_, itemIndex) => itemIndex !== index))}
              />
            ))
            : experienceItems.map((item, index) => <ExperienceItem key={index} item={item} />)}
          {experienceItems.length === 0 && !editing && <div className="text-[13px] text-[#868C98]">Не заполнено</div>}
          {editing && (
            <button
              type="button"
              onClick={() => onChangeExperience([...experienceItems, emptyExperienceItem()])}
              className="inline-flex h-[36px] items-center rounded-[8px] border border-dashed border-[#CDD0D5] px-3 text-[13px] text-[#868C98] hover:border-[#C9C6BE] hover:text-[#525866]"
            >
              Добавить место работы
            </button>
          )}
        </div>
      </div>

      <div className="mt-7">
        <h3 className="mb-4 flex items-center gap-2 text-[17px] font-bold text-[#23252D]">
          <span className="text-[16px]">◆</span>
          Образование
        </h3>
        <div className="space-y-3">
          {editing
            ? educationItems.map((item, index) => (
              <EducationEditor
                key={index}
                item={item}
                onChange={(next) => onChangeEducation(educationItems.map((current, itemIndex) => (itemIndex === index ? next : current)))}
                onRemove={() => onChangeEducation(educationItems.filter((_, itemIndex) => itemIndex !== index))}
              />
            ))
            : educationItems.map((item, index) => <EducationItem key={index} item={item} />)}
          {educationItems.length === 0 && !editing && <div className="text-[13px] text-[#868C98]">Не заполнено</div>}
          {editing && (
            <button
              type="button"
              onClick={() => onChangeEducation([...educationItems, emptyEducationItem()])}
              className="inline-flex h-[36px] items-center rounded-[8px] border border-dashed border-[#CDD0D5] px-3 text-[13px] text-[#868C98] hover:border-[#C9C6BE] hover:text-[#525866]"
            >
              Добавить образование
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

type ProcessStepView = {
  label: string;
  status: string;
  date: string;
};

const historyPayload = (event: unknown): Record<string, unknown> => {
  if (typeof event !== 'object' || event === null) return {};
  const payload = (event as Record<string, unknown>).payload;
  return typeof payload === 'object' && payload !== null ? payload as Record<string, unknown> : {};
};

const historyType = (event: unknown): string => {
  if (typeof event !== 'object' || event === null) return asText(event);
  return getStringValue((event as Record<string, unknown>).type) || 'Событие';
};

const historyDate = (event: unknown): string => {
  if (typeof event !== 'object' || event === null) return '';
  const record = event as Record<string, unknown>;
  const payload = historyPayload(event);
  return formatDate(getStringValue(payload.date) || getStringValue(record.at) || getStringValue(record.created_at));
};

const historyAuthor = (event: unknown, fallback?: string): string => {
  const author = getStringValue(historyPayload(event).author);
  const isPlaceholder = /^(?:автор\s*\d+|author\s*\d+|anonymous|system)$/i.test(author);
  return author && !isPlaceholder ? author : (fallback || 'Рекрутер не указан');
};

const formatHistoryDetails = (event: unknown): string => {
  const payload = historyPayload(event);
  const type = historyType(event);
  const comment = getStringValue(payload.comment) || getStringValue(payload.reason);

  if (type === 'manual_update') {
    const changes = payload.changes;
    if (typeof changes === 'object' && changes !== null) {
      const formattedChanges = Object.entries(changes as Record<string, unknown>)
        .map(([field, value]) => {
          if (typeof value !== 'object' || value === null) return `${field}: ${formatAuditValue(value)}`;
          const record = value as Record<string, unknown>;
          return `${field}: ${formatAuditValue(record.before)} -> ${formatAuditValue(record.after)}`;
        })
        .filter(Boolean);
      if (formattedChanges.length) return formattedChanges.join('; ');
    }

    const fields = Array.isArray(payload.fields)
      ? payload.fields.map(getStringValue).filter(Boolean)
      : [];
    return fields.length ? `Обновлены поля: ${fields.join(', ')}` : 'Карточка обновлена';
  }

  if (type === 'status_change') {
    const fromStatus = getStatusLabel(getStringValue(payload.from_status));
    const toStatus = getStatusLabel(getStringValue(payload.to_status) || getStringValue(payload.status));
    const reasonValue = getStringValue(payload.rejection_reason) || getStringValue(payload.candidate_refusal_reason);
    const reasonLabel = REJECTION_REASONS.find((item) => item.value === reasonValue)?.label
      || CANDIDATE_REFUSAL_REASONS.find((item) => item.value === reasonValue)?.label
      || '';
    const parts = [`${fromStatus} → ${toStatus}`];
    if (reasonLabel) parts.push(`Причина: ${reasonLabel}`);
    return parts.join('. ');
  }

  if (type === 'resume_parsed') {
    const fileName = getStringValue(payload.file_name);
    return fileName ? `Резюме «${fileName}» успешно распознано` : 'Резюме успешно распознано';
  }

  if (type === 'folder_moved') {
    const folderPath = getStringValue(payload.folder_path);
    return folderPath ? `Перемещено в папку «${folderPath}»` : 'Папка кандидата изменена';
  }

  if (type === 'responsible_recruiter_changed') {
    const recruiter = recruiterFromUnknown(payload.to);
    return recruiter ? `Назначен рекрутер: ${recruiter.name}` : 'Ответственный рекрутер изменён';
  }

  if (type === 'process_stage') {
    const statusValue = getStringValue(payload.status);
    return formatProcessStageHistoryDetails(payload as Record<string, unknown>, {
      comment: comment || undefined,
      statusLabel: statusValue ? getProcessStatusLabel(statusValue) : undefined,
    });
  }

  if (comment) return comment;

  const details = Object.entries(payload)
    .filter(([key, value]) => !['date', 'author', 'author_id', 'actor_id', 'id', 'event_id'].includes(key) && value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}: ${formatAuditValue(value)}`);
  return details.join('; ');
};

const normalizeStageTitle = (value: string): string => (
  value.toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim()
);

const getStageOrder = (stageValue: string): number => {
  const normalized = normalizeStageTitle(stageValue);
  const index = DEFAULT_PROCESS_STEPS.findIndex((step) => {
    const normalizedStep = normalizeStageTitle(step);
    return normalized.includes(normalizedStep) || normalizedStep.includes(normalized);
  });
  return index >= 0 ? index : DEFAULT_PROCESS_STEPS.length;
};

const getProcessStatusLabel = (statusValue: string): string => {
  if (!statusValue) return '';
  return getStageStatusOptions('Первый контакт').find((item) => item.value === statusValue)?.label
    || getStageStatusOptions('Оффер').find((item) => item.value === statusValue)?.label
    || getStageStatusOptions('Найм').find((item) => item.value === statusValue)?.label
    || REJECTION_REASONS.find((item) => item.value === statusValue)?.label
    || CANDIDATE_REFUSAL_REASONS.find((item) => item.value === statusValue)?.label
    || getStatusLabel(statusValue);
};

const getEventVacancyTitle = (event: unknown): string => {
  const payload = historyPayload(event);
  return getStringValue(payload.vacancy)
    || getStringValue(payload.vacancy_name)
    || getStringValue(payload.position)
    || getStringValue(payload.title);
};

const buildProcessSteps = (stageEvents: unknown[], fallbackStage: string): ProcessStepView[] => {
  const byStage = new Map<string, ProcessStepView>();

  stageEvents.forEach((event) => {
    const payload = historyPayload(event);
    const label = getStringValue(payload.stage);
    if (!label) return;

    const key = normalizeStageTitle(label);
    if (byStage.has(key)) return;

    byStage.set(key, {
      label,
      status: getStringValue(payload.status) || getStringValue(payload.rejection_reason),
      date: historyDate(event),
    });
  });

  if (byStage.size === 0 && fallbackStage) {
    byStage.set(normalizeStageTitle(fallbackStage), {
      label: fallbackStage,
      status: '',
      date: '',
    });
  }

  return Array.from(byStage.values()).sort((left, right) => {
    const orderDiff = getStageOrder(left.label) - getStageOrder(right.label);
    return orderDiff || left.label.localeCompare(right.label, 'ru');
  });
};

const VacancyProgressCard = ({
  title,
  date,
  steps,
  statusLabel,
  status,
  onSelectStage,
}: {
  title: string;
  date: string;
  steps: ProcessStepView[];
  statusLabel: string;
  status: string;
  onSelectStage?: (stage: string) => void;
}) => {
  const activeIndex = Math.max(0, steps.length - 1);

  return (
    <div className="rounded-[12px] border border-[#EEF0F2] bg-[#F8F7F3] p-4">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[15px] font-bold text-[#23252D]">{title}</div>
          {date && <div className="mt-1 text-[12px] text-[#868C98]">{date}</div>}
        </div>
        <span className={`rounded-[8px] px-3 py-1 text-[12px] ${isRejectionStatus(status) ? 'border border-[#F6C36B] bg-[#FFF3CD] text-[#8A5A00]' : 'border border-[#DDDCD6] text-[#525866]'}`}>
          {statusLabel}
        </span>
      </div>

      <div
        className="grid items-start"
        style={{ gridTemplateColumns: `repeat(${Math.max(steps.length, 1)}, minmax(72px, 1fr))` }}
      >
        {steps.map((step, index) => {
          const passed = index <= activeIndex;
          const active = index === activeIndex;
          const stageStatusLabel = getProcessStatusLabel(step.status) || (passed && !active ? 'Одобрен' : '');

          return (
            <button
              key={`${step.label}-${index}`}
              type="button"
              onClick={() => onSelectStage?.(step.label)}
              className="group relative flex min-h-[76px] flex-col items-center gap-2 rounded-[10px] px-1 py-1 text-center transition-colors hover:bg-white/70 focus:outline-none focus:ring-2 focus:ring-[#E16349]/25"
            >
              {index > 0 && (
                <span className={`absolute left-[-50%] top-[7px] h-0.5 w-full ${index <= activeIndex ? 'bg-[#1F9254]' : 'bg-[#D8D6CF]'}`} />
              )}
              <span className={`relative z-10 h-3.5 w-3.5 rounded-full ${active ? 'bg-[#2F7BFF]' : passed ? 'bg-[#1F9254]' : 'bg-[#D8D6CF]'}`} />
              <span className={`max-w-[74px] text-center text-[10px] leading-3 ${active ? 'font-semibold text-[#2F7BFF]' : 'text-[#868C98]'}`}>
                {step.label}
              </span>
              {stageStatusLabel && (
                <span className={`rounded-[6px] px-2 py-0.5 text-[10px] ${historyStatusClass(step.status)}`}>
                  {stageStatusLabel}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const HistoryTab = ({ card, vacancyTitle, stage, setStage, processStatus, setProcessStatus, processComment, setProcessComment, onOpenRejection, onOpenOffer, onOpenHire, onAdd }: {
  card: AtsCandidateCard;
  vacancyTitle: string;
  stage: string;
  setStage: (value: string) => void;
  processStatus: string;
  setProcessStatus: (value: string) => void;
  processComment: string;
  setProcessComment: (value: string) => void;
  onOpenRejection: () => void;
  onOpenOffer: () => void;
  onOpenHire: () => void;
  onAdd: () => void;
}) => {
  const [customStageOpen, setCustomStageOpen] = useState(false);
  const [customStage, setCustomStage] = useState('');
  const events = Array.isArray(card.history) ? card.history.slice().reverse() : [];
  const stageEvents = events.filter((event) => historyType(event) === 'process_stage');
  const responsibleRecruiter = getResponsibleRecruiter(card);
  const rejectionEvents = events.filter((event) => {
    const payload = historyPayload(event);
    return historyType(event) === 'candidate_refused'
      || ['rejected', 'candidate_refused', 'security_rejected'].includes(getStringValue(payload.status));
  });
  const stageOptions = useMemo(() => {
    const options = new Map<string, { value: string; label: string }>();
    DEFAULT_PROCESS_STAGE_OPTIONS.forEach((option) => options.set(normalizeStageTitle(String(option.value)), option));
    stageEvents.forEach((event) => {
      const value = getStringValue(historyPayload(event).stage);
      if (!value) return;
      options.set(normalizeStageTitle(value), { value, label: value });
    });
    if (stage.trim()) {
      options.set(normalizeStageTitle(stage), { value: stage.trim(), label: stage.trim() });
    }
    return Array.from(options.values());
  }, [stage, stageEvents]);

  const addCustomStage = () => {
    const normalizedStage = customStage.trim();
    if (!normalizedStage) return;
    setStage(normalizedStage);
    setCustomStage('');
    setCustomStageOpen(false);
  };
  const currentVacancyTitle = vacancyTitle || 'Вакансия';
  const relatedStageEvents = stageEventsForCurrentVacancy(
    stageEvents,
    currentVacancyTitle,
    getEventVacancyTitle,
  );
  const relatedEvent = relatedStageEvents[0];
  const relatedPayload = historyPayload(relatedEvent);
  const statusValue = getStringValue(relatedPayload.status) || getStringValue(relatedPayload.rejection_reason);
  const statusLabel = getProcessStatusLabel(statusValue) || 'В процессе';
  const processSteps = buildProcessSteps(relatedStageEvents, stage);

  return (
    <section className="rounded-[14px] bg-white p-5 shadow-[0_8px_24px_rgba(21,22,24,0.08)]">
      <div>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-[17px] font-bold text-[#23252D]">
            <BriefcaseIcon className="h-5 w-5" />
            История по вакансиям
          </h3>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onOpenOffer}
              className="h-9 rounded-[8px] border border-[#DDDCD6] px-3 text-[12px] font-semibold text-[#525866] hover:bg-[#F5F4EF]"
            >
              Оформить оффер
            </button>
            <button
              type="button"
              onClick={onOpenHire}
              className="h-9 rounded-[8px] bg-[#E16349] px-3 text-[12px] font-semibold text-white hover:bg-[#D95740]"
            >
              Оформить найм
            </button>
          </div>
        </div>
        <div className="space-y-4">
          <VacancyProgressCard
            title={currentVacancyTitle}
            date={historyDate(relatedEvent) || formatDate(card.updated_at)}
            steps={processSteps}
            statusLabel={statusLabel}
            status={statusValue}
            onSelectStage={setStage}
          />
        </div>
      </div>

      <div className="mt-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-[17px] font-bold text-[#23252D]">
            <NoSymbolIcon className="h-5 w-5" />
            Отказы
          </h3>
          <button
            type="button"
            onClick={onOpenRejection}
            className="h-9 rounded-[8px] border border-[#DDDCD6] px-3 text-[12px] font-semibold text-[#525866] hover:bg-[#F5F4EF]"
          >
            Добавить причину
          </button>
        </div>
        <div className="space-y-3">
          {rejectionEvents.length ? rejectionEvents.map((event, index) => {
            const payload = historyPayload(event);
            const statusValue = getStringValue(payload.status) || historyType(event);
            const reasonValue = getStringValue(payload.rejection_reason) || getStringValue(payload.candidate_refusal_reason);
            const reasonLabel = REJECTION_REASONS.find((item) => item.value === reasonValue)?.label
              || CANDIDATE_REFUSAL_REASONS.find((item) => item.value === reasonValue)?.label
              || asText(reasonValue);
            return (
              <div key={index} className="rounded-[10px] border border-[#EEF0F2] p-4">
                <div className="flex justify-between gap-3">
                  <div className="font-semibold text-[#23252D]">
                    {statusValue === 'candidate_refused'
                      ? 'Отказ кандидата'
                      : statusValue === 'security_rejected'
                        ? 'Отказ СБ'
                        : 'Отказ компании'}
                  </div>
                  <div className="text-[12px] text-[#868C98]">{historyDate(event)}</div>
                </div>
                {statusValue !== 'security_rejected' && (
                  <div className="mt-3 rounded-[8px] border border-[#DDDCD6] px-3 py-2 text-[13px] text-[#6B7280]">
                    {reasonLabel || 'Причина не указана'}
                  </div>
                )}
                {getStringValue(payload.comment) && <div className="mt-2 text-[13px] text-[#6B7280]">{getStringValue(payload.comment)}</div>}
              </div>
            );
          }) : (
            <div className="rounded-[10px] bg-[#F8F7F3] px-4 py-3 text-[13px] text-[#868C98]">Причины отказов не добавлены</div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <h3 className="mb-3 flex items-center gap-2 text-[17px] font-bold text-[#23252D]">
          <InformationCircleIcon className="h-5 w-5" />
          Процессинг
        </h3>
        <div className="grid grid-cols-[minmax(0,1fr)_160px_44px] gap-2 max-[900px]:grid-cols-1">
          <UniversalDropdown
            value={stage}
            onChange={(value) => {
              const nextStage = String(value);
              setStage(nextStage);
              setProcessStatus(getStageStatusOptions(nextStage)[0].value);
            }}
            options={stageOptions}
            placeholder="Выберите этап"
          />
          <UniversalDropdown
            value={processStatus}
            onChange={(value) => setProcessStatus(String(value))}
            options={getStageStatusOptions(stage)}
          />
          <button
            type="button"
            onClick={onAdd}
            disabled={!stage.trim()}
            className="flex h-[42px] items-center justify-center rounded-[8px] bg-[#E16349] text-white transition-colors hover:bg-[#D95740] disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
            aria-label="Добавить этап в историю"
          >
            <ChevronDownIcon className="h-5 w-5 -rotate-90" />
          </button>
        </div>
        <div className="mt-2">
          {!customStageOpen ? (
            <button
              type="button"
              onClick={() => setCustomStageOpen(true)}
              className="inline-flex h-8 items-center gap-1 rounded-[8px] border border-dashed border-[#CDD0D5] px-3 text-[12px] font-semibold text-[#868C98] hover:border-[#C9C6BE] hover:text-[#525866]"
            >
              <PlusIcon className="h-4 w-4" />
              Добавить свой этап
            </button>
          ) : (
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2 max-[700px]:grid-cols-1">
              <AtsTextInput
                value={customStage}
                onChange={setCustomStage}
                placeholder="Название нового этапа"
                autoFocus
              />
              <button
                type="button"
                onClick={addCustomStage}
                disabled={!customStage.trim()}
                className="h-[42px] rounded-[9px] bg-[#23252D] px-4 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
              >
                Добавить
              </button>
              <button
                type="button"
                onClick={() => {
                  setCustomStage('');
                  setCustomStageOpen(false);
                }}
                className="h-[42px] rounded-[9px] border border-[#DDDCD6] px-4 text-[13px] font-semibold text-[#77736B] hover:bg-[#F5F4EF]"
              >
                Отмена
              </button>
            </div>
          )}
        </div>
        <AtsTextInput
          value={processComment}
          onChange={setProcessComment}
          placeholder="Комментарий"
          className="mt-2"
        />

        <div className="mt-5 space-y-4 border-l border-[#D8D6CF] pl-5">
          {events.map((event, index) => {
            const payload = historyPayload(event);
            const eventType = historyType(event);
            const title = getStringValue(payload.stage) || HISTORY_TYPE_LABELS[eventType] || eventType;
            const comment = formatHistoryDetails(event);
            const statusValue = getStringValue(payload.status) || getStringValue(payload.rejection_reason);
            const statusLabel = getProcessStatusLabel(statusValue);
            const author = historyAuthor(event, responsibleRecruiter?.name);
            return (
              <div key={index} className="relative">
                <span className="absolute -left-[26px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-[#23252D]" />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-[12px] font-bold uppercase tracking-[0.04em] text-[#868C98]">
                    {[historyDate(event), author, title].filter(Boolean).join(' · ')}
                  </div>
                  {statusValue && <span className={`rounded-[6px] px-2 py-0.5 text-[10px] ${historyStatusClass(statusValue)}`}>{statusLabel}</span>}
                </div>
                <div className="mt-2 rounded-[8px] bg-[#F8F7F3] px-4 py-3 text-[13px] leading-5 text-[#6B7280]">
                  <HighlightedText text={comment || 'Без комментария'} />
                </div>
              </div>
            );
          })}
          {!events.length && <div className="text-[13px] text-[#868C98]">История пока пустая</div>}
        </div>
      </div>
    </section>
  );
};

const NotesTab = ({ card, comment, setComment, onAdd, onEdit, onDelete }: {
  card: AtsCandidateCard;
  comment: string;
  setComment: (value: string) => void;
  onAdd: () => void;
  onEdit: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  return (
    <div className="space-y-4">
      <Section title="Комментарии">
        <div className="mb-4 space-y-3">
          {(card.comments || []).map((item) => {
            const id = getItemId(item);
            const isEditing = editingId === id;
            return (
              <div key={id} className="rounded-[8px] bg-[#F8F9FB] p-3">
                {isEditing ? (
                  <>
                    <AtsTextArea value={editingText} onChange={setEditingText} rows={3} />
                    <div className="mt-2 flex justify-end gap-2">
                      <button type="button" onClick={() => setEditingId(null)} className="h-8 rounded-[7px] border border-[#DDDCD6] px-3 text-[12px] font-semibold text-[#77736B]">Отмена</button>
                      <button
                        type="button"
                        disabled={!editingText.trim()}
                        onClick={() => {
                          onEdit(id, editingText.trim());
                          setEditingId(null);
                        }}
                        className="h-8 rounded-[7px] bg-[#1F2A44] px-3 text-[12px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#CDD0D5]"
                      >
                        Сохранить
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between gap-3">
                      <div className="text-[14px] text-[#1F2A44]"><HighlightedText text={getCommentText(item)} /></div>
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(id);
                            setEditingText(getCommentText(item));
                          }}
                          className="rounded-[6px] px-2 text-[12px] font-semibold text-[#525866] hover:bg-white"
                        >
                          Изменить
                        </button>
                        <button type="button" onClick={() => onDelete(id)} className="text-[#D92D20]" aria-label="Удалить комментарий">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 text-[12px] text-[#868C98]">{asText((item as Record<string, unknown>).author)} · {asText((item as Record<string, unknown>).created_at)}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <AtsTextArea value={comment} onChange={setComment} rows={4} placeholder="Добавить комментарий" />
        <button type="button" onClick={onAdd} disabled={!comment.trim()} className="mt-2 h-[38px] rounded-[8px] bg-[#1F2A44] px-3 text-[14px] text-white disabled:cursor-not-allowed disabled:bg-[#CDD0D5]">Добавить</button>
      </Section>
    </div>
  );
};

const RecruiterModal = ({
  recruiters,
  selectedId,
  currentId,
  setSelectedId,
  disabled,
  loading,
  error,
  onClose,
  onSubmit,
}: {
  recruiters: RecruiterOption[];
  selectedId: string;
  currentId?: string;
  setSelectedId: (value: string) => void;
  disabled: boolean;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
}) => (
  <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
    <div className="w-full max-w-[520px] overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
      <div className="flex items-center justify-between border-b border-[#EEF0F2] px-5 py-4">
        <h2 className="text-[17px] font-bold text-[#23252D]">Сменить ответственного рекрутера</h2>
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-[#DDDCD6] text-[#525866] hover:bg-[#F5F4EF]"
          aria-label="Закрыть"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="max-h-[420px] space-y-1 overflow-y-auto px-5 py-4">
        {loading && (
          <div className="rounded-[10px] bg-[#F8F7F3] px-4 py-5 text-center text-[14px] text-[#868C98]">
            Загружаем сотрудников компании...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-[10px] bg-[#FFF2F0] px-4 py-3 text-[13px] text-[#D92D20]">
            {error}
          </div>
        )}

        {!loading && !error && recruiters.length === 0 && (
          <div className="rounded-[10px] bg-[#F8F7F3] px-4 py-5 text-center text-[14px] text-[#868C98]">
            В компании пока нет доступных сотрудников. Добавьте пользователя в настройках компании или команды.
          </div>
        )}

        {!loading && recruiters.map((recruiter) => {
          const selected = selectedId === recruiter.id;
          const current = currentId === recruiter.id;

          return (
            <button
              key={recruiter.id}
              type="button"
              onClick={() => setSelectedId(recruiter.id)}
              className={`flex w-full items-center gap-3 rounded-[8px] px-3 py-2 text-left transition ${
                selected ? 'bg-[#E6F0FF]' : 'hover:bg-[#F5F4EF]'
              }`}
            >
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                selected ? 'border-[#2F7BFF]' : 'border-[#9A9CA3]'
              }`}>
                {selected && <span className="h-2.5 w-2.5 rounded-full bg-[#2F7BFF]" />}
              </span>
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-[#525866]"
                style={{ backgroundColor: recruiter.color }}
              >
                {getInitials(recruiter.name)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-semibold text-[#23252D]">{recruiter.name}</span>
                <span className="block truncate text-[12px] text-[#868C98]">{recruiter.email || 'email не указан'}</span>
              </span>
              {current && (
                <span className="rounded-[7px] bg-[#EEF5FF] px-2 py-1 text-[11px] font-semibold text-[#2F7BFF]">
                  Текущий
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-[#EEF0F2] px-5 py-4">
        <button type="button" onClick={onClose} className="h-11 rounded-[10px] bg-[#F1F0EC] text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]">
          Отмена
        </button>
        <button
          type="button"
          disabled={disabled || loading || !selectedId || recruiters.length === 0}
          onClick={onSubmit}
          className="h-11 rounded-[10px] bg-[#E16349] text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
        >
          Сохранить
        </button>
      </div>
    </div>
  </div>
);

const RejectionReasonModal = ({
  mode,
  initialReason,
  disabled,
  onClose,
  onSubmit,
}: {
  mode: 'rejected' | 'candidate_refused';
  initialReason: string;
  disabled: boolean;
  onClose: () => void;
  onSubmit: (reason: string, comment: string) => void;
}) => {
  const reasons = mode === 'candidate_refused' ? CANDIDATE_REFUSAL_REASONS : REJECTION_REASONS;
  const [reason, setReason] = useState(initialReason || reasons[0].value);
  const [comment, setComment] = useState('');

  const selectReason = (value: string) => {
    setReason(value);
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
      <div className="w-full max-w-[420px] overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
        <div className="flex items-center justify-between border-b border-[#EEF0F2] px-5 py-4">
          <h2 className="text-[16px] font-bold text-[#23252D]">
            {mode === 'candidate_refused' ? 'Выберите причину самоотказа' : 'Выберите причину отказа'}
          </h2>
          <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-[#DDDCD6] text-[#525866] hover:bg-[#F5F4EF]" aria-label="Закрыть">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="py-2">
          {reasons.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => selectReason(item.value)}
              className={`block w-full px-5 py-3 text-left text-[13px] text-[#23252D] hover:bg-[#F5F4EF] ${
                reason === item.value ? 'bg-[#F1F0EC] font-semibold' : ''
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="px-5 pb-4">
          <label className="block">
            <span className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.05em] text-[#A0A4AE]">Комментарий</span>
            <AtsTextInput
              value={comment}
              onChange={setComment}
              placeholder="Комментарий обязателен"
            />
          </label>
          {!comment.trim() && <div className="mt-2 text-[12px] text-[#D92D20]">Добавьте комментарий к причине отказа.</div>}
        </div>

        <div className="grid grid-cols-2 gap-3 border-t border-[#EEF0F2] px-5 py-4">
          <button type="button" onClick={onClose} className="h-11 rounded-[10px] bg-[#F1F0EC] text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]">
            Отмена
          </button>
          <button
            type="button"
            disabled={disabled || !reason || !comment.trim()}
            onClick={() => onSubmit(reason, comment)}
            className="h-11 rounded-[10px] bg-[#E16349] text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
};

const toInputDate = (date: Date): string => date.toISOString().slice(0, 10);

const OfferModal = ({
  mode,
  candidateName,
  vacancy,
  disabled,
  onClose,
  onSubmit,
}: {
  mode: 'sent' | 'accepted';
  candidateName: string;
  vacancy: string;
  disabled: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    outcome: 'sent' | 'accepted';
    offer_date?: string;
    valid_until?: string;
    accepted_at?: string;
    comment?: string;
  }) => void;
}) => {
  const now = new Date();
  const defaultEnd = new Date(now);
  defaultEnd.setDate(defaultEnd.getDate() + 14);
  const [offerDate, setOfferDate] = useState(toInputDate(now));
  const [validUntil, setValidUntil] = useState(toInputDate(defaultEnd));
  const [acceptedAt, setAcceptedAt] = useState(toInputDate(now));
  const [comment, setComment] = useState('');

  const canSubmit = mode === 'sent'
    ? Boolean(offerDate && validUntil)
    : Boolean(acceptedAt);

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
      <div className="w-full max-w-[420px] overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
        <div className="px-6 pt-6">
          <h2 className="text-[20px] font-bold text-[#23252D]">
            {mode === 'accepted' ? 'Принятие оффера' : 'Оформление оффера'}
          </h2>
          <div className="mt-1 text-[13px] text-[#9A9CA3]">
            {[vacancy, candidateName].filter(Boolean).join(' · ')}
          </div>
        </div>

        <div className="space-y-4 px-6 py-5">
          {mode === 'sent' ? (
            <>
              <label className="block">
                <span className="mb-2 block text-[13px] text-[#868C98]">Дата оффера</span>
                <AtsTextInput
                  type="date"
                  value={offerDate}
                  onChange={setOfferDate}
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-[13px] text-[#868C98]">Срок действия оффера</span>
                <AtsTextInput
                  type="date"
                  value={validUntil}
                  onChange={setValidUntil}
                />
              </label>
            </>
          ) : (
            <>
              <label className="block">
                <span className="mb-2 block text-[13px] text-[#868C98]">
                  Дата принятия оффера <span className="text-[#E5484D]">*</span>
                </span>
                <AtsTextInput
                  type="date"
                  value={acceptedAt}
                  onChange={setAcceptedAt}
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-[13px] text-[#868C98]">Комментарий</span>
                <AtsTextInput
                  value={comment}
                  onChange={setComment}
                  placeholder="Условия оффера (необязательно)"
                />
              </label>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-[#EEF0F2] px-6 py-4">
          <button type="button" onClick={onClose} className="h-10 rounded-[9px] border border-[#DDDCD6] px-4 text-[14px] font-semibold text-[#77736B] hover:bg-[#F5F4EF]">
            Отмена
          </button>
          <button
            type="button"
            disabled={disabled || !canSubmit}
            onClick={() => onSubmit(
              mode === 'sent'
                ? { outcome: 'sent', offer_date: offerDate, valid_until: validUntil }
                : { outcome: 'accepted', accepted_at: acceptedAt, comment: comment.trim() || undefined },
            )}
            className="h-10 rounded-[9px] bg-[#E16349] px-5 text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusPickerModal = ({
  currentStatus,
  disabled,
  onClose,
  onSelect,
}: {
  currentStatus: string;
  disabled: boolean;
  onClose: () => void;
  onSelect: (status: string) => void;
}) => (
  <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
    <div className="w-full max-w-[420px] overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
      <div className="flex items-center justify-between border-b border-[#EEF0F2] px-5 py-4">
        <h2 className="text-[16px] font-bold text-[#23252D]">Изменить статус кандидата</h2>
        <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-[#DDDCD6] text-[#525866] hover:bg-[#F5F4EF]" aria-label="Закрыть">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="py-2">
        {CARD_STATUS_EDIT_OPTIONS.map((item) => (
          <button
            key={item.value}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(item.value)}
            className={`block w-full px-5 py-3 text-left text-[13px] text-[#23252D] hover:bg-[#F5F4EF] disabled:cursor-not-allowed ${
              currentStatus === item.value ? 'bg-[#F1F0EC] font-semibold' : ''
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const InactiveReasonModal = ({
  disabled,
  onClose,
  onSubmit,
}: {
  disabled: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => void;
}) => {
  const [comment, setComment] = useState('');

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
      <div className="w-full max-w-[420px] overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
        <div className="flex items-center justify-between border-b border-[#EEF0F2] px-5 py-4">
          <h2 className="text-[16px] font-bold text-[#23252D]">Причина неактивности</h2>
          <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-[#DDDCD6] text-[#525866] hover:bg-[#F5F4EF]" aria-label="Закрыть">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4">
          <label className="block">
            <span className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.05em] text-[#A0A4AE]">Комментарий</span>
            <AtsTextInput
              value={comment}
              onChange={setComment}
              placeholder="Укажите причину перевода в неактивные"
            />
          </label>
          {!comment.trim() && <div className="mt-2 text-[12px] text-[#D92D20]">Комментарий обязателен.</div>}
        </div>
        <div className="grid grid-cols-2 gap-3 border-t border-[#EEF0F2] px-5 py-4">
          <button type="button" onClick={onClose} className="h-11 rounded-[10px] bg-[#F1F0EC] text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]">
            Отмена
          </button>
          <button
            type="button"
            disabled={disabled || !comment.trim()}
            onClick={() => onSubmit(comment)}
            className="h-11 rounded-[10px] bg-[#E16349] text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
};

const HireModal = ({
  candidateName,
  vacancy,
  disabled,
  onClose,
  onSubmit,
}: {
  candidateName: string;
  vacancy: string;
  disabled: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    start_date: string;
    department: string;
    manager: string;
    position: string;
    employment_type: string;
    probation_period: string;
  }) => void;
}) => {
  const defaultStart = new Date();
  defaultStart.setDate(defaultStart.getDate() + 2);
  const [startDate, setStartDate] = useState(toInputDate(defaultStart));
  const [department, setDepartment] = useState('Управление данных');
  const [manager, setManager] = useState('Иванов И.И.');
  const [position, setPosition] = useState(vacancy || 'Middle Data Scientist');
  const [employmentType, setEmploymentType] = useState('Штат');
  const [probationPeriod, setProbationPeriod] = useState('3 месяца');

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
      <div className="w-full max-w-[460px] overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
        <div className="px-6 pt-6">
          <h2 className="text-[20px] font-bold text-[#23252D]">Оформление найма</h2>
          <div className="mt-1 text-[13px] text-[#9A9CA3]">
            {[vacancy, candidateName].filter(Boolean).join(' · ')}
          </div>
        </div>

        <div className="space-y-4 px-6 py-5">
          <label className="block">
            <span className="mb-2 block text-[13px] text-[#868C98]">
              Дата выхода <span className="text-[#E5484D]">*</span>
            </span>
            <AtsTextInput
              type="date"
              value={startDate}
              onChange={setStartDate}
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-[13px] text-[#868C98]">Подразделение</span>
            <AtsTextInput
              value={department}
              onChange={setDepartment}
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-[13px] text-[#868C98]">Руководитель</span>
            <AtsTextInput
              value={manager}
              onChange={setManager}
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-[13px] text-[#868C98]">Должность</span>
            <AtsTextInput
              value={position}
              onChange={setPosition}
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-[13px] text-[#868C98]">Тип занятости</span>
            <UniversalDropdown
              value={employmentType}
              onChange={(value) => setEmploymentType(String(value))}
              options={[
                { value: 'Штат', label: 'Штат' },
                { value: 'Контракт', label: 'Контракт' },
                { value: 'Проектная занятость', label: 'Проектная занятость' },
                { value: 'Стажировка', label: 'Стажировка' },
              ]}
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-[13px] text-[#868C98]">Длительность испытательного срока</span>
            <UniversalDropdown
              value={probationPeriod}
              onChange={(value) => setProbationPeriod(String(value))}
              options={[
                { value: 'Без испытательного срока', label: 'Без испытательного срока' },
                { value: '1 месяц', label: '1 месяц' },
                { value: '2 месяца', label: '2 месяца' },
                { value: '3 месяца', label: '3 месяца' },
                { value: '6 месяцев', label: '6 месяцев' },
              ]}
            />
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t border-[#EEF0F2] px-6 py-4">
          <button type="button" onClick={onClose} className="h-10 rounded-[9px] border border-[#DDDCD6] px-4 text-[14px] font-semibold text-[#77736B] hover:bg-[#F5F4EF]">
            Отмена
          </button>
          <button
            type="button"
            disabled={disabled || !startDate}
            onClick={() => onSubmit({
              start_date: startDate,
              department,
              manager,
              position,
              employment_type: employmentType,
              probation_period: probationPeriod,
            })}
            className="h-10 rounded-[9px] bg-[#E16349] px-5 text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
};

const BlacklistModal = ({
  reason,
  setReason,
  disabled,
  onClose,
  onSubmit,
}: {
  reason: string;
  setReason: (value: string) => void;
  disabled: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) => (
  <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
    <div className="w-full max-w-[500px] rounded-[14px] bg-white p-6 shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
      <h2 className="text-[20px] font-bold text-[#23252D]">Внести в чёрный список</h2>
      <p className="mt-4 text-[14px] text-[#868C98]">
        Укажите причину — комментарий обязателен.
      </p>

      <label className="mt-5 block">
        <span className="mb-2 block text-[13px] font-semibold text-[#525866]">
          Причина <span className="text-[#E5484D]">*</span>
        </span>
        <AtsTextArea
          value={reason}
          onChange={setReason}
          rows={5}
          className="bg-[#F5F4EF]"
          placeholder="Например: подделал данные в резюме, подтверждено проверкой СБ."
          autoFocus
        />
      </label>

      <div className="mt-5 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="h-11 rounded-[9px] border border-[#DDDCD6] px-5 text-[14px] font-semibold text-[#77736B] hover:bg-[#F5F4EF]"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={disabled || !reason.trim()}
          className="h-11 rounded-[9px] bg-[#E5484D] px-5 text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
        >
          Внести в ЧС
        </button>
      </div>
    </div>
  </div>
);

const DeleteCandidateModal = ({
  candidateName,
  disabled,
  onClose,
  onSubmit,
}: {
  candidateName: string;
  disabled: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) => (
  <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
    <div className="w-full max-w-[500px] rounded-[14px] bg-white p-6 shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
      <h2 className="text-[20px] font-bold text-[#23252D]">Удалить кандидата?</h2>
      <p className="mt-4 text-[14px] leading-6 text-[#525866]">
        Карточка «{candidateName}», связанные резюме, вложения и история будут удалены без возможности восстановления.
      </p>
      <div className="mt-5 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={disabled}
          className="h-11 rounded-[9px] border border-[#DDDCD6] px-5 text-[14px] font-semibold text-[#77736B] hover:bg-[#F5F4EF] disabled:cursor-not-allowed"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={disabled}
          className="h-11 rounded-[9px] bg-[#E5484D] px-5 text-[14px] font-semibold text-white hover:bg-[#D93F45] disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
        >
          Удалить
        </button>
      </div>
    </div>
  </div>
);

const formatFileSize = (size: number): string => {
  if (size >= 1024 * 1024 * 1024) return `${(size / 1024 / 1024 / 1024).toFixed(1).replace('.', ',')} ГБ`;
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;
  return `${Math.max(1, Math.round(size / 1024))} КБ`;
};

const ATTACHMENT_EXTENSIONS = new Set(['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'heic', 'rtf', 'txt', 'xls', 'xlsx', 'csv']);
const RESUME_EXTENSIONS = new Set(['pdf', 'doc', 'docx']);

const AttachmentModal = ({
  disabled,
  onClose,
  onSubmit,
}: {
  disabled: boolean;
  onClose: () => void;
  onSubmit: (file: File, type: 'resume' | 'attachment') => void;
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [type, setType] = useState<'resume' | 'attachment'>('resume');
  const [dragging, setDragging] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setOpened(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const closeWithAnimation = () => {
    setOpened(false);
    window.setTimeout(onClose, 180);
  };

  const updateFile = (nextFiles: FileList | File[]) => {
    const nextFile = Array.from(nextFiles)[0] || null;
    if (!nextFile) {
      setFile(null);
      setFileError(null);
      return;
    }
    const extension = nextFile.name.split('.').pop()?.toLowerCase() || '';
    const allowed = type === 'resume' ? RESUME_EXTENSIONS : ATTACHMENT_EXTENSIONS;
    if (!allowed.has(extension)) {
      setFile(null);
      setFileError(
        type === 'resume'
          ? `Неподдерживаемый формат резюме: .${extension || 'без расширения'}. Доступны PDF, DOC, DOCX.`
          : `Неподдерживаемый формат вложения: .${extension || 'без расширения'}. Доступны PDF, DOC, DOCX, PNG, JPG, HEIC, RTF, TXT, XLS, XLSX, CSV.`,
      );
      return;
    }
    setFileError(null);
    setFile(nextFile);
  };

  return (
    <div className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 transition-colors duration-200 ease-out ${opened ? 'bg-black/35' : 'bg-black/0'}`}>
      <div className={`flex max-h-[calc(100vh-32px)] w-full max-w-[520px] origin-center flex-col overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)] transition-all duration-200 ease-out ${opened ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-2 scale-95 opacity-0'}`}>
        <div className="flex shrink-0 items-center justify-between border-b border-[#EEF0F2] px-6 py-5">
          <h2 className="text-[18px] font-bold text-[#23252D]">Добавить вложение</h2>
          <button type="button" onClick={closeWithAnimation} className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-[#DDDCD6] text-[#525866] hover:bg-[#F5F4EF]" aria-label="Закрыть">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <TabToggle
            className="inline-flex w-auto"
            options={[
              { id: 'resume', label: 'Резюме' },
              { id: 'attachment', label: 'Обычный файл' },
            ]}
            activeTab={type}
            onTabChange={(tab) => {
              setType(tab as 'resume' | 'attachment');
              setFile(null);
              setFileError(null);
            }}
            radius="rounded-[8px]"
          />

          <div className="text-[13px] leading-5 text-[#6B7280]">
            {type === 'resume'
              ? 'Файл будет распознан, сохранён как резюме и обновит данные карточки кандидата.'
              : 'Обычные файлы сохраняются как вложения без обработки резюме.'}
          </div>

          <label
            className={`flex min-h-[170px] cursor-pointer flex-col items-center justify-center rounded-[14px] border border-dashed px-6 text-center transition ${dragging ? 'border-[#E16349] bg-[#FFF2EC]' : 'border-[#D8D6CF] bg-[#F8F7F3]'}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              updateFile(Array.from(event.dataTransfer.files || []));
            }}
          >
            <DocumentArrowUpIcon className="mb-3 h-12 w-12 text-[#CFC9BA]" />
            <div className="text-[15px] font-semibold text-[#23252D]">
              {type === 'resume' ? 'Перетащите файл с резюме' : 'Перетащите файл вложения'}
            </div>
            <div className="mt-1 text-[12px] text-[#A19B8E]">
              {type === 'resume' ? 'PDF, DOC, DOCX до 200 МБ' : 'PDF, DOC, DOCX, PNG, JPG, HEIC, RTF, TXT, XLS, XLSX, CSV'}
            </div>
            <input
              type="file"
              accept={type === 'resume' ? '.pdf,.doc,.docx' : '.pdf,.doc,.docx,.png,.jpg,.jpeg,.heic,.rtf,.txt,.xls,.xlsx,.csv'}
              onChange={(event) => updateFile(event.target.files || [])}
              className="hidden"
            />
          </label>

          {fileError && (
            <div className="rounded-[8px] bg-[#FFF2F0] px-3 py-2 text-[13px] text-[#B42318]">
              {fileError}
            </div>
          )}

          {file && (
            <div className="flex items-center gap-3 rounded-[10px] border border-[#DDDCD6] bg-white px-3 py-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] text-[11px] font-bold ${type === 'resume' ? 'bg-[#FFE6F0] text-[#E5487B]' : 'bg-[#FFF2EC] text-[#E16349]'}`}>
                {type === 'resume' ? (file.name.split('.').pop() || 'CV').slice(0, 3).toUpperCase() : <PaperClipIcon className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-semibold text-[#23252D]">{file.name}</div>
                <div className="text-[12px] text-[#868C98]">{formatFileSize(file.size)}</div>
              </div>
              <button type="button" onClick={() => { setFile(null); setFileError(null); }} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[#868C98] hover:bg-[#F5F4EF]" aria-label="Убрать файл">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-[#EEF0F2] px-6 py-4">
          <button type="button" onClick={closeWithAnimation} className="h-12 rounded-[10px] bg-[#F1F0EC] text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]">
            Отмена
          </button>
          <button
            type="button"
            disabled={disabled || !file}
            onClick={() => file && onSubmit(file, type)}
            className="h-12 rounded-[10px] bg-[#E16349] text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
          >
            {disabled ? 'Загрузка...' : type === 'resume' ? 'Начать импорт' : 'Добавить файл'}
          </button>
        </div>
      </div>
    </div>
  );
};

const getExpandableFolderPaths = (nodes: AtsFolderNode[]): string[] => (
  nodes.flatMap((node) => (
    node.children.length ? [node.path, ...getExpandableFolderPaths(node.children)] : []
  ))
);

const folderMatchesQuery = (node: AtsFolderNode, query: string): boolean => {
  if (!query) return true;
  const normalizedQuery = query.trim().toLocaleLowerCase('ru-RU');
  if (!normalizedQuery) return true;
  return node.name.toLocaleLowerCase('ru-RU').includes(normalizedQuery)
    || node.path.toLocaleLowerCase('ru-RU').includes(normalizedQuery)
    || node.children.some((child) => folderMatchesQuery(child, normalizedQuery));
};

export const MoveCandidateFolderModal = ({
  folders,
  currentFolder,
  disabled,
  onClose,
  onSubmit,
}: {
  folders: AtsFolderNode[];
  currentFolder: string;
  disabled: boolean;
  onClose: () => void;
  onSubmit: (folderPath: string) => void;
}) => {
  const [selectedFolder, setSelectedFolder] = useState(currentFolder);
  const [query, setQuery] = useState('');
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(
    () => new Set(getExpandableFolderPaths(folders)),
  );

  useEffect(() => {
    setSelectedFolder(currentFolder);
    setExpandedPaths(new Set(getExpandableFolderPaths(folders)));
  }, [currentFolder, folders]);

  const toggleExpanded = (path: string) => {
    setExpandedPaths((current) => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const renderNode = (node: AtsFolderNode, level = 0): React.ReactNode => {
    if (!folderMatchesQuery(node, query)) return null;
    const hasChildren = node.children.length > 0;
    const forceExpanded = Boolean(query.trim());
    const expanded = forceExpanded || expandedPaths.has(node.path);
    const selected = selectedFolder === node.path;
    const current = currentFolder === node.path;

    return (
      <div key={node.path}>
        <div
          className={`group flex min-h-10 items-center gap-2 rounded-[8px] pr-2 ${
            selected ? 'bg-[#FFF2EC] text-[#C94F32]' : 'text-[#23252D] hover:bg-[#F5F4EF]'
          }`}
          style={{ paddingLeft: `${8 + level * 20}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggleExpanded(node.path)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-[#868C98] hover:bg-white"
              aria-label={expanded ? `Свернуть ${node.name}` : `Развернуть ${node.name}`}
            >
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${expanded ? '' : '-rotate-90'}`} />
            </button>
          ) : (
            <span className="w-7 shrink-0" />
          )}
          <ArchiveBoxIcon className="h-4 w-4 shrink-0 text-[#F59E0B]" />
          <button
            type="button"
            onClick={() => !hasChildren && setSelectedFolder(node.path)}
            disabled={hasChildren}
            className={`min-w-0 flex-1 truncate py-2 text-left text-[13px] ${
              hasChildren ? 'cursor-default font-semibold' : 'cursor-pointer'
            }`}
            title={node.path}
          >
            {node.name}
          </button>
          <span className="shrink-0 text-[12px] text-[#868C98]">{node.count}</span>
          {current && <span className="hidden shrink-0 text-[11px] text-[#868C98] sm:inline">текущая</span>}
        </div>
        {hasChildren && expanded && node.children.map((child) => renderNode(child, level + 1))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
      <div className="w-full max-w-[620px] overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
        <div className="flex items-center justify-between border-b border-[#EEF0F2] px-6 py-5">
          <div>
            <h2 className="text-[18px] font-bold text-[#23252D]">Переместить резюме</h2>
            <p className="mt-1 text-[13px] text-[#868C98]">Выберите вакансию в структуре Страна - Проект - Вакансия.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-[#DDDCD6] text-[#525866] hover:bg-[#F5F4EF]"
            aria-label="Закрыть"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <AtsTextInput
            value={query}
            onChange={setQuery}
            placeholder="Найти страну, проект или вакансию"
            autoFocus
          />
          <div className="rounded-[10px] border border-[#E7E8EC] bg-white p-2">
            <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-[#868C98]">Папки</div>
            <div className="max-h-[360px] overflow-y-auto pr-1">
              {folders.map((node) => renderNode(node))}
              {!folders.length && <div className="px-2 py-5 text-center text-[13px] text-[#868C98]">Папок пока нет</div>}
            </div>
          </div>
          <div className="rounded-[8px] bg-[#F8F7F3] px-3 py-2 text-[13px] text-[#525866]">
            Новая папка: <span className="font-semibold text-[#23252D]">{selectedFolder || 'не выбрана'}</span>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-[#EEF0F2] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-[9px] bg-[#F1F0EC] px-5 text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]"
          >
            Отмена
          </button>
          <button
            type="button"
            disabled={disabled || !selectedFolder || selectedFolder === currentFolder}
            onClick={() => onSubmit(selectedFolder)}
            className="h-11 rounded-[9px] bg-[#E16349] px-5 text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
          >
            {disabled ? 'Перенос...' : 'Переместить сюда'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const DuplicatesModal = ({ onClose, onMerged }: {
  onClose: () => void;
  onMerged: () => void;
}) => {
  const [groups, setGroups] = useState<AtsDuplicateGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<AtsCandidateCard[]>([]);
  const [primaryCandidateId, setPrimaryCandidateId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [scanStatus, setScanStatus] = useState<'idle' | 'running' | 'completed' | 'failed'>('idle');
  const selectedGroup = groups.find((group) => group.id === selectedGroupId) || null;

  useEffect(() => {
    let cancelled = false;
    const loadGroups = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await atsApi.refreshDuplicateOverview();
        if (!cancelled) {
          const openGroups = result.groups.filter((group) => group.status === 'open');
          setGroups(openGroups);
          setSelectedGroupId((current) => openGroups.some((group) => group.id === current) ? current : openGroups[0]?.id || null);
          setScanStatus(result.scan_status);
          if (result.scan_error) setError(result.scan_error);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить дубли');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadGroups();
    return () => { cancelled = true; };
  }, [reloadVersion]);

  useEffect(() => {
    if (scanStatus !== 'running') return;
    const timer = setInterval(() => {
      void atsApi.getDuplicateOverview().then((result) => {
        const openGroups = result.groups.filter((group) => group.status === 'open');
        setGroups(openGroups);
        setSelectedGroupId((current) => openGroups.some((group) => group.id === current) ? current : openGroups[0]?.id || null);
        setScanStatus(result.scan_status);
        if (result.scan_error) setError(result.scan_error);
      }).catch(() => setError('Не удалось проверить состояние поиска дублей'));
    }, 3000);
    return () => clearInterval(timer);
  }, [scanStatus]);

  useEffect(() => {
    if (!selectedGroup) {
      setCandidates([]);
      setPrimaryCandidateId('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.allSettled(selectedGroup.candidate_ids.map((candidateId) => atsApi.getCandidateCard(candidateId)))
      .then((results) => {
        const cards = results.flatMap((result) => result.status === 'fulfilled' ? [result.value] : []);
        if (!cancelled) {
          if (!cards.length) {
            setCandidates([]);
            setPrimaryCandidateId('');
            setError('Карточки этой группы больше недоступны. Обновите список дублей.');
            return;
          }
          setCandidates(cards);
          setPrimaryCandidateId((current) => (
            cards.some((card) => card.candidate_id === current)
              ? current
              : cards.some((card) => card.candidate_id === selectedGroup.primary_candidate_id)
                ? selectedGroup.primary_candidate_id || ''
                : cards[0]?.candidate_id || ''
          ));
          if (cards.length !== selectedGroup.candidate_ids.length) {
            setError('Часть карточек этой группы больше недоступна. Доступные карточки можно объединить.');
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedGroup]);

  const merge = async () => {
    if (!selectedGroup || !primaryCandidateId) return;
    const mergedCandidateIds = selectedGroup.candidate_ids.filter((candidateId) => candidateId !== primaryCandidateId);
    if (!mergedCandidateIds.length) return;
    setBusy(true);
    setError(null);
    try {
      await atsApi.mergeDuplicates({
        group_id: selectedGroup.id,
        primary_candidate_id: primaryCandidateId,
        merged_candidate_ids: mergedCandidateIds,
      });
      setGroups((items) => items.filter((group) => group.id !== selectedGroup.id));
      setSelectedGroupId(null);
      onMerged();
    } catch {
      setError('Не удалось объединить карточки. Проверьте права доступа и повторите попытку.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
      <div className="flex max-h-[calc(100vh-32px)] w-full max-w-[920px] flex-col overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
        <div className="flex items-center justify-between border-b border-[#EEF0F2] px-6 py-5">
          <div>
            <h2 className="text-[18px] font-bold text-[#23252D]">Дубли кандидатов</h2>
            <p className="mt-1 text-[13px] text-[#868C98]">Выберите основную карточку. Контакты, резюме и метки остальных будут перенесены в нее.</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] border border-[#DDDCD6] text-[#525866] hover:bg-[#F5F4EF]" aria-label="Закрыть">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="max-h-[45vh] overflow-y-auto border-b border-[#EEF0F2] p-3 md:max-h-none md:border-b-0 md:border-r">
            {loading && !groups.length && <div className="p-3 text-[14px] text-[#868C98]">Загрузка...</div>}
            {scanStatus === 'running' && <div className="p-3 text-[13px] text-[#868C98]">Проверяем карточки на дубли. Список обновится автоматически.</div>}
            {!loading && scanStatus !== 'running' && !groups.length && !error && <div className="p-3 text-[14px] text-[#868C98]">Открытых дублей нет</div>}
            {groups.map((group) => (
              <button
                key={group.id}
                type="button"
                onClick={() => setSelectedGroupId(group.id)}
                className={`mb-2 w-full rounded-[10px] p-3 text-left ${selectedGroupId === group.id ? 'bg-[#FFF2EC] ring-1 ring-[#E16349]' : 'bg-[#F8F7F3] hover:bg-[#F1F0EC]'}`}
              >
                <div className="text-[14px] font-semibold text-[#23252D]">{group.candidate_ids.length} карточки</div>
                <div className="mt-1 truncate text-[12px] text-[#868C98]">Совпадение: {group.match_reason}</div>
                <div className="mt-1 text-[12px] text-[#868C98]">Оценка: {Math.round(group.match_score * 100)}%</div>
              </button>
            ))}
          </aside>

          <section className="min-h-0 overflow-y-auto p-5">
            {error && <div className="mb-4 rounded-[10px] bg-[#FFF2F0] p-3 text-[13px] text-[#B42318]">{error}</div>}
            {error && scanStatus !== 'running' && (
              <button type="button" onClick={() => setReloadVersion((version) => version + 1)} className="rounded-[8px] border border-[#CDD0D5] px-3 py-2 text-[13px] font-semibold text-[#23252D]">Повторить загрузку</button>
            )}
            {selectedGroup && (
              <>
                <div className="mb-3 text-[13px] text-[#868C98]">Причина совпадения: {selectedGroup.match_reason}</div>
                <div className="space-y-3">
                  {candidates.map((candidate) => {
                    const folderPaths = getCandidateDocumentFolderPaths(candidate);
                    const displayName = getCandidateDisplayName(candidate.full_name);
                    return (
                      <label key={candidate.candidate_id} className={`block cursor-pointer rounded-[10px] border p-4 ${primaryCandidateId === candidate.candidate_id ? 'border-[#E16349] bg-[#FFF9F6]' : 'border-[#DDDCD6] bg-white'}`}>
                        <div className="flex gap-3">
                          <input
                            type="radio"
                            name="duplicate-primary"
                            checked={primaryCandidateId === candidate.candidate_id}
                            onChange={() => setPrimaryCandidateId(candidate.candidate_id)}
                            className="mt-1 h-4 w-4 accent-[#E16349]"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-3">
                              <a href={buildAtsCandidateLink(candidate.candidate_id, folderPaths[0])}
                                target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}
                                aria-label={`Открыть карточку кандидата ${displayName} в новой вкладке`}
                                title="Открыть карточку в новой вкладке"
                                className="min-w-0 break-words text-[15px] font-semibold text-[#2F7BFF] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2F7BFF]">
                                {displayName}
                              </a>
                              {primaryCandidateId === candidate.candidate_id && <span className="shrink-0 rounded-full bg-[#FFE6DA] px-2 py-1 text-[11px] font-semibold text-[#C94F32]">Основная</span>}
                            </div>
                            <div className="mt-2 grid grid-cols-1 gap-1 text-[13px] text-[#525866]">
                              <div className="truncate">Телефон: {candidate.phone || candidate.phones.map(String).join(', ') || 'нет'}</div>
                              <div className="truncate">Email: {candidate.email || candidate.emails.map(String).join(', ') || 'нет'}</div>
                              <div className="truncate">Должность: {candidate.desired_position || 'не указана'}</div>
                              <div className="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere]">
                                Папка: {folderPaths.length ? folderPaths.map((path) => (
                                  <div key={path}>{path}</div>
                                )) : 'не указана'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </div>

        <div className="flex justify-end gap-3 border-t border-[#EEF0F2] px-6 py-4">
          <button type="button" onClick={onClose} className="h-11 rounded-[10px] bg-[#F1F0EC] px-5 text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]">Отмена</button>
          <button type="button" disabled={busy || scanStatus !== 'completed' || !selectedGroup || !primaryCandidateId || selectedGroup.candidate_ids.length < 2} onClick={() => void merge()} className="h-11 rounded-[10px] bg-[#E16349] px-5 text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]">
            {busy ? 'Объединение...' : 'Объединить'}
          </button>
        </div>
      </div>
    </div>
  );
};

const getParseWarnings = (document: AtsDocument): string[] => {
  const warnings = document.parsed_data?.raw?.parse_warnings;
  if (Array.isArray(warnings)) {
    return warnings.map(String).map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const parseWarningFieldLabels = (warnings: string[]): string[] => {
  const mapping: Array<[RegExp, string]> = [
    [/опыт|места работы/i, 'Места работы'],
    [/образован/i, 'Образование'],
    [/ФИО/i, 'ФИО'],
    [/контакт/i, 'Контакты'],
  ];
  const labels = mapping
    .filter(([pattern]) => warnings.some((warning) => pattern.test(warning)))
    .map(([, label]) => label);
  return labels.length ? labels : warnings;
};

export const ImportModal = ({ onClose, importJob, onImportJob, onImported, onOpenCandidate, onOpenDuplicates, onFoldersChanged, folder, folders }: {
  onClose: () => void;
  importJob: AtsImportJob | null;
  onImportJob: (job: AtsImportJob | null) => void;
  onImported: (folderPath: string) => void;
  onOpenCandidate: (candidateId: string, options?: { edit?: boolean }) => void;
  onOpenDuplicates: () => void;
  onFoldersChanged: () => Promise<void>;
  folder: string;
  folders: AtsFolderNode[];
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [busy, setBusy] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [opened, setOpened] = useState(false);
  const [country, setCountry] = useState(() => folderPathParts(folder)[0]);
  const [project, setProject] = useState(() => folderPathParts(folder)[1]);
  const [vacancy, setVacancy] = useState(() => folderPathParts(folder)[2]);
  const [locationMenuOpen, setLocationMenuOpen] = useState<'country' | 'project' | 'vacancy' | null>(null);
  const [duplicate, setDuplicate] = useState<{ candidateId: string; sameFolder: boolean; folders: string[] } | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [duplicatePending, setDuplicatePending] = useState(false);
  const [parseWarning, setParseWarning] = useState<{ candidateId: string; warnings: string[] } | null>(null);
  const selectedFile = files[0];
  const bulkImportSource = useMemo(() => getBulkImportSource(files), [files]);
  const usesArchiveStructure = mode === 'bulk' && bulkImportSource === 'archive';
  const hasMixedBulkSources = mode === 'bulk' && bulkImportSource === 'mixed';
  const selectedFolder = useMemo(() => [country, project, vacancy].map((part) => part.trim()).join('/'), [country, project, vacancy]);
  const countries = useMemo(
    () => folders.map((item) => item.name).filter((name) => !isAllCountriesFolderName(name)),
    [folders],
  );
  const selectedCountry = useMemo(
    () => folders.find((item) => item.name === country.trim() && !isAllCountriesFolderName(item.name)),
    [country, folders],
  );
  const projects = useMemo(() => selectedCountry?.children.map((item) => item.name) || [], [selectedCountry]);
  const selectedProject = useMemo(
    () => selectedCountry?.children.find((item) => item.name === project.trim()),
    [project, selectedCountry],
  );
  const vacancies = useMemo(() => selectedProject?.children.map((item) => item.name) || [], [selectedProject]);
  const locationIsComplete = Boolean(country.trim() && project.trim() && vacancy.trim() && !isAllCountriesFolderName(country));
  const canCreateCountry = Boolean(country.trim())
    && !isAllCountriesFolderName(country)
    && !countries.includes(country.trim());
  const canCreateProject = Boolean(country.trim() && project.trim() && selectedCountry) && !projects.includes(project.trim());
  const canCreateVacancy = Boolean(country.trim() && project.trim() && vacancy.trim() && selectedProject) && !vacancies.includes(vacancy.trim());
  const visibleOptions = (options: string[], value: string) => (
    options.includes(value.trim())
      ? options
      : options.filter((option) => option.toLocaleLowerCase('ru-RU').includes(value.trim().toLocaleLowerCase('ru-RU')))
  );
  const importStatusLabel: Record<string, string> = {
    queued: 'В очереди',
    processing: 'Обрабатывается',
    completed: 'Завершен',
    completed_with_errors: 'Завершен с ошибками',
    failed: 'Ошибка импорта',
    cancelled: 'Отменен',
  };
  const importIsActive = importJob?.status === 'queued' || importJob?.status === 'processing';
  const importErrors = importJob?.report?.errors || importJob?.files.filter((item) => item.status === 'failed') || [];
  const importWarnings = (importJob?.report?.files || []).filter((item) => {
    if (Array.isArray(item.warnings)) return item.warnings.length > 0;
    return Boolean(typeof item.warnings === 'string' && item.warnings.trim());
  });
  const createdCandidates = (importJob?.report?.files || []).filter((item) => item.candidate_id && item.status === 'Создан');
  const autoAttachedCandidates = (importJob?.report?.files || []).filter((item) => (
    item.candidate_id && item.status === 'Прикреплен к существующему кандидату'
  ));

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setOpened(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (!importJob?.id || !importIsActive) return undefined;

    let cancelled = false;
    const refresh = async () => {
      try {
        const job = await atsApi.getImportJob(importJob.id);
        if (!cancelled) {
          onImportJob(job);
          if (job.status === 'completed' || job.status === 'completed_with_errors') {
            onImported(selectedFolder);
            closeWithAnimation();
          }
        }
      } catch {
        if (!cancelled) setMessage('Не удалось обновить статус импорта');
      }
    };

    void refresh();
    const timer = window.setInterval(() => void refresh(), 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [importIsActive, importJob?.id, onImportJob, onImported]);

  const closeWithAnimation = () => {
    setOpened(false);
    window.setTimeout(onClose, 180);
  };

  const updateFiles = (nextFiles: File[]) => {
    const selected = mode === 'single' ? nextFiles.slice(0, 1) : nextFiles;
    setFiles(selected);
    setMessage(getImportFilesError(selected, mode));
    onImportJob(null);
  };

  const createLocation = async (level: 'country' | 'project' | 'vacancy') => {
    const name = (level === 'country' ? country : level === 'project' ? project : vacancy).trim();
    const parentPath = getFolderCreateParentPath(level, country, project);

    if (!name || (level === 'project' && !selectedCountry) || (level === 'vacancy' && !selectedProject)) return;
    if (level === 'country' && isAllCountriesFolderName(name)) {
      setMessage(`Папку «${ALL_COUNTRIES_FOLDER}» создать нельзя. Укажите «${DEFAULT_COUNTRY_FOLDER}» или другую страну.`);
      return;
    }

    setBusy(true);
    setMessage(null);
    try {
      await atsApi.createFolder(parentPath, name);
      await onFoldersChanged();
      setLocationMenuOpen(null);
      setMessage(`${level === 'country' ? 'Страна' : level === 'project' ? 'Проект' : 'Вакансия'} «${name}» создан${level === 'country' || level === 'vacancy' ? 'а' : ''}`);
    } catch {
      setMessage('Не удалось создать папку. Проверьте название и выбранный родительский уровень.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!files.length || hasMixedBulkSources || (!usesArchiveStructure && !locationIsComplete)) return;
    const sizeError = getImportFilesError(files, mode);
    if (sizeError) {
      setMessage(sizeError);
      return;
    }
    if (!usesArchiveStructure && isAllCountriesFolderName(country)) {
      setMessage(`В папку «${ALL_COUNTRIES_FOLDER}» импортировать нельзя. Выберите «${DEFAULT_COUNTRY_FOLDER}» или другую страну.`);
      return;
    }
    setBusy(true);
    setUploadPercent(mode === 'bulk' ? 0 : null);
    setMessage(null);
    try {
      const metadata = {
        source: 'ats_ui',
        tags: !usesArchiveStructure && selectedFolder && selectedFolder !== 'Все резюме' ? selectedFolder : undefined,
        folder_path: !usesArchiveStructure && selectedFolder && selectedFolder !== 'Все резюме' ? selectedFolder : undefined,
      };
      if (mode === 'single') {
        const duplicateCheck = await atsApi.checkResumeDuplicate(files[0], { folder_path: metadata.folder_path });
        if (duplicateCheck.candidate_id) {
          setDuplicateError(null);
          setDuplicate({
            candidateId: duplicateCheck.candidate_id,
            sameFolder: duplicateCheck.same_folder,
            folders: duplicateCheck.folder_paths,
          });
          return;
        }
      }
      const job = await atsApi.importResumes(files, metadata, setUploadPercent);
      onImportJob(job);
      setMessage(`Импорт создан: ${job.id}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Не удалось запустить импорт. Проверьте формат и размер файла.');
    } finally {
      setBusy(false);
      setUploadPercent(null);
    }
  };

  const poll = async () => {
    if (!importJob) return;
    setBusy(true);
    try {
      onImportJob(await atsApi.getImportJob(importJob.id));
    } finally {
      setBusy(false);
    }
  };

  const resolveDuplicate = async (action: 'attach' | 'keep' | 'cancel') => {
    if (!duplicate || busy || (duplicatePending && action !== 'cancel')) return;
    if (action === 'cancel') {
      setDuplicate(null);
      setDuplicateError(null);
      setDuplicatePending(false);
      setMessage('Загрузка отменена');
      return;
    }
    setBusy(true);
    setDuplicateError(null);
    try {
      const metadata = action === 'attach'
        ? { source: 'ats_ui' }
        : {
            source: 'ats_ui',
            tags: selectedFolder && selectedFolder !== 'Все резюме' ? selectedFolder : undefined,
            folder_path: selectedFolder && selectedFolder !== 'Все резюме' ? selectedFolder : undefined,
          };
      if (action === 'keep') {
        const job = await atsApi.importResumes(files, metadata, setUploadPercent);
        setDuplicate(null);
        onImportJob(job);
        setMessage(`Импорт создан: ${job.id}`);
        return;
      }
      const fileHash = await sha256File(files[0]);
      const beforeUpload = await atsApi.getResumeUpdateStatus(duplicate.candidateId, fileHash);
      const existingParse = beforeUpload.status === 'processing' && !!beforeUpload.document_id;
      let waitForDocument = existingParse;
      let document: AtsDocument | null = null;
      if (!existingParse) {
        try {
          document = await atsApi.parseResume(files[0], metadata, duplicate.candidateId);
        } catch (error) {
          if (!(error instanceof AtsParseTimeoutError)) throw error;
          waitForDocument = true;
        }
      }
      if (waitForDocument) {
        setDuplicatePending(true);
        let recovered: AtsDocument | null = null;
        for (let attempt = 0; attempt < 40; attempt += 1) {
          const result = await atsApi.getResumeUpdateStatus(duplicate.candidateId, fileHash);
          if (!existingParse && result.document_id === beforeUpload.document_id) {
            if (attempt >= 9) {
              setDuplicatePending(false);
              throw new Error('Сервер не получил новый файл. Можно повторить загрузку.');
            }
          } else if (result.status === 'completed' && result.document) {
            recovered = result.document;
            break;
          } else if (result.status === 'failed') {
            setDuplicatePending(false);
            throw new Error(result.error_message || 'Не удалось обновить карточку кандидата');
          }
          await new Promise((resolve) => setTimeout(resolve, 3000));
        }
        if (!recovered) throw new Error('Разбор ещё идёт. Закройте окно и проверьте карточку позже.');
        document = recovered;
      }
      if (!document || document.status !== 'parsed' || !document.candidate_id) {
        throw new Error(document?.error_message || 'Не удалось обновить карточку кандидата');
      }
      const warnings = getParseWarnings(document);
      setDuplicate(null);
      setDuplicatePending(false);
      if (document.candidate_id && warnings.length) {
        setParseWarning({ candidateId: document.candidate_id, warnings });
        return;
      }
      setMessage('Карточка кандидата обновлена');
      onImported(selectedFolder);
      closeWithAnimation();
    } catch (error) {
      setDuplicateError(error instanceof Error ? error.message : 'Не удалось обработать дубликат');
    } finally {
      setBusy(false);
    }
  };

  const resolveParseWarning = async (action: 'create' | 'cancel') => {
    if (!parseWarning) return;
    if (action === 'create') {
      onImported(selectedFolder);
      onOpenCandidate(parseWarning.candidateId, { edit: true });
      closeWithAnimation();
      return;
    }
    setBusy(true);
    try {
      await atsApi.deleteCandidateCard(parseWarning.candidateId);
      setParseWarning(null);
      setMessage('Создание карточки отменено');
      onImported(selectedFolder);
    } catch {
      setParseWarning(null);
      setMessage('Карточка уже создана, но открыть её не удалось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 transition-colors duration-200 ease-out ${opened ? 'bg-black/35' : 'bg-black/0'}`}>
      <div className={`flex max-h-[calc(100vh-32px)] w-full max-w-[560px] origin-top-right flex-col overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)] transition-all duration-200 ease-out ${opened ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-3 scale-95 opacity-0'}`}>
        <div className="flex shrink-0 items-center justify-between border-b border-[#EEF0F2] px-6 py-5">
          <h2 className="text-[18px] font-bold text-[#23252D]">Загрузка резюме</h2>
          <button type="button" onClick={closeWithAnimation} className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-[#DDDCD6] text-[#525866] hover:bg-[#F5F4EF]" aria-label="Закрыть">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <TabToggle
            className="inline-flex w-auto"
            options={[
              { id: 'single', label: 'Одно резюме' },
              { id: 'bulk', label: 'Массовая загрузка' },
            ]}
            activeTab={mode}
            onTabChange={(tab) => {
              setMode(tab as 'single' | 'bulk');
              updateFiles([]);
            }}
            radius="rounded-[8px]"
          />

          {!usesArchiveStructure && (
          <div>
            <label className="mb-2 block text-[13px] font-medium text-[#525866]">Расположение</label>
            <p className="mb-3 text-[12px] text-[#868C98]">Выберите существующее значение или введите новое. Новая структура появится после импорта.</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block text-[13px] font-medium text-[#525866]">
                Страна
                <div className="relative mt-1.5">
                  <input value={country} onFocus={() => setLocationMenuOpen('country')} onChange={(event) => { setCountry(event.target.value); setProject(''); setVacancy(''); setLocationMenuOpen('country'); }} placeholder="Выберите или введите страну" className="h-11 w-full rounded-[10px] border border-[#DDDCD6] px-3 pr-10 text-[14px] font-normal text-[#23252D] outline-none focus:border-[#E16349]" />
                  <button type="button" aria-label="Показать страны" onClick={() => setLocationMenuOpen((current) => current === 'country' ? null : 'country')} className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-[#868C98]"><ChevronDownIcon className={`h-4 w-4 transition-transform ${locationMenuOpen === 'country' ? 'rotate-180' : ''}`} /></button>
                  {locationMenuOpen === 'country' && <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-44 overflow-y-auto rounded-[10px] border border-[#E4E6EA] bg-white p-1 shadow-[0_10px_24px_rgba(21,22,24,0.16)]">{visibleOptions(countries, country).map((value) => <button key={value} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => { setCountry(value); setProject(''); setVacancy(''); setLocationMenuOpen(null); }} className="flex h-9 w-full items-center rounded-[7px] px-3 text-left text-[13px] text-[#23252D] hover:bg-[#F5F4EF]">{value}</button>)}{canCreateCountry && <button type="button" disabled={busy} onMouseDown={(event) => event.preventDefault()} onClick={() => void createLocation('country')} className="mt-1 flex h-9 w-full items-center rounded-[7px] px-3 text-left text-[13px] font-semibold text-[#C94F32] hover:bg-[#FFF2EC] disabled:opacity-60">Создать страну «{country.trim()}»</button>}{!visibleOptions(countries, country).length && !canCreateCountry && <p className="px-3 py-2 text-[12px] text-[#868C98]">Введите название страны</p>}</div>}
                </div>
              </label>
              <label className="block text-[13px] font-medium text-[#525866]">
                Проект
                <div className="relative mt-1.5">
                  <input value={project} onFocus={() => country.trim() && setLocationMenuOpen('project')} onChange={(event) => { setProject(event.target.value); setVacancy(''); setLocationMenuOpen('project'); }} disabled={!country.trim()} placeholder={country.trim() ? 'Выберите или введите проект' : 'Сначала выберите страну'} className="h-11 w-full rounded-[10px] border border-[#DDDCD6] px-3 pr-10 text-[14px] font-normal text-[#23252D] outline-none focus:border-[#E16349] disabled:cursor-not-allowed disabled:bg-[#F5F4EF]" />
                  <button type="button" aria-label="Показать проекты" disabled={!country.trim()} onClick={() => setLocationMenuOpen((current) => current === 'project' ? null : 'project')} className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-[#868C98] disabled:cursor-not-allowed"><ChevronDownIcon className={`h-4 w-4 transition-transform ${locationMenuOpen === 'project' ? 'rotate-180' : ''}`} /></button>
                  {locationMenuOpen === 'project' && <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-44 overflow-y-auto rounded-[10px] border border-[#E4E6EA] bg-white p-1 shadow-[0_10px_24px_rgba(21,22,24,0.16)]">{visibleOptions(projects, project).map((value) => <button key={value} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => { setProject(value); setVacancy(''); setLocationMenuOpen(null); }} className="flex h-9 w-full items-center rounded-[7px] px-3 text-left text-[13px] text-[#23252D] hover:bg-[#F5F4EF]">{value}</button>)}{canCreateProject && <button type="button" disabled={busy} onMouseDown={(event) => event.preventDefault()} onClick={() => void createLocation('project')} className="mt-1 flex h-9 w-full items-center rounded-[7px] px-3 text-left text-[13px] font-semibold text-[#C94F32] hover:bg-[#FFF2EC] disabled:opacity-60">Создать проект «{project.trim()}»</button>}{!visibleOptions(projects, project).length && !canCreateProject && <p className="px-3 py-2 text-[12px] text-[#868C98]">Сначала выберите существующую страну</p>}</div>}
                </div>
              </label>
            </div>
            <label className="mt-3 block text-[13px] font-medium text-[#525866]">
              Вакансия
              <div className="relative mt-1.5">
                <input value={vacancy} onFocus={() => project.trim() && setLocationMenuOpen('vacancy')} onChange={(event) => { setVacancy(event.target.value); setLocationMenuOpen('vacancy'); }} disabled={!project.trim()} placeholder={project.trim() ? 'Выберите или введите вакансию' : 'Сначала выберите проект'} className="h-11 w-full rounded-[10px] border border-[#DDDCD6] px-3 pr-10 text-[14px] font-normal text-[#23252D] outline-none focus:border-[#E16349] disabled:cursor-not-allowed disabled:bg-[#F5F4EF]" />
                <button type="button" aria-label="Показать вакансии" disabled={!project.trim()} onClick={() => setLocationMenuOpen((current) => current === 'vacancy' ? null : 'vacancy')} className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-[#868C98] disabled:cursor-not-allowed"><ChevronDownIcon className={`h-4 w-4 transition-transform ${locationMenuOpen === 'vacancy' ? 'rotate-180' : ''}`} /></button>
                {locationMenuOpen === 'vacancy' && <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-44 overflow-y-auto rounded-[10px] border border-[#E4E6EA] bg-white p-1 shadow-[0_10px_24px_rgba(21,22,24,0.16)]">{visibleOptions(vacancies, vacancy).map((value) => <button key={value} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => { setVacancy(value); setLocationMenuOpen(null); }} className="flex h-9 w-full items-center rounded-[7px] px-3 text-left text-[13px] text-[#23252D] hover:bg-[#F5F4EF]">{value}</button>)}{canCreateVacancy && <button type="button" disabled={busy} onMouseDown={(event) => event.preventDefault()} onClick={() => void createLocation('vacancy')} className="mt-1 flex h-9 w-full items-center rounded-[7px] px-3 text-left text-[13px] font-semibold text-[#C94F32] hover:bg-[#FFF2EC] disabled:opacity-60">Создать вакансию «{vacancy.trim()}»</button>}{!visibleOptions(vacancies, vacancy).length && !canCreateVacancy && <p className="px-3 py-2 text-[12px] text-[#868C98]">Сначала выберите существующие страну и проект</p>}</div>}
              </div>
            </label>
            {!locationIsComplete && <p className="mt-2 text-[12px] text-[#D65F28]">Чтобы импортировать в вакансию, заполните страну, проект и вакансию.</p>}
          </div>
          )}

          {usesArchiveStructure && (
            <div className="flex gap-3 rounded-[10px] bg-[#EEF7FF] px-4 py-3 text-[13px] leading-5 text-[#286090]">
              <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
              <div><strong>Структура будет взята из архива.</strong> Папки внутри ZIP или 7z определят страну, проект и вакансию для каждого резюме.</div>
            </div>
          )}

          <label
            className={`flex min-h-[170px] cursor-pointer flex-col items-center justify-center rounded-[14px] border border-dashed px-6 text-center transition ${dragging ? 'border-[#E16349] bg-[#FFF2EC]' : 'border-[#D8D6CF] bg-[#F8F7F3]'}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              updateFiles(Array.from(event.dataTransfer.files || []));
            }}
          >
            <DocumentArrowUpIcon className="mb-3 h-12 w-12 text-[#CFC9BA]" />
            <div className="text-[15px] font-semibold text-[#23252D]">
              {mode === 'single' ? 'Перетащите файл с резюме' : 'Перетащите ZIP/7z-архив или файлы с резюме'}
            </div>
            <div className="mt-1 text-[12px] text-[#A19B8E]">
              {mode === 'single' ? 'PDF, DOC, DOCX до 25 МБ' : 'ZIP/7z до 15 ГБ · PDF, DOC, DOCX до 25 МБ'}
            </div>
            <div className="mt-1 text-[12px] text-[#868C98]">
              PDF до 50 страниц. У сканов длиннее 20 страниц распознаются только первые 20.
            </div>
            <input
              type="file"
              aria-label="Файл резюме"
              multiple={mode === 'bulk'}
              accept={mode === 'bulk' ? '.zip,.7z,.pdf,.doc,.docx' : '.pdf,.doc,.docx'}
              onChange={(event) => updateFiles(Array.from(event.target.files || []))}
              className="hidden"
            />
          </label>

          {selectedFile && (
            <div className="flex items-center gap-3 rounded-[10px] border border-[#DDDCD6] bg-white px-3 py-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] text-[11px] font-bold ${mode === 'bulk' ? 'bg-[#FFF2EC] text-[#E16349]' : 'bg-[#FFE6F0] text-[#E5487B]'}`}>
                {mode === 'bulk' ? <ArchiveBoxIcon className="h-5 w-5" /> : (selectedFile.name.split('.').pop() || 'file').toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-semibold text-[#23252D]">{selectedFile.name}</div>
                <div className="text-[12px] text-[#868C98]">
                  {formatFileSize(selectedFile.size)}
                  {files.length > 1 ? ` · выбрано ${files.length} файлов` : ''}
                </div>
              </div>
              <button type="button" onClick={() => updateFiles([])} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[#868C98] hover:bg-[#F5F4EF]" aria-label="Убрать файл">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          {mode === 'bulk' && (
            <div className="flex gap-3 rounded-[10px] bg-[#FFF2EC] px-4 py-3 text-[13px] leading-5 text-[#D65F28]">
              <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
              <div>{hasMixedBulkSources
                ? 'Загружайте архив отдельно от отдельных файлов: для ZIP/7z сохраняется его структура, а файлы без архива попадают в выбранную папку.'
                : usesArchiveStructure
                  ? 'Структура ZIP/7z-архива сохраняется. Выбранная ранее папка не влияет на этот импорт.'
                  : 'Несколько отдельных файлов будут импортированы в выбранную папку. Каждое резюме обрабатывается асинхронно.'}</div>
            </div>
          )}

          {uploadPercent !== null && (
            <div className="rounded-[10px] bg-[#F8F9FB] px-3 py-2 text-[13px] text-[#525866]">
              Загрузка архива: {uploadPercent}%
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E7E4DC]">
                <div className="h-full bg-[#E16349]" style={{ width: `${uploadPercent}%` }} />
              </div>
            </div>
          )}

          {message && <div className="text-[14px] text-[#1F2A44]">{message}</div>}
          {importJob && (
            <div className="rounded-[10px] bg-[#F8F9FB] p-3 text-[13px] text-[#525866]">
              <div className="font-semibold text-[#23252D]">Статус: {importStatusLabel[importJob.status] || importJob.status}</div>
              <div>Файлы: {importJob.processed_files}/{importJob.total_files}</div>
              <div>Обработано резюме: {importJob.successful_files}, ошибок: {importJob.failed_files}</div>
              <div>Создано карточек: {createdCandidates.length}</div>
              {!!autoAttachedCandidates.length && (
                <div>Автоматически прикреплено к существующим карточкам: {autoAttachedCandidates.length}</div>
              )}
              {!!importJob.potential_duplicates && <div>Потенциальных дублей: {importJob.potential_duplicates}</div>}
              {importJob.error_message && <div className="mt-2 text-[#D92D20]">{importJob.error_message}</div>}
              {!!importErrors.length && (
                <div className="mt-2 rounded-[8px] bg-[#FFF2F0] p-2 text-[#B42318]">
                  <div className="font-semibold">Ошибки файлов</div>
                  {importErrors.slice(0, 3).map((item, index) => (
                    <div key={`${item.file_name}-${index}`} className="mt-1 truncate">{item.file_name}: {item.error || item.error_message || 'Не удалось обработать файл'}</div>
                  ))}
                  {importErrors.length > 3 && <div className="mt-1">Еще: {importErrors.length - 3}</div>}
                </div>
              )}
              {!!importWarnings.length && (
                <div className="mt-2 rounded-[8px] bg-[#FFF8EB] p-2 text-[#B54708]">
                  <div className="font-semibold">Предупреждения парсинга</div>
                  {importWarnings.slice(0, 3).map((item, index) => {
                    const warningText = Array.isArray(item.warnings)
                      ? item.warnings.join('; ')
                      : String(item.warnings || '');
                    return (
                      <div key={`${item.file_name}-warn-${index}`} className="mt-1 truncate">
                        {item.file_name}: {warningText}
                      </div>
                    );
                  })}
                  {importWarnings.length > 3 && <div className="mt-1">Еще: {importWarnings.length - 3}</div>}
                </div>
              )}
              {!!createdCandidates.length && (
                <div className="mt-3 rounded-[8px] border border-[#E7E8EC] bg-white p-2">
                  <div className="mb-1 font-semibold text-[#23252D]">Созданные карточки</div>
                  {createdCandidates.slice(0, 3).map((item) => (
                    <button key={`${item.candidate_id}-${item.file_name}`} type="button" onClick={() => onOpenCandidate(item.candidate_id as string)} className="block w-full truncate py-1 text-left text-[#2F7BFF] hover:underline">
                      {item.file_name}
                    </button>
                  ))}
                  {createdCandidates.length > 3 && <div className="mt-1 text-[#868C98]">Еще: {createdCandidates.length - 3}</div>}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" disabled={busy} onClick={() => void poll()} className="h-[34px] rounded-[8px] border border-[#CDD0D5] px-3 text-[13px]">Обновить</button>
                <button type="button" onClick={() => void atsApi.downloadImportReport(importJob.id, 'csv')} className="h-[34px] rounded-[8px] border border-[#CDD0D5] px-3 text-[13px]">CSV</button>
                <button type="button" onClick={() => void atsApi.downloadImportReport(importJob.id, 'xlsx')} className="h-[34px] rounded-[8px] border border-[#CDD0D5] px-3 text-[13px]">XLSX</button>
                {!!importJob.potential_duplicates && <button type="button" onClick={onOpenDuplicates} className="h-[34px] rounded-[8px] border border-[#CDD0D5] px-3 text-[13px]">Открыть дубли</button>}
              </div>
            </div>
          )}
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-[#EEF0F2] px-6 py-4">
          <button type="button" onClick={closeWithAnimation} className="h-12 rounded-[10px] bg-[#F1F0EC] text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]">
            Отмена
          </button>
          <button type="button" disabled={busy || !files.length || hasMixedBulkSources || Boolean(getImportFilesError(files, mode)) || (!usesArchiveStructure && !locationIsComplete)} onClick={() => void submit()} className="h-12 rounded-[10px] bg-[#E16349] text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]">
            {busy ? (uploadPercent !== null ? `Загрузка ${uploadPercent}%` : 'Загрузка...') : 'Начать импорт'}
          </button>
        </div>
      </div>
      {duplicate && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/35 p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="duplicate-title" className="w-full max-w-[520px] rounded-[16px] bg-white p-6 shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
            <h3 id="duplicate-title" className="text-[18px] font-bold">Найден дубликат кандидата</h3>
            <p className="mt-3 text-[14px] text-[#525866]">{duplicate.sameFolder ? 'Данный кандидат уже есть в папке. Обновить его карточку?' : `Данный кандидат уже есть в папке «${duplicate.folders.join(', ') || 'Все резюме'}».`}</p>
            {duplicateError && <p role="alert" className="mt-3 rounded-[8px] bg-[#FFF2F0] p-3 text-[14px] text-[#B42318]">{duplicateError}</p>}
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              {duplicate.sameFolder ? (
                <>
                  <button type="button" disabled={busy} onClick={() => void resolveDuplicate('cancel')} className="h-10 rounded-[8px] border px-4 disabled:opacity-50">Нет</button>
                  <button type="button" disabled={busy || duplicatePending} onClick={() => void resolveDuplicate('attach')} className="h-10 rounded-[8px] bg-[#E16349] px-4 font-semibold text-white disabled:opacity-50">{busy ? 'Обновление...' : 'Да, обновить'}</button>
                </>
              ) : (
                <>
                  <button type="button" disabled={busy} onClick={() => void resolveDuplicate('keep')} className="h-10 rounded-[8px] border px-4 disabled:opacity-50">{busy ? 'Создание...' : 'Создать дубль в заданной папке'}</button>
                  <button type="button" disabled={busy || duplicatePending} onClick={() => void resolveDuplicate('attach')} className="h-10 rounded-[8px] bg-[#E16349] px-4 font-semibold text-white disabled:opacity-50">{busy ? 'Обновление...' : 'Обновить в исходной папке'}</button>
                  <button type="button" disabled={busy} onClick={() => void resolveDuplicate('cancel')} className="h-10 rounded-[8px] border px-4 disabled:opacity-50">Отменить</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {parseWarning && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/35 p-4">
          <div className="max-h-[calc(100vh-32px)] w-full max-w-[520px] overflow-y-auto rounded-[16px] bg-white p-6 shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
            <h3 className="text-[18px] font-bold">Не удалось распознать часть полей</h3>
            <p className="mt-3 text-[14px] text-[#525866]">Система не смогла заполнить:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-[14px] text-[#23252D]">
              {parseWarningFieldLabels(parseWarning.warnings).map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button type="button" disabled={busy} onClick={() => void resolveParseWarning('cancel')} className="h-10 rounded-[8px] border px-4">
                Отменить
              </button>
              <button type="button" disabled={busy} onClick={() => void resolveParseWarning('create')} className="h-10 rounded-[8px] bg-[#E16349] px-4 font-semibold text-white">
                Создать карточку кандидата
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AtsPage;
